import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FeedbackButton } from "./components/FeedbackButton";
import { PaymentStep } from "./components/PaymentStep";
import { InfoPopover } from "./components/InfoPopover";
import { DoneScreen } from "./components/DoneScreen";
import { RetentionNote } from "./components/RetentionNote";
import { DeepLinkLanding } from "./components/DeepLinkLanding";
import { QueuedScreen } from "./components/QueuedScreen";
import { parseDeepLink, classifyChainState, isMobileUa, type DeepLink, type DeepLinkUiState } from "./lib/deep-link";
import { shouldGuardUnload } from "./lib/unload-guard";
import { brandHomeHref, finishHref, isStagingHost } from "./lib/host";
import { attachNotifyEmailWithRetry } from "./lib/notify-email-retry";
import { FormatConfirmStep } from "./components/FormatConfirmStep";
import { ColumnMappingStep } from "./components/ColumnMappingStep";
import { TimeZonePrompt, defaultZone } from "./components/TimeZonePrompt";
import { ConfirmPeoplePanel, type GroundingState } from "./components/ConfirmPeoplePanel";
import { applyPronounsToPromptVars } from "./lib/pronouns";
import { ChangeEmailModal } from "./components/ChangeEmailModal";
import { EmailConfirmBlock } from "./components/EmailConfirmBlock";
import { makeExcerptSearcher } from "./lib/corpus-excerpts";
import {
  RunState,
  RunHandoff,
  PassphraseGate,
  ResultsSummary,
  EmptyResult,
  deriveRunView,
  selectRunStepScreen,
  shouldHoldForPassAck,
  slugToTitle,
  useElapsedLabel,
  undeliveredPatternIds,
} from "./components/RunState";
import {
  clearActiveRun,
  loadActiveRun,
  loadActiveRunForCode,
  markActiveRunPassAcked,
  notifyResumeDeadEnd,
  saveActiveRun,
} from "./lib/active-run";
import {
  clearPendingRun,
  loadPendingRun,
  PENDING_RUN_JANITOR_MIN_AGE_MS,
  savePendingRun,
} from "./lib/pending-run";
import { decideCostStepAction } from "./lib/cost-step-action";
import { decideCheckoutReturn } from "./lib/checkout-return";
import { friendlyLaunchLimitMessage } from "./lib/launch-limit-errors";
import {
  PAID_UNREACHABLE_NOTICE,
  SERVER_UNREACHABLE_MESSAGE,
  isServerUnreachable,
} from "./lib/server-unreachable";
import { DEFAULT_SERVER, SERVER_STORAGE_KEY } from "./lib/server-config";
import { CLOUD_ENGINES } from "./lib/engines.generated";
import { FormatHelp } from "./components/FormatHelp";
import {
  cancelRun,
  checkHealth,
  classifyEntities,
  downloadViewerBundle,
  createCheckoutSession,
  fetchStatus,
  getConfig,
  getEntitlement,
  isAgreementGateError,
  lookupLinkedEmail,
  operatorGetSettings,
  postCheckoutReturn,
  postWizardEvent,
  sendWizardEventOnUnload,
  postConvert,
  postPrescan,
  setNotifyEmail as apiSetNotifyEmail,
  postPreview,
  subscribeEvents,
  type ApiError,
  type ClassifyEntitiesResponse,
  type Pattern,
  type PrescanResponse,
  type SseEvent,
  type StatusResponse,
} from "./lib/api";
import { buildIntakeMailto } from "./lib/intake";
import {
  buildHowHeardEvent,
  buildWizardEvent,
  describeUnsupportedFile,
  isThinParse,
  shouldSendWizardEvent,
  sniffFileType,
  type HowHeardSource,
  type WizardEventKind,
} from "./lib/wizard-events";
import { deriveContext, nicknameMapFromAliases } from "@app/lib/anonymize";
import {
  resolveViewerAfterDownload,
  type ViewerState,
} from "./lib/viewer-state";

type WizardStep = 0 | 1 | 2 | 3 | 4 | 5;

// Step indices. Payment (STEP_PAY) exists only on paying self-serve journeys —
// covered codes (pilot-free / comp / already-paid) skip STEP_COST → STEP_RUN
// and the rail omits the Payment entry, so the counter and checkmarks stay
// honest in both shapes.
const STEP_DROP: WizardStep = 0;
const STEP_PARTIES: WizardStep = 1;
const STEP_COST: WizardStep = 2;
const STEP_PAY: WizardStep = 3;
const STEP_RUN: WizardStep = 4;
const STEP_DONE: WizardStep = 5;
// Step map. Internal indices are 0-based; the UI displays them 1-based
// (`Step {currentStep + 1} of 5`). The old step 0 ("How your data is
// handled") moved to the full-screen agreement before the wizard opens,
// so the wizard now opens straight on "Drop your messages."
//   0  Step 1 of 5  — Drop your messages
//   1  Step 2 of 5  — Confirm the parties
//   2  Step 3 of 5  — Review the cost
//   3  Step 4 of 5  — Read the messages (the "Run" step)
//   4  Step 5 of 5  — Download your viewer
import { type IngestResult, ingestOfwCsv } from "./adapters/ofw";
import { type IngestResult as CcIngestResult } from "./adapters/civil_communicator";
import { type IngestResult as TalkingParentsIngestResult } from "./adapters/talking_parents";
import { type IngestResult as AppCloseIngestResult } from "./adapters/appclose";
import { type IngestResult as TwoHousesIngestResult } from "./adapters/two_houses";
import {
  type IngestResult as GenericCsvIngestResult,
  ingestGenericCsv,
  ingestGenericCsvMapped,
} from "./adapters/generic_csv";
import {
  extractPdfPages,
  extractPdfTextEol,
  type PositionedLine,
} from "./adapters/_pdf";
import {
  decidePdfRoute,
  ingestPdfByFormat,
  PDF_ADAPTER_LABEL,
  PDF_FORMAT_INFO,
  type FormatChoice,
  type PdfFormat,
} from "./adapters/_pdf_ingest";
import { decideCsvRoute } from "./adapters/_csv_ingest";
import {
  type ColumnMapping,
  headerSignature,
} from "./adapters/_column_mapping";
import { saveProfile } from "./adapters/_mapping_profiles";

const CLIENT_STORAGE_KEY = "aurora.cloud.clientId";
const ACCESS_CODE_STORAGE_KEY = "aurora.cloud.accessCode";
// Latch for the run screen's "where did you hear about Aurora?" question:
// present ⇒ answered or skipped in some session — never ask this browser
// again. Value is a marker, never the answer (that goes to the server only).
const HOW_HEARD_STORAGE_KEY = "aurora.cloud.howHeard";
// The context-framing sentence ("their child Madison …") is derived by the
// shared deriveContext (app/src/lib/anonymize.ts, imported via @app) so desktop
// + cloud emit identical wording from one implementation. Its no-alias output
// must keep matching the server's aurora_cloud.entity_classifier
// .build_context_sentence (Python), which produces the initial classify phrase.

// Format an ISO date (YYYY-MM-DD or a full ISO timestamp) as a human-
// readable "Feb 3, 2025" style. Returns the input unchanged on parse
// failure so we never silently lie about what the corpus contains.
function humanDate(iso: string | null | undefined): string {
  if (!iso) return "";
  // Take just the date portion so timezone shifts don't move the day.
  const day = iso.slice(0, 10);
  const m = day.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return day;
  const date = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  if (Number.isNaN(date.getTime())) return day;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

// Format an IANA zone name as a short human label: "America/Los_Angeles"
// → "Pacific time". Falls back to the IANA identifier if Intl can't
// resolve it.
function humanTimezone(iana: string | null | undefined): string {
  if (!iana) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: iana,
      timeZoneName: "long",
    }).formatToParts(new Date());
    const tz = parts.find((p) => p.type === "timeZoneName")?.value;
    if (!tz) return iana;
    // "Pacific Standard Time" / "Pacific Daylight Time" → "Pacific time".
    return tz.replace(/\s+(Standard|Daylight|Summer)\s+Time$/, " time");
  } catch {
    return iana;
  }
}

// Compact two-date range: "Feb 3 → Feb 24, 2025" when both fall in the
// same year; "Dec 28, 2024 → Jan 12, 2025" otherwise.
function humanDateRange(
  start: string | null | undefined,
  end: string | null | undefined,
): string {
  if (!start || !end) return "";
  const sYear = start.slice(0, 4);
  const eYear = end.slice(0, 4);
  if (sYear === eYear) {
    // "Feb 3" + "Feb 24, 2025"
    const sShort = new Date(
      Date.UTC(+sYear, +start.slice(5, 7) - 1, +start.slice(8, 10)),
    ).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
    return `${sShort} → ${humanDate(end)}`;
  }
  return `${humanDate(start)} → ${humanDate(end)}`;
}

type Phase =
  | { kind: "setup" }
  | {
      kind: "preview-running";
      previewId: string;
      previewToken: string;
      events: SseEvent[];
    }
  | {
      kind: "preview-ready";
      previewId: string;
      previewToken: string;
      events: SseEvent[];
      patterns: Pattern[];
    }
  | {
      kind: "preview-failed";
      previewId: string;
      events: SseEvent[];
      reason: string;
      // The user stopped it themselves (cancel request, exit 130, or a
      // "cancelled" chain event) — render a calm stop, not a failure.
      cancelled?: boolean;
    }
  | {
      // Accepted into the queue (all full-run slots were busy at submit). No
      // chain subprocess yet — a status poll (not SSE) watches for promotion,
      // then transitions to convert-running. Rendered as a full-screen takeover
      // (QueuedScreen), so it doesn't thread through the wizard's run-step state.
      kind: "convert-queued";
      runId: string;
      runToken: string;
      position: number | null;
    }
  | {
      kind: "convert-running";
      runId: string;
      runToken: string;
      events: SseEvent[];
    }
  | {
      kind: "convert-failed";
      runId: string;
      events: SseEvent[];
      reason: string;
      cancelled?: boolean;
    }
  | {
      kind: "run-complete";
      runId: string;
      runToken: string;
      events: SseEvent[];
      viewer?: ViewerState;
    };

// Decides whether the entity classifier (a real, billable Haiku call) should
// fire for the current corpus, given the size-check (prescan) state. Pulled out
// as a pure function so the "don't spend on an over-budget or not-yet-sized
// record" rule is unit-testable without driving the whole wizard.
//   "wait" — size check still running, or a stale `ready` left over from a
//            just-replaced file; hold without touching classify state.
//   "skip" — record is over budget; it dead-ends at the off-ramp, so we must
//            never spend the classify call on it.
//   "go"   — sized and under budget, OR the size check errored (fail open: the
//            chain's own pre-flight cost gate remains the backstop).
export type ClassifyGate = "wait" | "skip" | "go";
export function classifyGateState(args: {
  prescanKind: "idle" | "loading" | "ready" | "error";
  readyForThisFile: boolean;
  overBudget: boolean;
}): ClassifyGate {
  if (args.prescanKind === "idle" || args.prescanKind === "loading") {
    return "wait";
  }
  if (args.prescanKind === "ready") {
    if (!args.readyForThisFile) return "wait";
    return args.overBudget ? "skip" : "go";
  }
  return "go"; // error → fail open
}

// Decides what the cost-estimate (prescan) effect should do for the current
// corpus, driven purely by file IDENTITY (not prescan.kind). Pulled out so the
// "refetch iff the file changed" rule — the fix for the stuck "Checking the
// size…" after a file replacement — is unit-testable.
//   "reset" — no corpus loaded; clear back to idle.
//   "skip"  — already (re)fetched for this exact file; do nothing.
//   "fetch" — a corpus we haven't sized yet (or a forced retry cleared the ref).
export type PrescanAction = "reset" | "skip" | "fetch";
export function prescanAction(args: {
  corpusFile: File | null;
  fetchedFor: File | null;
}): PrescanAction {
  if (!args.corpusFile) return "reset";
  if (args.fetchedFor === args.corpusFile) return "skip";
  return "fetch";
}

// Per-tier cost viability (door 2, 2026-08-05). The server marks a PWYC tier
// `viable: false` when this record's run cost exceeds that tier's ceiling
// (Access has its own, lower ceiling than Standard/Solidarity) — the card
// renders disabled: visible, but not selectable. A server that predates the
// field omits it, so ONLY an explicit false disables — undefined reads as
// viable, keeping a mixed-deploy window from bricking the cost screen.
export function tierIsViable(
  tier: { viable?: boolean } | undefined,
): boolean {
  return tier?.viable !== false;
}

// Decides whether the current PWYC selection survives a (re-)prescan. If the
// selected tier came back `viable: false` — e.g. the user swapped in a larger
// file and Access no longer covers its run cost — the selection is CLEARED
// ("" = no card picked; Continue stays disabled until a viable card is
// chosen). Pulled out as a pure function so the clearing rule is unit-testable
// without driving the whole wizard. A tier missing from the ladder entirely
// also clears — a selection the checkout can't honor must not persist.
export function reconcileTierChoice(args: {
  tiers: Array<{ name: string; viable?: boolean }>;
  choice: string;
}): string {
  if (!args.choice) return args.choice;
  const chosen = args.tiers.find((t) => t.name === args.choice);
  if (!chosen || !tierIsViable(chosen)) return "";
  return args.choice;
}

// Door 2 × already-paid (review fix, 2026-08-05): the per-tier viability gate
// must hold even when no charge is pending. An already-paid user who goes
// Back and swaps in a larger export would otherwise keep a live Submit for a
// run the server just marked underwater — exactly what the gate exists to
// prevent. The tier that matters is the one the payment is FOR — the
// server-recorded tier (prescan's paid_tier, or /billing/entitlement's — both
// read the same payment record; the entitlement one is the only source on the
// lost-webhook path, review R1).
//
// When the tier is STILL unknown (legacy pre-S25 record, older server that
// omits the field, entitlement fetch failed), fail CLOSED for money: judge by
// the most restrictive PRICED tier — Access — never Standard. A
// wrongly-blocked Standard payer lands on the off-ramp whose already-paid
// copy says the payment stays on file (annoying, recoverable by email); an
// underwater run is the thing this whole gate exists to prevent. Deliberately
// NOT the current selection: on the lost-webhook path pwycChoice is an
// untouched "Standard" DEFAULT — the paid band replaces the clickable grid
// before the user could ever pick a card — which is exactly the poison that
// let an Access payment get judged by Standard's ceiling (review R1). A named
// tier missing from the ladder gets the same Access fallback (its ceiling is
// unknowable). Beyond the tier pick, fail-open stands: an omitted `viable`
// field never blocks (same mixed-deploy back-compat as tierIsViable).
export function paidTierInviable(args: {
  tiers: Array<{ name: string; viable?: boolean }>;
  paidTier?: string | null;
}): boolean {
  const name = args.paidTier || "Access";
  const tier =
    args.tiers.find((t) => t.name === name) ??
    args.tiers.find((t) => t.name === "Access");
  return !tierIsViable(tier);
}

const SAMPLE_CSV =
  "timestamp,sender,recipient,subject,body,attachments\n" +
  "2024-01-01T08:00:00Z,Alice,Bob,Schedule,Pick up kids at 5pm.,\n" +
  "2024-01-01T09:00:00Z,Bob,Alice,Re: Schedule,Got it. See you then.,\n";

const SAMPLE_META = JSON.stringify(
  {
    id: "smoke-case",
    privacy_class: "fictional",
    prompt_vars: {
      party_a: "Alice",
      party_b: "Bob",
      format: "demo CSV",
      time_period: "Jan 2024",
      context: "their two children Riley and Sky",
    },
    known_patterns: [],
    notes: "browser harness fixture",
  },
  null,
  2,
);

// Dev affordances (settings cog, sample-load buttons, parity banners,
// adapter override, raw meta.json textarea) only render in:
//   - local `npm run dev` (import.meta.env.DEV === true), or
//   - any build the URL of which carries `?dev=1`.
// Production Pages deploys have DEV=false and no query param, so the
// settings gear is invisible to pilot users.
const DEV_QUERY_FLAG =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).has("dev");

// Dev-driving affordances (corpus preload, adapter override) — on for the
// whole dev server so you can run the wizard without real files.
const DEV_AFFORDANCES_ENABLED = import.meta.env.DEV || DEV_QUERY_FLAG;

// Raw diagnostics (run id + the black event-log "terminal"). These are
// noise when reviewing the actual user experience, so they require an
// explicit ?dev opt-in — the plain dev server shows what a pilot user
// sees. Add ?dev to the URL to bring them back.
const DEV_DIAGNOSTICS_ENABLED = DEV_QUERY_FLAG;

// Supported formats come from the engine-catalog manifest (generated
// engines.generated.ts). Add an engine in engines.yaml + re-run sync.py and the
// wrong-file error here and the FormatHelp accordion below both pick it up.
function oxfordJoin(items: string[], conj: string): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} ${conj} ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, ${conj} ${items[items.length - 1]}`;
}
const CLOUD_FORMAT_NAMES = CLOUD_ENGINES.map((e) => e.display);
const CLOUD_FORMATS_OR = oxfordJoin(CLOUD_FORMAT_NAMES, "or");

export function App() {
  // --- Config (read once, no UI to change them in the pilot wizard) ---
  const [server] = useState<string>(() => {
    // In dev, force same-origin so Vite's proxy forwards backend routes.
    if (typeof window !== "undefined") {
      const host = window.location.hostname;
      if (host === "localhost" || host === "127.0.0.1") {
        return window.location.origin;
      }
    }
    return localStorage.getItem(SERVER_STORAGE_KEY) ?? DEFAULT_SERVER;
  });
  const [clientId] = useState<string>(
    // "-dev" was misleading on prod (same build ships everywhere). The server
    // masks any "cloud-…" client to the family label "cloud" in status/console.
    () => localStorage.getItem(CLIENT_STORAGE_KEY) ?? "cloud-frontend",
  );
  // Access code stamped into localStorage by the Landing flow before
  // the wizard mounts. Required for /prescan, /runs/preview, /runs/start
  // and /feedback. In the standalone dev build (running App.tsx without
  // going through Landing) this may be empty — server returns 401, which
  // surfaces as an error banner in the cost-screen step.
  const [accessCode] = useState<string>(
    () => localStorage.getItem(ACCESS_CODE_STORAGE_KEY) ?? "",
  );

  // --- Wizard state ---
  // currentStep is per-session and never persisted: a fresh load starts at
  // step 0 (Drop your messages). The data-handling agreement that used to
  // be the wizard's step 0 now lives on the full-screen agreement shown
  // before App mounts, so there's no separate privacy ack to track here.
  const [currentStep, setCurrentStep] = useState<WizardStep>(0);

  // Operator-only run-mode chip. A wizard viewer who is ALSO a signed-in
  // operator (operator token in sessionStorage — same key OperatorShell
  // uses) sees the current run-mode in the masthead, but only when it's
  // NOT Live. Regular pro-se users never have the token, so they never see
  // a "Demo"/"Pilot" badge; Live shows nothing (the normal paid state).
  const [siteMode, setSiteMode] = useState<"demo" | "pilot" | "live" | null>(
    null,
  );
  useEffect(() => {
    let token: string | null = null;
    try {
      token = sessionStorage.getItem("aurora.cloud.operatorToken");
    } catch {
      token = null;
    }
    if (!token) return;
    let cancelled = false;
    operatorGetSettings(server, token)
      .then((s) => {
        if (cancelled) return;
        setSiteMode(
          s.use_fake_provider.value
            ? "demo"
            : s.pilot_free.value
              ? "pilot"
              : "live",
        );
      })
      .catch(() => {
        /* not an operator / token expired — no chip, fail quiet */
      });
    return () => {
      cancelled = true;
    };
  }, [server]);

  // Cost-screen state. The /prescan call fires when the user arrives
  // at step 3 and the result lives here until launch (no point
  // re-calling if they back out and return). Idle → loading → ready
  // or error; user can retry on error without leaving the step.
  type PrescanState =
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "ready"; estimate: PrescanResponse }
    | { kind: "error"; message: string };
  const [prescan, setPrescan] = useState<PrescanState>({ kind: "idle" });
  // The exact File a `ready` prescan was computed for. Lets the classify effect
  // tell a fresh `ready` from a stale one left over from a just-replaced file
  // (prescan state lags corpusFile by one render on replacement). Without this,
  // an over-budget record's size check could be read as "ready & under budget"
  // for the wrong file and fire the Haiku classify call we're trying to avoid.
  const prescanReadyForRef = useRef<File | null>(null);
  // The exact File the current/last prescan fetch was launched for. The size
  // check (re)fires iff this differs from corpusFile — driving it purely off
  // file IDENTITY instead of the prescan.kind state, which is what a single
  // robust effect needs (the old two-effect dance read a stale prescan.kind in
  // the same flush as the reset and could strand "Checking the size…" on a
  // file replacement). `prescanNonce` is the retry trigger: bump it (and clear
  // this ref) to force a refetch for the same file after an error.
  const prescanFetchedForRef = useRef<File | null>(null);
  const [prescanNonce, setPrescanNonce] = useState(0);

  // --- Run inputs ---
  const [corpusFile, setCorpusFile] = useState<File | null>(null);
  const [metaJson, setMetaJson] = useState<string>("");
  const [adapter, setAdapter] = useState<string>("generic_csv");
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- Browser PDF ingest ---
  // Set during pdf.js + adapter run; cleared when CSV is in hand. Drives
  // a progress + stats banner under the corpus input.
  // After PDF ingest, the cloud-server's Haiku classifier proposes
  // parties + children. The banner gates "Start preview" until the user
  // confirms (or edits) the classifier's result — without this gate, the
  // chain rejects on the TODO context sentinel that CC ingest leaves in
  // meta.json (and OFW's cover-page-derived context can usually be
  // improved by the classifier seeing the whole corpus).
  const [classify, setClassify] = useState<
    | { kind: "idle" }
    | { kind: "running"; sourceName: string }
    | { kind: "ready"; result: ClassifyEntitiesResponse }
    | { kind: "confirmed"; result: ClassifyEntitiesResponse }
    | { kind: "error"; message: string }
  >({ kind: "idle" });
  // Latest structured grounding from the people editor (children + folded/edited
  // nicknames). The editor emits its seeded state on mount, so this is populated
  // with the matched nicknames even before the user touches anything; advancing
  // persists it. `epoch` bumps per classify run so a new file remounts the editor
  // (it's uncontrolled — seeds once at mount).
  const [classifyGrounding, setClassifyGrounding] = useState<GroundingState | null>(
    null,
  );
  const [classifyEpoch, setClassifyEpoch] = useState(0);
  // `classifyNonce` is the retry trigger for a failed people scan: bump it to
  // re-fire the classify effect for the same file (mirrors prescanNonce — the
  // file already parsed by this step, so "try a different file" is never the
  // right ask; a re-scan is).
  const [classifyNonce, setClassifyNonce] = useState(0);
  // "Verify against the messages" search, bound to the in-memory corpus File the
  // browser already holds — entirely client-side, no server call, no spend.
  const excerptSearcher = useMemo(
    () => (corpusFile ? makeExcerptSearcher(corpusFile) : undefined),
    [corpusFile],
  );

  const [pdfIngest, setPdfIngest] = useState<
    | { kind: "idle" }
    | {
        kind: "running";
        pageNumber: number;
        totalPages: number;
        sourceName: string;
      }
    | {
        kind: "done";
        adapter:
          | "ofw"
          | "civil_communicator"
          | "two_houses"
          | "talking_parents"
          | "appclose"
          | "generic_csv";
        // OFW stats carry timezone + children; CC stats carry
        // rejected_count + by_reason on the meta; generic_csv stats
        // carry rejected_count + by_reason inline; 2houses/TalkingParents
        // stats carry timezone (no children). All share
        // message_count / party_a / party_b / date_range / out_of_set_senders.
        stats:
          | (IngestResult["stats"] & { _kind?: "ofw" })
          | (CcIngestResult["stats"] & { _kind?: "cc" })
          | (TwoHousesIngestResult["stats"] & { _kind?: "two_houses" })
          | (TalkingParentsIngestResult["stats"] & { _kind?: "talking_parents" })
          | (AppCloseIngestResult["stats"] & { _kind?: "appclose" })
          | (GenericCsvIngestResult["stats"] & { _kind?: "generic_csv" });
        sourceName: string;
        // Non-fatal adapter warnings (assumed time zone, look-alike lines,
        // malformed stanzas) — every adapter produces them; the done banner
        // renders them so an assumed zone is never silent.
        warnings: string[];
      }
    // Detection wasn't confident (low score, or two formats too close to call):
    // show the user the guess + alternatives and let them pick. We hold the
    // already-extracted page text so confirming doesn't re-read the file.
    | {
        kind: "confirm";
        sourceName: string;
        corpusId: string;
        pages: string[];
        // Retained alongside `pages` because AppClose parses by column, not
        // by line text — see PdfIngestOptions.lines.
        lines: { pageNumber: number; lines: PositionedLine[] }[];
        choices: FormatChoice[];
        guess: PdfFormat | null;
        selected: PdfFormat | null;
      }
    // Parsed fine, but the export declares no time zone and none is set on
    // the case yet (AppClose). Nothing can be placed on a timeline until the
    // user says which zone the wall-clock times are in, so we hold the parse
    // and ask — the same prompt the CSV paths use, solicited once per case.
    | {
        kind: "timezone";
        sourceName: string;
        corpusId: string;
        pages: string[];
        lines: { pageNumber: number; lines: PositionedLine[] }[];
        format: PdfFormat;
        pendingCount: number;
        selectedZone: string;
      }
    // Couldn't read it. NOT a dead-end and NOT the old wrong-parser error: an
    // honest get-in-touch handoff (FormatConfirmStep, recover mode) — no picker,
    // so no `choices`. (pages/corpusId/selected are retained only because the
    // shared confirm-path types over both kinds.)
    | {
        kind: "recover";
        sourceName: string;
        corpusId: string;
        pages: string[];
        lines: { pageNumber: number; lines: PositionedLine[] }[];
        selected: PdfFormat | null;
        // Why we're on recover: set when a detected format was tried and failed
        // (surfaced to the user); absent for a nothing-recognized recover.
        note?: string;
      }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  // The timezone the user assigned for this case's naive-timestamp sources
  // (Piece C). Solicited ONCE and remembered here so a second naive source
  // (or a re-import) reuses it without re-asking. Null until first solicited.
  const [caseTimezone, setCaseTimezone] = useState<string | null>(null);

  // --- Browser CSV ingest (Phase 3) ---
  // A dropped .csv routes through decideCsvRoute: a KNOWN OFW message CSV
  // auto-maps; the canonical schema parses direct; an arbitrary tabular CSV
  // goes through column-mapping. Naive timestamps (OFW-CSV always; generic /
  // mapped sometimes) pause on the shared timezone prompt before committing.
  const [csvIngest, setCsvIngest] = useState<
    | { kind: "idle" }
    // Arbitrary CSV — the user matches columns to roles. We hold the file text
    // + headers + a sample so the live preview re-projects on every edit.
    | {
        kind: "column-map";
        sourceName: string;
        corpusId: string;
        text: string;
        headers: string[];
        sampleRows: Record<string, string>[];
        rowCount: number;
        mapping: ColumnMapping;
        /** Aurora's original fuzzy-matched guess, for "reset to suggested". */
        suggested: ColumnMapping;
        signature: string;
      }
    // Naive timestamps with no zone — solicit one (best-guess default).
    | {
        kind: "timezone";
        sourceName: string;
        corpusId: string;
        text: string;
        // How to finish once a zone is chosen: re-run the same adapter with it.
        via: "ofw-csv" | "generic-csv-mapped";
        mapping: ColumnMapping | null;
        signature: string | null;
        selectedZone: string;
      }
  >({ kind: "idle" });

  // Derive a corpus_id slug from a PDF filename for the case_meta.id field.
  // The cloud-server requires meta_json.id; if it's blank the POST 400s.
  function slugFromFileName(name: string): string {
    const stem = name.replace(/\.[^.]+$/, "");
    return (
      stem
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 64) || "uploaded-pdf"
    );
  }

  // How many pages the SOURCE PDF had, keyed by the canonical CSV we
  // synthesized from it.
  //
  // The cost screen has always had a branch for the real page count — "Your
  // record is 197 pages" instead of the hedged "measures roughly 63" — and the
  // server has always read it from `meta.ingestion.page_count`. Nothing ever
  // put it on the wire, so that branch had never rendered in the cloud wizard
  // for ANY adapter (found 2026-07-30 on a 197-page AppClose export shown as
  // "roughly 63 pages").
  //
  // A WeakMap keyed by the File rather than a piece of state: the count is only
  // ever readable for the exact file it was measured from, so swapping in a CSV
  // afterwards cannot inherit a stale page count — the failure mode that would
  // be worse than the hedge it replaces. Entries die with the File.
  const pdfPageCounts = useRef(new WeakMap<File, number>()).current;
  const rememberPdfPageCount = useCallback(
    (csv: File, pages: number | undefined) => {
      if (typeof pages === "number" && pages > 0) pdfPageCounts.set(csv, pages);
    },
    [pdfPageCounts],
  );

  // Wire a successfully-parsed PDF into the wizard: synthesize the canonical
  // CSV as the corpus file, fill meta.json, set the adapter, and mark the
  // ingest done. Shared by the dev-affordance OFW load, the auto-detect path,
  // and the confirm-then-parse path — so all three commit identically.
  // Every dead end in the import step reports through here. Parsing happens
  // in the browser, so without this a failed import reaches the server as
  // silence — indistinguishable from a visitor who wandered off (2026-07-30:
  // one didn't, and we only found out because he emailed). Metadata only;
  // lib/wizard-events.ts owns what may travel. Fire-and-forget: it never
  // throws and never gates the error the user is shown.
  const reportWizardEvent = useCallback(
    (args: {
      kind: WizardEventKind;
      file?: File | null;
      fileName?: string | null;
      sizeBytes?: number | null;
      pages?: number | null;
      detectedFormat?: string | null;
      score?: number | null;
      error?: unknown;
      actualType?: string | null;
      attempts?: number | null;
      messageCount?: number | null;
      chosenFormat?: string | null;
      guessFormat?: string | null;
      step?: string | null;
    }) => {
      const payload = buildWizardEvent(args);
      // Two of the call sites live inside a setState updater, which React
      // StrictMode runs twice in dev.
      if (!shouldSendWizardEvent(payload)) return;
      void postWizardEvent({
        server,
        accessCode: accessCode || null,
        payload,
      });
    },
    [server, accessCode],
  );

  // "Where did you hear about Aurora?" — the run screen's optional question
  // (RunHandoff). Asked once per BROWSER, not per run: the latch below
  // outlives the run and the access code. "open" renders the card, "thanked"
  // renders the swap's thanks line for the rest of this session, "done"
  // renders nothing (skipped, or resolved in an earlier session).
  const [howHeardState, setHowHeardState] = useState<"open" | "thanked" | "done">(
    () => {
      try {
        return localStorage.getItem(HOW_HEARD_STORAGE_KEY) ? "done" : "open";
      } catch {
        return "open";
      }
    },
  );
  const resolveHowHeard = useCallback(
    (source: HowHeardSource | null, detail: string | null) => {
      if (source) {
        const payload = buildHowHeardEvent(source, detail);
        if (shouldSendWizardEvent(payload)) {
          void postWizardEvent({ server, accessCode: accessCode || null, payload });
        }
      }
      setHowHeardState(source ? "thanked" : "done");
      try {
        localStorage.setItem(HOW_HEARD_STORAGE_KEY, "1");
      } catch {
        // Storage blocked — the ask simply repeats on some future run.
      }
    },
    [server, accessCode],
  );

  const commitParsedPdf = useCallback(
    (
      sourceName: string,
      pageCount: number | undefined,
      adapterKey:
        | "ofw"
        | "civil_communicator"
        | "two_houses"
        | "talking_parents"
        | "appclose",
      result:
        | IngestResult
        | CcIngestResult
        | TwoHousesIngestResult
        | TalkingParentsIngestResult
        | AppCloseIngestResult,
    ) => {
      const csvName = sourceName.replace(/\.pdf$/i, ".csv") || "ingested.csv";
      const csvFile = new File([result.csv], csvName, { type: "text/csv" });
      const dt = new DataTransfer();
      dt.items.add(csvFile);
      if (fileInputRef.current) fileInputRef.current.files = dt.files;
      rememberPdfPageCount(csvFile, pageCount);
      setCorpusFile(csvFile);
      setMetaJson(JSON.stringify(result.case_meta, null, 2));
      setAdapter(adapterKey);
      setPdfIngest({
        kind: "done",
        adapter: adapterKey,
        stats: result.stats,
        sourceName,
        warnings: result.warnings,
      });
      // Green wizard, tiny record. Nobody sees this one: no error fires, the
      // quote comes back small, and the analysis runs on a fraction of what
      // they handed over.
      // Size is deliberately absent here: `csvFile` is the canonical CSV we
      // just generated, not the export they handed over, and reporting its
      // size would put "4 KB" next to "300 pages" in the alert and invite
      // exactly the wrong conclusion. Pages is the honest measure on this path.
      if (
        isThinParse({
          messageCount: result.stats.message_count,
          pages: pageCount ?? null,
        })
      ) {
        reportWizardEvent({
          kind: "thin-parse",
          fileName: sourceName,
          pages: pageCount ?? null,
          detectedFormat: adapterKey,
          messageCount: result.stats.message_count,
        });
      }
    },
    [reportWizardEvent],
  );

  // Wire a committed CSV (canonical text + meta + stats) into the wizard. CSV
  // adapters (OFW-CSV, generic-CSV, mapped) all reduce to a canonical CSV +
  // case_meta + a stats object the "done" banner reads — same surface as a
  // parsed PDF, marked adapter:"generic_csv" (the done branch already handles
  // that kind). Clears any in-flight csvIngest sub-state.
  const commitCsv = useCallback(
    (
      sourceName: string,
      csvText: string,
      caseMeta: unknown,
      stats: GenericCsvIngestResult["stats"],
    ) => {
      const csvFile = new File([csvText], sourceName, { type: "text/csv" });
      const dt = new DataTransfer();
      dt.items.add(csvFile);
      if (fileInputRef.current) fileInputRef.current.files = dt.files;
      setCorpusFile(csvFile);
      setMetaJson(JSON.stringify(caseMeta, null, 2));
      setAdapter("generic_csv");
      setCsvIngest({ kind: "idle" });
      setPdfIngest({
        kind: "done",
        adapter: "generic_csv",
        stats,
        sourceName,
        warnings: [],
      });
      // Same silent hazard as the PDF path: a spreadsheet whose rows mostly
      // failed the timestamp rules still lands here looking healthy.
      if (
        isThinParse({
          messageCount: stats.message_count,
          sizeBytes: csvFile.size,
        })
      ) {
        reportWizardEvent({
          kind: "thin-parse",
          fileName: sourceName,
          sizeBytes: csvFile.size,
          detectedFormat: "generic_csv",
          messageCount: stats.message_count,
        });
      }
    },
    [reportWizardEvent],
  );

  // Drive the browser-side OFW ingest directly (skips detection). Used only
  // by the dev-affordance "Load demo-charlie PDF" button, which is a known
  // OFW file — the user-facing drop path goes through runPdfIngest's
  // detection-first flow instead.
  const runOfwIngest = useCallback(
    async (pdfFile: File, corpusId: string) => {
      setPdfIngest({
        kind: "running",
        pageNumber: 0,
        totalPages: 0,
        sourceName: pdfFile.name,
      });
      setErrorMsg("");
      try {
        const buf = await pdfFile.arrayBuffer();
        const pages = await extractPdfTextEol(buf, ({ pageNumber, totalPages }) =>
          setPdfIngest({
            kind: "running",
            pageNumber,
            totalPages,
            sourceName: pdfFile.name,
          }),
        );
        const { result } = ingestPdfByFormat(pages, "ofw", { corpusId });
        commitParsedPdf(pdfFile.name, pages.length, "ofw", result);
      } catch (e) {
        setPdfIngest({
          kind: "error",
          message: e instanceof Error ? e.message : String(e),
        });
      }
    },
    [commitParsedPdf],
  );

  // Drive the browser-side generic-CSV ingest. Validates required
  // columns + per-row timestamp/required-field rules, then canonicalizes
  // to the six-column shape (with empty subject). The synthesized CSV
  // is what gets uploaded to the server, not the user's raw file — so
  // bad data is caught before any server round-trip.
  //
  // Detection-first PDF ingest (Phase 1b — replaces the old "try OFW → on
  // failure fall back to Civil Communicator" trial-parse misroute). We extract
  // the page text ONCE, classify it with the structural registry, and route by
  // tier (spec §3–§4, §13):
  //   - auto    → parse the confidently-detected format straight through.
  //   - confirm → detection wasn't sure (low score or two formats too close):
  //               show the guess + alternatives and let the user pick.
  //   - recover → nothing recognizable: a clean "we couldn't read this" state
  //               with a manual format picker + a content-free get-in-touch CTA
  //               (NOT a dead-end, NOT the old wrong-parser error).

  const runPdfIngest = useCallback(
    async (pdfFile: File, corpusId: string) => {
      // A new corpus = a fresh case. Clear any resumed/stale completed run so
      // the downstream step checks (Parties / Cost / Run) don't show a prior
      // run's green ticks while you're still on step 1 (Bug D — a resumed
      // run-complete phase was lighting them). Independent of the analysis.
      setPhase({ kind: "setup" });
      setClassify({ kind: "idle" });
      clearActiveRun();
      // Light the progress indicator immediately — extraction can take several
      // seconds on a multi-MB PDF while pdf.js cold-starts (all in-browser;
      // nothing is transmitted at this stage).
      setPdfIngest({
        kind: "running",
        pageNumber: 0,
        totalPages: 0,
        sourceName: pdfFile.name,
      });
      setErrorMsg("");
      let pages: string[];
      // Positioned lines from the SAME extraction pass. AppClose parses by
      // column, not by line text, and re-extracting a 100 MB+ export a second
      // time in the browser is not an option.
      let geometry: { pageNumber: number; lines: PositionedLine[] }[];
      try {
        const buf = await pdfFile.arrayBuffer();
        const full = await extractPdfPages(buf, ({ pageNumber, totalPages }) =>
          setPdfIngest({
            kind: "running",
            pageNumber,
            totalPages,
            sourceName: pdfFile.name,
          }),
        );
        // Extraction is contractually an array or a throw, but "or a throw"
        // is the part we can't enforce: anything else here used to crash the
        // handler with an unhandled rejection and leave the wizard frozen on
        // the progress state, which reads to the user as a hang, not an error.
        if (!Array.isArray(full)) throw new Error("pdf extraction returned nothing");
        pages = full.map((pg) => pg.text);
        geometry = full.map((pg, i) => ({ pageNumber: i + 1, lines: pg.lines }));
      } catch (err) {
        // The file couldn't be read as a PDF at all (corrupt / not a PDF).
        setPdfIngest({
          kind: "error",
          message:
            "We couldn't open this file. Make sure it's a PDF export and try again.",
        });
        reportWizardEvent({
          kind: "pdf-open-failed",
          file: pdfFile,
          error: err,
        });
        return;
      }

      // Detection drives routing — no trial-parse, no silent fallback.
      const decision = decidePdfRoute(pages, {
        corpusId,
        lines: geometry,
        // AppClose declares no zone; reuse the one already chosen for this
        // case so a second import never re-asks.
        assignedTimezone: caseTimezone ?? null,
      });
      if (decision.tier === "auto") {
        const st = decision.parsed.result.stats as { needs_timezone?: boolean; pending_message_count?: number };
        if (st.needs_timezone) {
          setPdfIngest({
            kind: "timezone",
            sourceName: pdfFile.name,
            corpusId,
            pages,
            lines: geometry,
            format: decision.parsed.format,
            pendingCount: st.pending_message_count ?? 0,
            selectedZone: caseTimezone ?? defaultZone(),
          });
          return;
        }
        commitParsedPdf(
          pdfFile.name,
          pages.length,
          PDF_FORMAT_INFO[decision.parsed.format].adapter,
          decision.parsed.result,
        );
        return;
      }
      if (decision.tier === "confirm") {
        setPdfIngest({
          kind: "confirm",
          sourceName: pdfFile.name,
          corpusId,
          pages,
          lines: geometry,
          choices: decision.choices,
          guess: decision.guess,
          // Pre-select the guess so Continue is enabled out of the gate.
          selected: decision.guess,
        });
        // Not a failure — a detection-quality signal. Asking the user which
        // format this is means our fingerprints couldn't tell; how often that
        // happens, and for which format, is invisible otherwise.
        reportWizardEvent({
          kind: "format-confirm-shown",
          file: pdfFile,
          pages: pages.length,
          guessFormat: decision.guess,
          score: decision.classification.best?.score ?? null,
        });
        return;
      }
      // recover — nothing scored, or a detected format that failed to parse
      // (decision.note carries the plain reason in the latter case).
      setPdfIngest({
        kind: "recover",
        sourceName: pdfFile.name,
        corpusId,
        pages,
        lines: geometry,
        selected: null,
        note: decision.note,
      });
      // The note is the only thing that separates the two recovers, and they
      // are diagnostically opposite: "we know this export and can't read this
      // one" vs "nothing matched at all". The leader's score rides along even
      // when nothing cleared its threshold — a near-miss is the difference
      // between a broken adapter and a format we've never seen.
      reportWizardEvent({
        kind: decision.note ? "pdf-detected-unreadable" : "pdf-nothing-scored",
        file: pdfFile,
        pages: pages.length,
        detectedFormat: decision.classification.best?.name ?? null,
        score: decision.classification.best?.score ?? null,
      });
    },
    [commitParsedPdf, caseTimezone, reportWizardEvent],
  );

  // Confirm/recover step: the user picked a format. Parse the already-extracted
  // page text with that format. On a parse failure, fall back to the recover
  // state with the same picker (never a wrong-parser crash) so the user can
  // try a different format.
  const confirmPdfFormat = useCallback(
    (format: PdfFormat) => {
      setPdfIngest((prev) => {
        if (prev.kind !== "confirm" && prev.kind !== "recover") return prev;
        const { sourceName, corpusId, pages } = prev;
        try {
          const { result } = ingestPdfByFormat(pages, format, {
            corpusId,
            lines: prev.lines,
            assignedTimezone: caseTimezone ?? null,
          });
          const csvName =
            sourceName.replace(/\.pdf$/i, ".csv") || "ingested.csv";
          const csvFile = new File([result.csv], csvName, { type: "text/csv" });
          const dt = new DataTransfer();
          dt.items.add(csvFile);
          if (fileInputRef.current) fileInputRef.current.files = dt.files;
          rememberPdfPageCount(csvFile, pages.length);
          setCorpusFile(csvFile);
          setMetaJson(JSON.stringify(result.case_meta, null, 2));
          setAdapter(PDF_FORMAT_INFO[format].adapter);
          // We guessed one format, the user picked another, and theirs
          // worked. That is the ONLY way a wrong guess ever reaches us —
          // the user just fixes it and moves on.
          const guess = prev.kind === "confirm" ? prev.guess : null;
          if (guess && guess !== format) {
            reportWizardEvent({
              kind: "format-corrected",
              fileName: sourceName,
              pages: pages.length,
              guessFormat: guess,
              chosenFormat: format,
              messageCount: result.stats.message_count,
            });
          }
          return {
            kind: "done",
            adapter: PDF_FORMAT_INFO[format].adapter,
            stats: result.stats,
            sourceName,
            warnings: result.warnings,
          };
        } catch (err) {
          // The chosen format couldn't read the file. Surface why (never a
          // silent drop) and return to recover with the full picker so the user
          // can pick another — no wrong-parser crash.
          console.error(`PDF parse failed as ${format}:`, err);
          reportWizardEvent({
            kind: "pdf-format-rejected",
            fileName: sourceName,
            pages: pages.length,
            detectedFormat: format,
            error: err,
          });
          return {
            kind: "recover",
            sourceName,
            corpusId,
            pages,
            lines: prev.lines,
            selected: null,
            note: `Aurora couldn't read it as ${PDF_FORMAT_INFO[format].label}.`,
          };
        }
      });
    },
    [caseTimezone, reportWizardEvent],
  );

  // Update the highlighted format in the confirm/recover picker (does not parse
  // until the user hits Continue).
  const selectPdfFormat = useCallback((format: PdfFormat) => {
    setPdfIngest((prev) => {
      if (prev.kind !== "confirm" && prev.kind !== "recover") return prev;
      return { ...prev, selected: format };
    });
  }, []);

  // --- CSV ingest (Phase 3) ---

  // Finish an OFW-CSV / mapped-generic ingest once a timezone is in hand.
  // `zone` is the user's pick (remembered on the case). Returns true on
  // success (committed), false if the chosen zone still leaves rows unplaced
  // (shouldn't happen — a named zone always places naive rows).
  // The user chose a zone for a PDF whose export declares none. Re-run the
  // same format's parser with it and commit. The zone is remembered on the
  // case, so a second import never re-asks.
  const finishPdfWithZone = useCallback(
    (
      sourceName: string,
      corpusId: string,
      pages: string[],
      lines: { pageNumber: number; lines: PositionedLine[] }[],
      format: PdfFormat,
      zone: string,
    ) => {
      setCaseTimezone(zone);
      try {
        const { result } = ingestPdfByFormat(pages, format, {
          corpusId,
          lines,
          assignedTimezone: zone,
        });
        commitParsedPdf(sourceName, pages.length, PDF_FORMAT_INFO[format].adapter, result);
      } catch (e) {
        setPdfIngest({ kind: "idle" });
        setErrorMsg(e instanceof Error ? e.message : String(e));
      }
    },
    [commitParsedPdf],
  );

  const finishCsvWithZone = useCallback(
    (
      sourceName: string,
      corpusId: string,
      text: string,
      via: "ofw-csv" | "generic-csv-mapped",
      mapping: ColumnMapping | null,
      zone: string,
    ) => {
      setCaseTimezone(zone);
      try {
        if (via === "ofw-csv") {
          const r = ingestOfwCsv(text, { corpusId, inputTimezone: zone });
          commitCsv(sourceName, r.csv, r.case_meta, r.stats as unknown as GenericCsvIngestResult["stats"]);
        } else {
          const r = ingestGenericCsvMapped(text, mapping!, {
            corpusId,
            inputTimezone: zone,
          });
          commitCsv(sourceName, r.csv, r.case_meta, r.stats);
        }
      } catch (e) {
        setCsvIngest({ kind: "idle" });
        setErrorMsg(e instanceof Error ? e.message : String(e));
      }
    },
    [commitCsv],
  );

  // Drop/choose path for a CSV. Classifies the header (structural registry),
  // then routes: OFW-CSV → tz prompt; canonical schema → direct (tz prompt if
  // its ISO rows are naive); arbitrary → column-mapping. A remembered case
  // timezone is reused without re-asking.
  const runCsvIngest = useCallback(
    async (csvFile: File, corpusId: string) => {
      // New corpus = fresh case: clear any resumed/stale completed run so the
      // downstream step checks don't show a prior run's green ticks (Bug D).
      setPhase({ kind: "setup" });
      setClassify({ kind: "idle" });
      clearActiveRun();
      setErrorMsg("");
      let text: string;
      try {
        text = await csvFile.text();
      } catch (err) {
        setCsvIngest({ kind: "idle" });
        setErrorMsg("We couldn't read this file. Make sure it's a CSV and try again.");
        reportWizardEvent({
          kind: "csv-unreadable",
          file: csvFile,
          error: err,
        });
        return;
      }
      const decision = decideCsvRoute(text);

      if (decision.kind === "ofw-csv") {
        // Always naive → tz prompt (reuse the remembered zone if we have one).
        if (caseTimezone) {
          finishCsvWithZone(csvFile.name, corpusId, text, "ofw-csv", null, caseTimezone);
          return;
        }
        setCsvIngest({
          kind: "timezone",
          sourceName: csvFile.name,
          corpusId,
          text,
          via: "ofw-csv",
          mapping: null,
          signature: null,
          selectedZone: defaultZone(),
        });
        return;
      }

      if (decision.kind === "generic-csv") {
        // Canonical six-column schema. Parse direct; if its ISO timestamps are
        // naive (no offset) the adapter rejects them as missing_timezone — in
        // that case fall through to the tz prompt via the mapping path (the
        // canonical headers map 1:1 to roles).
        try {
          const r = ingestGenericCsv(text, { corpusId });
          if (r.stats.accepted_count === 0 && (r.stats.by_reason.invalid_timestamp ?? 0) > 0) {
            // Likely naive timestamps — re-run as a mapped (identity) CSV so
            // the case timezone can localize them.
            const identity: ColumnMapping = {
              timestamp: "timestamp",
              sender: "sender",
              body: "body",
              recipient: "recipient",
              subject: "subject",
            };
            if (caseTimezone) {
              finishCsvWithZone(csvFile.name, corpusId, text, "generic-csv-mapped", identity, caseTimezone);
              return;
            }
            setCsvIngest({
              kind: "timezone",
              sourceName: csvFile.name,
              corpusId,
              text,
              via: "generic-csv-mapped",
              mapping: identity,
              signature: null,
              selectedZone: defaultZone(),
            });
            return;
          }
          commitCsv(csvFile.name, r.csv, r.case_meta, r.stats);
        } catch (e) {
          setCsvIngest({ kind: "idle" });
          setErrorMsg(e instanceof Error ? e.message : String(e));
        }
        return;
      }

      // column-map — arbitrary tabular CSV.
      setCsvIngest({
        kind: "column-map",
        sourceName: csvFile.name,
        corpusId,
        text,
        headers: decision.headers,
        sampleRows: decision.sampleRows,
        rowCount: decision.rowCount,
        mapping: decision.proposed,
        suggested: decision.proposed,
        signature: decision.signature,
      });
    },
    [caseTimezone, commitCsv, finishCsvWithZone, reportWizardEvent],
  );

  // Every file that arrives — dropped or chosen — lands here first.
  //
  // Routing used to trust the extension, which lies in both directions: the
  // zip a co-parenting app emails you, saved as "record.pdf", went down the
  // PDF path and died inside pdf.js with an error that told the user nothing.
  // Sixteen bytes settle what the file actually is, so the message can name
  // their next move ("open the zip") instead of our constraint ("PDF or CSV").
  // Where they were standing when the tab went away.
  //
  // "A visitor arrived and never ran anything" is the shape of every
  // abandoned session, and until now they were all identical. Knowing it was
  // the upload step (couldn't get a file in) rather than the cost step (saw
  // the price) is the difference between a bug and a business question.
  //
  // sendBeacon, not fetch: during pagehide the browser owes fetch nothing,
  // even with keepalive. Sent once, and never after a finished run — leaving
  // when you're done is not leaving.
  const exitReportedRef = useRef(false);
  const exitStateRef = useRef({ step: STEP_DROP as WizardStep, done: false });
  useEffect(() => {
    exitStateRef.current = {
      step: currentStep,
      done: currentStep === STEP_DONE,
    };
  }, [currentStep]);
  useEffect(() => {
    const STEP_NAMES: Record<number, string> = {
      [STEP_DROP]: "upload",
      [STEP_PARTIES]: "parties",
      [STEP_COST]: "cost",
      [STEP_PAY]: "payment",
      [STEP_RUN]: "run",
      [STEP_DONE]: "done",
    };
    const onHide = () => {
      const { step, done } = exitStateRef.current;
      if (done || exitReportedRef.current) return;
      exitReportedRef.current = true;
      sendWizardEventOnUnload({
        server,
        accessCode: accessCode || null,
        payload: buildWizardEvent({
          kind: "left-at-step",
          step: STEP_NAMES[step] ?? "unknown",
        }),
      });
    };
    // pagehide covers close, navigate-away and the iOS cases `unload` misses.
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [server, accessCode]);

  // The one import failure the page genuinely cannot see: the OS file dialog
  // filters on `accept`, so an export it won't show can never reach any
  // handler — no event, no error, nothing. What we CAN see is the dialog
  // being opened again and again with nothing to show for it. Three opens
  // and no file is not someone browsing; it is someone whose file isn't
  // selectable. Fires once per session.
  const pickerOpensRef = useRef(0);
  const pickerFileArrivedRef = useRef(false);
  const pickerReportedRef = useRef(false);
  const notePickerOpened = useCallback(() => {
    if (pickerFileArrivedRef.current || pickerReportedRef.current) return;
    pickerOpensRef.current += 1;
    if (pickerOpensRef.current < 3) return;
    pickerReportedRef.current = true;
    reportWizardEvent({
      kind: "picker-no-file",
      attempts: pickerOpensRef.current,
    });
  }, [reportWizardEvent]);

  const acceptChosenFile = useCallback(
    async (f: File) => {
      const namedPdf = /\.pdf$/i.test(f.name) || f.type === "application/pdf";
      const namedCsv = /\.csv$/i.test(f.name) || f.type === "text/csv";
      const actual = await sniffFileType(f);

      // Sniffing REFUSES, it never admits. A positive "this is a zip" is
      // certain and worth acting on; the absence of a %PDF header at byte 0
      // is not — real generators prepend bytes, and a stricter gate here
      // would turn a working import into a dead end. So anything we can't
      // positively identify as the wrong thing keeps the old name-based
      // routing.
      const wrongContainer = actual !== null && actual !== "pdf";
      if (wrongContainer) {
        const named = namedPdf
          ? "This file is named like a PDF, but it isn't one. "
          : "";
        setErrorMsg(
          named +
            (describeUnsupportedFile(actual) ??
              `Aurora reads PDF or CSV message exports. Drop your ${CLOUD_FORMATS_OR} export.`),
        );
        reportWizardEvent({
          kind: "unsupported-file-type",
          file: f,
          actualType: actual,
        });
        return;
      }

      if (actual === "pdf" || namedPdf) {
        void runPdfIngest(f, slugFromFileName(f.name));
        return;
      }
      if (namedCsv || actual === null) {
        // Plain text is what a CSV is, and a .txt of exported messages is a
        // legitimate thing to try — the CSV path has its own honest failure.
        void runCsvIngest(f, slugFromFileName(f.name));
        return;
      }
      setErrorMsg(
        `Aurora reads PDF or CSV message exports. Drop your ${CLOUD_FORMATS_OR} export.`,
      );
      reportWizardEvent({
        kind: "unsupported-file-type",
        file: f,
        actualType: actual,
      });
    },
    [runPdfIngest, runCsvIngest, reportWizardEvent],
  );

  // The column-mapping step's "Continue" — save the profile (keyed by header
  // signature, so the same shape skips the step next time), then either commit
  // (offset-aware rows) or fall through to the tz prompt (naive rows).
  const confirmColumnMapping = useCallback(() => {
    if (csvIngest.kind !== "column-map") return;
    const { sourceName, corpusId, text, mapping, signature, headers } = csvIngest;
    saveProfile(signature || headerSignature(headers), mapping, sourceName);
    try {
      const r = ingestGenericCsvMapped(text, mapping, { corpusId });
      if (r.stats.needs_timezone) {
        setCsvIngest({
          kind: "timezone",
          sourceName,
          corpusId,
          text,
          via: "generic-csv-mapped",
          mapping,
          signature,
          selectedZone: caseTimezone ?? defaultZone(),
        });
        return;
      }
      commitCsv(sourceName, r.csv, r.case_meta, r.stats);
    } catch (e) {
      setCsvIngest({ kind: "idle" });
      setErrorMsg(e instanceof Error ? e.message : String(e));
    }
  }, [csvIngest, caseTimezone, commitCsv]);

  const loadSample = () => {
    const file = new File([SAMPLE_CSV], "sample-corpus.csv", {
      type: "text/csv",
    });
    const dt = new DataTransfer();
    dt.items.add(file);
    if (fileInputRef.current) fileInputRef.current.files = dt.files;
    setCorpusFile(file);
    setMetaJson(SAMPLE_META);
    setAdapter("generic_csv");
  };

  // Bundled real fixture (corpora/demo-charlie/ copied into public/samples/
  // at build time so the browser can fetch it without local file picks).
  // Reliably surfaces 10+ patterns; convert costs ~$2 against real Opus.
  const loadDemoCharlie = async () => {
    setErrorMsg("");
    try {
      const [csvResp, metaResp] = await Promise.all([
        fetch("/samples/demo-charlie/ofw/corpus.csv"),
        fetch("/samples/demo-charlie/meta.json"),
      ]);
      if (!csvResp.ok) throw new Error(`corpus.csv: HTTP ${csvResp.status}`);
      if (!metaResp.ok) throw new Error(`meta.json: HTTP ${metaResp.status}`);
      const csvText = await csvResp.text();
      const metaText = await metaResp.text();
      // Round-trip meta through JSON.parse for validation + pretty-print.
      const meta = JSON.parse(metaText);
      const file = new File([csvText], "demo-charlie.csv", {
        type: "text/csv",
      });
      const dt = new DataTransfer();
      dt.items.add(file);
      if (fileInputRef.current) fileInputRef.current.files = dt.files;
      setCorpusFile(file);
      setMetaJson(JSON.stringify(meta, null, 2));
      setAdapter("ofw");
      setPdfIngest({ kind: "idle" });
    } catch (e) {
      setErrorMsg(
        `failed to load demo-charlie: ${e instanceof Error ? e.message : e}`,
      );
    }
  };

  // Bundled PDF fixture: fetches the demo-charlie OFW PDF and runs it
  // through the browser-side adapter end-to-end.
  const loadDemoCharliePdf = async () => {
    setErrorMsg("");
    try {
      const pdfResp = await fetch(
        "/samples/demo-charlie/ofw/OFW_Messages_Report_demo_charlie.pdf",
      );
      if (!pdfResp.ok) throw new Error(`PDF: HTTP ${pdfResp.status}`);
      const blob = await pdfResp.blob();
      const pdfFile = new File([blob], "OFW_Messages_Report_demo_charlie.pdf", {
        type: "application/pdf",
      });
      await runOfwIngest(pdfFile, "demo-charlie");
    } catch (e) {
      setErrorMsg(
        `failed to fetch demo-charlie PDF: ${e instanceof Error ? e.message : e}`,
      );
    }
  };

  // --- Run lifecycle ---
  // `previewSnapshot` outlives the preview phase so the patterns stay
  // visible while convert runs. The phase machine still drives buttons
  // and event-log routing.
  const [previewSnapshot, setPreviewSnapshot] = useState<{
    previewId: string;
    patterns: Pattern[];
    events: SseEvent[];
  } | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: "setup" });

  // Deep-link entry (Phase 3.1): the "your analysis is ready" email links to
  // /#run=<id>&token=<token> (token in the FRAGMENT so it never hits server logs
  // or Referer). Parsed once at load; when present it short-circuits the wizard
  // and renders a gate-free download landing (the run-token is the auth — see the
  // early return below). null on a normal visit.
  const [deepLink] = useState<DeepLink | null>(() =>
    typeof window === "undefined" ? null : parseDeepLink(window.location.hash),
  );
  const [deepLinkState, setDeepLinkState] = useState<DeepLinkUiState>(
    deepLink ? "loading" : "ready",
  );
  // In-place re-run of the resolver (the classifyNonce idiom). Retry must NOT
  // be a page reload: on a still-down connection the reload's document fetch
  // fails and a browser error page replaces the "your link is still good"
  // screen — the exact reassurance the unreachable state exists to give
  // (review, 2026-07-10).
  const [deepLinkNonce, setDeepLinkNonce] = useState(0);
  // The elapsed clock is the one genuinely-continuous real signal during the
  // run. Start it the instant the run step opens (setup included) so it doesn't
  // sit at 0:00 through the setup→preview-running gap; stop on terminal states.
  const runClockActive =
    currentStep === STEP_RUN &&
    phase.kind !== "run-complete" &&
    phase.kind !== "preview-failed" &&
    phase.kind !== "convert-failed";
  const runElapsedLabel = useElapsedLabel(runClockActive);
  // Cancel is a two-step confirm — stopping a run is destructive (the access
  // code was already spent the moment the run launched, so there's no restart).
  const [confirmCancel, setConfirmCancel] = useState(false);
  // Submit confirmation (UX audit F): the moment of commitment must be
  // unmistakable — starting the run spends the single-use access code.
  const [confirmLaunch, setConfirmLaunch] = useState(false);
  // Calm (non-error) note on the cost screen for the Checkout round-trip —
  // e.g. "Payment canceled — you can try again." Distinct from errorMsg, which
  // renders as a red failure strip.
  const [payNotice, setPayNotice] = useState<string>("");
  // Armed by the `?paid=1` return AFTER the stashed file is rehydrated into
  // state; a one-shot effect then launches the run (we can't call startPreview
  // inline because it closes over the not-yet-updated corpusFile state).
  const [autoLaunchAfterPay, setAutoLaunchAfterPay] = useState(false);
  // True for the rest of this visit once the user came back from a successful
  // Checkout. Guards against a SECOND charge: if the launch needs a retry
  // (e.g. the webhook lands a beat after the redirect), Authorize re-launches
  // the run instead of minting another Checkout session.
  const [paidThisVisit, setPaidThisVisit] = useState(false);
  // Embedded checkout (Option A, 2026-07-22). The pk_ key arrives via
  // /config (null → the wizard keeps the hosted-redirect flow, incl. local
  // dev's fake checkout); the client secret is minted by goToCheckout and
  // consumed by the Payment step's in-card mount.
  const [stripePublishableKey, setStripePublishableKey] = useState<string | null>(null);
  const [embeddedClientSecret, setEmbeddedClientSecret] = useState<string | null>(null);
  // True from Stripe's onComplete until the entitlement poll resolves —
  // navigation freezes so Back can't expose a live price CTA on a
  // just-charged card (seam audit 2026-07-22).
  const [entitlementPolling, setEntitlementPolling] = useState(false);
  // Server-confirmed entitlement discovered OUTSIDE prescan (cost-step
  // arrival check, seam audit 2026-07-22): prescan's already_paid is a
  // deliberately Stripe-free local read, so a lost/slow webhook painted a
  // PAID code's cost screen with the full-price CTA. One entitlement check
  // on cost-step arrival converges the posture through the endpoint that
  // already reconciles (and fires the one-time payment side effects).
  const [entitledOnServer, setEntitledOnServer] = useState(false);
  // WHICH tier that server-confirmed payment bought (review R1, 2026-08-05):
  // /billing/entitlement's paid_tier. On the lost-webhook path prescan's own
  // paid_tier is ALWAYS null (the record didn't exist when prescan read it),
  // so this is the only tier knowledge the per-tier viability gate
  // (paidTierInviable) can key on there — without it an Access payment got
  // judged by Standard's ceiling. Null until a paid entitlement response
  // carries a tier (older server, legacy record) — the gate then fails
  // CLOSED to the Access ceiling.
  const [entitledPaidTier, setEntitledPaidTier] = useState<string | null>(null);
  const entitlementCheckedForRef = useRef<string | null>(null);
  // Live tier prices, refreshed from /config at every cost-step entry (seam
  // audit 2026-07-22): the cards used to render prescan-time prices while
  // /billing/checkout re-resolves the operator-overridable price at mint —
  // an operator reprice between file-drop and Continue made the button name
  // one amount and the session charge another. The server's mint-time price
  // stays authoritative; this keeps what the user READS converged with it.
  const [livePrices, setLivePrices] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    let cancelled = false;
    getConfig(server)
      .then((c) => {
        if (!cancelled) setStripePublishableKey(c.stripe_publishable_key ?? null);
      })
      .catch(() => {
        /* config unreachable → hosted-redirect fallback, same as key-absent */
      });
    return () => {
      cancelled = true;
    };
  }, [server]);
  // PWYC cost screen (pricing decision 2026-06-30): the user self-selects
  // Solidarity / Standard / Access. Standard is the pre-checked default;
  // choosing Access reveals a one-checkbox hardship attestation (the only
  // gate on the tier — no documents, no income field, ever). Switching
  // tiers re-arms the attestation.
  const [pwycChoice, setPwycChoice] = useState("Standard");
  const [pwycAccessAttested, setPwycAccessAttested] = useState(false);
  // Door 2 (per-tier cost gate): when a (re-)prescan marks the currently
  // selected tier inviable — the user swapped in a larger file and its run
  // cost now exceeds that tier's ceiling — clear the selection so a
  // no-longer-offered price can't ride through to checkout. The user then
  // picks a viable card (Continue stays disabled meanwhile, see
  // continueDisabled's step-2 gate). Clearing also re-arms the Access
  // attestation, mirroring every other tier switch.
  useEffect(() => {
    if (prescan.kind !== "ready") return;
    const next = reconcileTierChoice({
      tiers: prescan.estimate.pwyc_tiers,
      choice: pwycChoice,
    });
    if (next !== pwycChoice) {
      setPwycChoice(next);
      setPwycAccessAttested(false);
    }
  }, [prescan, pwycChoice]);
  // True once the user restarts after a beat-1 cancel — the next run's
  // passphrase gate explains that the new key replaces the old one.
  const [restartedRun, setRestartedRun] = useState(false);
  // Terminal: this access code has used both preview starts (server 429
  // preview_limit_for_code). The cost step swaps its submit affordance for
  // a no-fresh-starts panel with the two real exits (email us / new code).
  const [capBlocked, setCapBlocked] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  // Set the instant a POST starts; cleared on response. Distinct from
  // phase because the user shouldn't be able to fire a second
  // /runs/preview while the first is in-flight (MAX_CONCURRENT_RUNS=1
  // will 503 the second one — what Rich hit on 2026-05-21).
  const [submitting, setSubmitting] = useState<"preview" | "convert" | null>(
    null,
  );
  const sseCleanupRef = useRef<(() => void) | null>(null);
  // Interval id for the queued-run status poll (a queued run has no SSE stream
  // until it's promoted). Torn down alongside the SSE so every teardown path
  // also stops the poll.
  const queuePollRef = useRef<number | null>(null);
  // Set when the user clicks cancel; consulted when a terminal failed
  // state arrives so a self-inflicted stop never paints the crash banner.
  const cancelRequestedRef = useRef(false);
  // Finding 3b (launch-audit review, 2026-07-04): tracks which preview id
  // already burned its bounded status-fetch retry once (attachPreviewStream's
  // onTerminal, below) without a fold — the SAME id's status check ALSO
  // failing a second time (this time from the dead-stream poll's own tick,
  // further down) is what finally folds to preview-failed. Keeps the fold as
  // the final fallback rather than the first response to one transient
  // blip, without churning forever.
  const previewStatusRetryExhaustedRef = useRef<string | null>(null);
  // Finding 3a (launch-audit review, 2026-07-04): true from the moment the
  // user hits local-stop while phase.kind === "preview-ready" (triggerCancel,
  // below — there's no live server run to cancel at that instant) until the
  // NEXT startConvert invocation resets it. Lets startConvert's in-flight
  // /runs/start tell, on resolution, whether the user already walked away
  // while it was in the air.
  const localStopBeforeStartRef = useRef(false);
  // The AbortController backing the CURRENT /runs/start POST (if any) — lets
  // triggerCancel's local-stop branch abort the corpus upload itself
  // (belt-and-braces alongside localStopBeforeStartRef's suspenders) instead
  // of just letting it run to completion unaborted.
  const convertAbortRef = useRef<AbortController | null>(null);

  const tearDownSse = useCallback(() => {
    if (sseCleanupRef.current) {
      sseCleanupRef.current();
      sseCleanupRef.current = null;
    }
    if (queuePollRef.current !== null) {
      window.clearInterval(queuePollRef.current);
      queuePollRef.current = null;
    }
  }, []);

  useEffect(() => () => tearDownSse(), [tearDownSse]);

  // When an error toast appears, .has-toast shrinks the active pane's
  // scroll area (the toast gets its own band below — styles.css
  // .wizard-status). Pin the pane to its bottom so the footer CTA the
  // user just clicked stays in view as the band opens, instead of
  // slipping below the fold.
  useEffect(() => {
    if (!errorMsg) return;
    const pane = document.querySelector<HTMLElement>(".work-pane.active");
    if (pane) pane.scrollTop = pane.scrollHeight;
  }, [errorMsg]);

  // Fire the entity classifier when a corpusFile is in scope AND the
  // connection inputs are filled. Re-fires on file replacement, adapter
  // change, server/token change. Aborts in-flight on dep change.
  //
  // Gated on the size check: this is a real billable Haiku call, so it must not
  // fire until prescan has cleared THIS file as under budget. An over-budget
  // record dead-ends at the Source-step off-ramp and never reaches Parties, so
  // spending the classify call on it is pure waste (the whole reason the
  // off-ramp moved ahead of "Confirm the parties"). See classifyGateState.
  // The classify effect reads meta.json for the party hints but must not
  // re-run when meta.json changes: confirming the parties writes the
  // grounding into meta.json, and a re-run would null the grounding, remount
  // the editor (losing the pronoun choice on Back → Continue) and spend a
  // second classifier call (review 2026-09-08). A ref keeps the read fresh.
  const metaJsonRef = useRef(metaJson);
  metaJsonRef.current = metaJson;
  useEffect(() => {
    if (!corpusFile || corpusFile.size === 0) {
      setClassify({ kind: "idle" });
      return;
    }
    if (server.trim() === "" || accessCode.trim() === "") {
      setClassify({ kind: "idle" });
      return;
    }
    const gate = classifyGateState({
      prescanKind: prescan.kind,
      readyForThisFile: prescanReadyForRef.current === corpusFile,
      overBudget:
        prescan.kind === "ready" && prescan.estimate.over_budget === true,
    });
    // "wait": size check pending or a stale ready from a just-replaced file —
    // hold without disturbing classify state (the effect re-runs when prescan
    // resolves, via the prescan.kind dep). "skip": over budget → leave classify
    // idle and never spend. "go": fall through and classify.
    if (gate === "wait") return;
    if (gate === "skip") {
      setClassify({ kind: "idle" });
      return;
    }
    let cancelled = false;
    // New classification → drop any prior grounding and bump the epoch so the
    // people editor remounts and re-seeds from the fresh result.
    setClassifyGrounding(null);
    setClassifyEpoch((e) => e + 1);
    setClassify({ kind: "running", sourceName: corpusFile.name });
    // Pluck party hints from meta.json — OFW's adapter fills these from
    // the cover page, CC leaves them blank. Passing through preserves
    // the adapter's order in the classifier banner.
    let hintPartyA: string | null = null;
    let hintPartyB: string | null = null;
    try {
      const meta = JSON.parse(metaJsonRef.current);
      const pv = meta?.prompt_vars;
      if (pv && typeof pv === "object") {
        if (typeof pv.party_a === "string") hintPartyA = pv.party_a;
        if (typeof pv.party_b === "string") hintPartyB = pv.party_b;
      }
    } catch {
      // meta.json malformed mid-edit — classifier falls back to freq derivation.
    }
    (async () => {
      try {
        const result = await classifyEntities({
          server,
          clientId,
          accessCode,
          corpusFile,
          adapter,
          hintPartyA,
          hintPartyB,
        });
        if (cancelled) return;
        setClassify({ kind: "ready", result });
      } catch (e) {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : String(e);
        setClassify({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
    // adapter, corpusFile, accessCode, server, clientId are the inputs
    // to the POST body. Re-classify on any change. prescan.kind is here so the
    // effect re-runs when the size check resolves (idle/loading → ready/error)
    // and the gate above flips from "wait" to "go"/"skip". prescanReadyForRef is
    // a ref, intentionally not a dep.
    // classifyNonce is the error-retry trigger (same file, fresh scan).
    // metaJson is read through metaJsonRef, deliberately NOT a dep (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gate reads prescan.estimate via prescan.kind; metaJson via ref
  }, [corpusFile, adapter, accessCode, server, clientId, prescan.kind, classifyNonce]);

  // --- Page-refresh resume ---
  // Deep-link resolver: given run+token from the email link, find the run and
  // arm the gate-free landing. Completed → set the run-complete phase (so the
  // landing's Download button reuses triggerViewerDownload → DoneScreen).
  // Still running → "almost there". Anything else / 404 / 410 (purged past the
  // 7-day window) → "expired". The run-token is the only auth needed.
  useEffect(() => {
    if (!deepLink) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await fetchStatus({
          server,
          runId: deepLink.runId,
          runToken: deepLink.runToken,
        });
        if (cancelled) return;
        const ui = classifyChainState(status.chain_state);
        if (ui === "ready") {
          setPhase({
            kind: "run-complete",
            runId: deepLink.runId,
            runToken: deepLink.runToken,
            events: [],
          });
        }
        setDeepLinkState(ui);
      } catch (e) {
        if (cancelled) return;
        // Only the statuses the run-status route actually emits for a dead
        // link (404 unknown run, 403 bad token, 410 purged) count as
        // "expired". Everything else — network drop, 5xx, and edge-generated
        // 4xxs (Cloudflare 429 / WAF-challenge 403 with an HTML body say
        // nothing about the link) — is transient: saying "expired" there
        // told users on flaky wifi to re-run a finished analysis
        // (sweep + review, 2026-07-10).
        const st = (e as ApiError | undefined)?.status;
        const dead = st === 403 || st === 404 || st === 410;
        setDeepLinkState(dead ? "expired" : "unreachable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deepLink, server, deepLinkNonce]);

  // Track whether the mount-time resume probe is still running so the UI
  // can show "resuming…" instead of flashing the setup screen.
  const [resuming, setResuming] = useState<boolean>(
    () => !deepLink && loadActiveRunForCode(accessCode) !== null,
  );
  useEffect(() => {
    if (deepLink) return; // deep-link entry owns the phase; skip normal resume
    const active = loadActiveRunForCode(accessCode);
    if (!active) return;
    let cancelled = false;
    (async () => {
      try {
        const status = await fetchStatus({
          server,
          runId: active.runId,
          runToken: active.runToken,
        });
        if (cancelled) return;
        const state = status.chain_state;
        // Re-show the save-your-passphrase card on resumed tabs: the key is
        // in-memory only, so a reload would otherwise orphan it silently
        // (UX audit finding A). The server only serves this while the run
        // is in flight, to the run-token holder.
        if (status.pending_passphrase) {
          setRunPassphrase(status.pending_passphrase);
        }
        setRunStartedAt(status.started_at ?? null);
        if (state === "running" || state === "initializing") {
          // Still in flight server-side — reattach SSE + restore the
          // *-running phase. The events array starts empty; the SSE
          // resume sends a Last-Event-ID of 0 so the server replays
          // every prior event.
          if (active.kind === "preview") {
            setPhase({
              kind: "preview-running",
              previewId: active.runId,
              previewToken: active.runToken,
              events: [],
            });
            attachPreviewStream(active.runId, active.runToken);
          } else {
            setPhase({
              kind: "convert-running",
              runId: active.runId,
              runToken: active.runToken,
              events: [],
            });
            attachConvertStream(active.runId, active.runToken);
          }
          // Land the user ON the run, not on step 0. Restoring the phase
          // without the step left every reload on an empty "Drop your
          // messages" with the paid run invisible (audit B6) — in the
          // close-the-tab era, reload is the DEFAULT return path.
          setCurrentStep(STEP_RUN);
        } else if (state === "queued") {
          // Reloaded onto a still-queued run → re-enter the queued hand-off and
          // resume polling for promotion.
          setPhase({
            kind: "convert-queued",
            runId: active.runId,
            runToken: active.runToken,
            position: status.queue_position ?? null,
          });
          setCurrentStep(STEP_RUN);
          startQueuePoll(active.runId, active.runToken);
        } else if (state === "preview_ready") {
          const patterns = status.preview_summary?.patterns ?? [];
          setPhase({
            kind: "preview-ready",
            previewId: active.runId,
            previewToken: active.runToken,
            events: [],
            patterns,
          });
          setPreviewSnapshot({
            previewId: active.runId,
            patterns,
            events: [],
          });
          clearActiveRun();
          setCurrentStep(STEP_RUN);
        } else if (state === "run_complete") {
          setPhase({
            kind: "run-complete",
            runId: active.runId,
            runToken: active.runToken,
            events: [],
          });
          // Straight to Download — the whole point of keeping the pointer is
          // reload-and-re-download (the pane is phase-driven, so it works
          // without any in-memory wizard state).
          setCurrentStep(STEP_DONE);
          // Keep the active-run pointer (unlike the other terminal branches):
          // it's what lets the user reload the wizard and re-download their
          // file within the run's 24h life. It's cleared on an explicit "Done"
          // (handleClose) and on a 404/410 (run purged → catch below). A run
          // from a different access code can't resume — loadActiveRunForCode
          // gates on the code, which "Done" also clears.
        } else if (state === "preview_failed" || state === "run_failed") {
          // exit 130 = the user's own cancel (the server has no distinct
          // canceled state) — survive the reload as a calm stop, not a crash.
          const cancelled = status.exit_code === 130;
          if (active.kind === "preview") {
            setPhase({
              kind: "preview-failed",
              previewId: active.runId,
              events: [],
              reason: state,
              cancelled,
            });
          } else {
            setPhase({
              kind: "convert-failed",
              runId: active.runId,
              events: [],
              reason: state,
              cancelled,
            });
          }
          clearActiveRun();
          // Show the failure where it happened instead of a silent step-0
          // reset — the user needs to see that something went wrong.
          setCurrentStep(STEP_RUN);
        } else {
          // preview_consumed or unknown → don't try to restore; just clear.
          clearActiveRun();
          // Nothing restored: if the stored code is dead too, the user is
          // stranded on an empty wizard — let Shell revalidate and decide.
          notifyResumeDeadEnd();
        }
      } catch (e) {
        if (cancelled) return;
        // 404 / 410 → the run is gone (purged, never existed, wrong server).
        // Clear so the user gets a clean setup screen instead of a stuck
        // "resuming" state.
        clearActiveRun();
        notifyResumeDeadEnd();
        // Read the STATUS, the way the deep-link probe above does. This tested
        // e.message, which is the server's `detail` string and carries no
        // status code — the route sends 404 with detail "unknown cloud_run_id",
        // so the guard matched nothing and EVERY stale pointer opened the
        // wizard under a red "resume failed: unknown cloud_run_id" banner. The
        // one case the guard exists for was the one case it never caught.
        const st = (e as ApiError | undefined)?.status;
        const gone = st === 403 || st === 404 || st === 410;
        if (!gone && e instanceof Error) {
          setErrorMsg(`resume failed: ${e.message}`);
        }
      } finally {
        if (!cancelled) setResuming(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run once at mount. The deps the effect reads (server, attach*)
    // come from the same render's closure; if server changes mid-resume
    // we already abort via `cancelled`. We intentionally omit the
    // attach* helpers from deps — they recreate every render, which
    // would trigger infinite resumes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Write the classifier's (or user-edited) grounding into meta.json. Writes
  // the SAME four-field contract the desktop writes (write_case_grounding):
  // prompt_vars.{party_a,party_b,context} + top-level confirmed_children,
  // has_no_children, and nickname_map (flat alias->canonical). Without the
  // structured children the viewer's anonymize-children export stays dark on
  // cloud cases, so both products must write all four. Mutates the metaJson
  // textarea so the user can still peek/edit the raw JSON.
  const applyClassifierToMeta = (
    partyA: string,
    partyB: string,
    childNames: string[],
    aliasesByChild: Record<string, string[]> = {},
    pronouns: Record<string, string | undefined> = {},
  ) => {
    try {
      const parsed = JSON.parse(metaJson || "{}");
      if (parsed && typeof parsed === "object") {
        if (!parsed.prompt_vars || typeof parsed.prompt_vars !== "object") {
          parsed.prompt_vars = {};
        }
        parsed.prompt_vars.party_a = partyA;
        parsed.prompt_vars.party_b = partyB;
        parsed.prompt_vars.context = deriveContext(childNames, aliasesByChild);
        // The readability pass keys its mode on these two keys; absent =
        // not declared (first names for that party).
        applyPronounsToPromptVars(parsed.prompt_vars, partyA, partyB, pronouns);
        parsed.confirmed_children = childNames.map((name) => ({ name }));
        parsed.has_no_children = childNames.length === 0;
        parsed.nickname_map = nicknameMapFromAliases(aliasesByChild);
        setMetaJson(JSON.stringify(parsed, null, 2));
      }
    } catch {
      // meta.json malformed mid-edit — leave alone; user fixes manually.
    }
  };

  const confirmClassify = () => {
    const r =
      classify.kind === "ready" || classify.kind === "confirmed"
        ? classify.result
        : null;
    if (!r) return;
    // Persist the people editor's CURRENT grounding — children + folded/edited
    // nicknames (child + party aliases both go into nickname_map; deriveContext
    // ignores the party entries so the framing sentence stays child-only). The
    // editor emits its seeded state on mount, so the matched nicknames are
    // captured even with no manual edit. Falls back to the raw classifier
    // children if grounding hasn't arrived yet.
    const g = classifyGrounding;
    if (g) {
      applyClassifierToMeta(
        r.party_a,
        r.party_b,
        g.children,
        { ...g.childAliases, ...g.partyAliases },
        g.partyPronouns ?? {},
      );
    } else {
      applyClassifierToMeta(
        r.party_a,
        r.party_b,
        r.children.map((c) => c.name),
        {},
      );
    }
    setClassify({ kind: "confirmed", result: r });
  };

  // Track whether meta.json's prompt_vars.context still holds a TODO
  // sentinel — the chain refuses to run when it does, so we gate the
  // Start preview button on this guard regardless of classify state.
  const contextHasTodoSentinel = useMemo<boolean>(() => {
    try {
      const parsed = JSON.parse(metaJson);
      const ctx = parsed?.prompt_vars?.context;
      if (typeof ctx !== "string") return false;
      return ctx.trim().startsWith("TODO:");
    } catch {
      return false;
    }
  }, [metaJson]);

  const missing: string[] = [];
  if (server.trim() === "") missing.push("server URL");
  if (accessCode.trim() === "") missing.push("access code");
  if (clientId.trim() === "") missing.push("client ID");
  if (corpusFile === null) missing.push("corpus.csv file");
  if (metaJson.trim() === "") missing.push("meta.json");
  if (contextHasTodoSentinel) missing.push("case context");
  const ready = missing.length === 0;

  // Cost-screen pilot-free state: the grant covers any in-scope run (never
  // an over-boundary Service-routed record). Drives the step-3 CTA label,
  // helper line, and lede so the whole step agrees with the
  // free-during-pilot band that PwycCostPanel renders.
  const pilotFreeCovered =
    prescan.kind === "ready" &&
    prescan.estimate.pilot_free === true &&
    prescan.estimate.service_route !== true;

  // Server-known already-paid (launch-audit fix, 2026-07-04) — independent of
  // `paidThisVisit` (set only after THIS visit's Stripe redirect). A fresh
  // browser session re-entering a code that already has a completed payment
  // on file (webhook or reconcile) is a normal launch-era state: a cancelled/
  // failed checkout re-arms the code while its payment survives (never
  // double-charge), so paid-but-unused codes recur. Either signal means the
  // same thing to the cost step — no charge is pending — so they're combined
  // here and threaded everywhere `paidThisVisit` alone used to gate the
  // paid-run treatment.
  const alreadyPaid =
    paidThisVisit ||
    entitledOnServer ||
    (prescan.kind === "ready" && prescan.estimate.already_paid === true);

  // Cost-step arrival entitlement check (see entitledOnServer above). Once
  // per code; only when prescan's local read said UNPAID on a billed rail —
  // the one state where a lost webhook can misprice the screen. Errors are
  // swallowed (the endpoint 404s when Stripe is off; posture just stays on
  // prescan's answer).
  useEffect(() => {
    if (currentStep !== STEP_COST) return;
    if (prescan.kind !== "ready" || prescan.estimate.already_paid === true) return;
    if (pilotFreeCovered || paidThisVisit || entitledOnServer) return;
    if (!accessCode || entitlementCheckedForRef.current === accessCode) return;
    entitlementCheckedForRef.current = accessCode;
    void getEntitlement({ server, accessCode })
      .then((r) => {
        if (r.paid) {
          setEntitledOnServer(true);
          // Carry the server's word on WHICH tier was bought into the
          // viability gate (review R1) — prescan couldn't know it here.
          if (typeof r.paid_tier === "string") setEntitledPaidTier(r.paid_tier);
        }
      })
      .catch(() => {
        /* stay on prescan's posture */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot per code at cost-step arrival
  }, [currentStep, prescan.kind, accessCode]);

  // Refresh the displayed ladder on every cost-step entry (see livePrices).
  useEffect(() => {
    if (currentStep !== STEP_COST) return;
    let cancelled = false;
    getConfig(server)
      .then((c) => {
        if (cancelled || !Array.isArray(c.pwyc_tiers)) return;
        const next: Record<string, number> = {};
        for (const t of c.pwyc_tiers) {
          if (t && typeof t.name === "string" && typeof t.price_usd === "number") {
            next[t.name] = t.price_usd;
          }
        }
        setLivePrices(next);
      })
      .catch(() => {
        /* unreachable config → keep prescan-time prices */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refresh on entry
  }, [currentStep, server]);

  // Whether this journey includes the in-card Payment step (Option A).
  // Paying self-serve journeys only, and only when the embedded rail is
  // available (pk_ key in /config). Once paid THIS visit the step stays on
  // the rail as a checkmark — the journey the user walked shouldn't vanish —
  // but a code that arrived already paid (re-entry, comp-adjacent) skips it,
  // exactly like pilot-free.
  const showPaymentStep =
    stripePublishableKey !== null &&
    !pilotFreeCovered &&
    (paidThisVisit || !alreadyPaid);

  // Record too large to run within the per-run budget ceiling. The chain would
  // refuse it at the pre-flight cost gate, so we pre-empt at the cost screen:
  // show the off-ramp (email us) instead of the tier grid + Authorize, and
  // block advancement. The server computes this with the same estimator the
  // chain gates on, so a record that passes here will not hit the gate.
  const prescanOverBudget =
    prescan.kind === "ready" && prescan.estimate.over_budget === true;

  // Door 2 × already-paid (review fix, 2026-08-05): this code's payment is
  // for a tier whose ceiling the record's run cost now exceeds — the classic
  // shape is an Access payer who went Back and swapped in a larger export
  // (re-prescan: already_paid true, Access viable false). Launching would be
  // the underwater run the per-tier gate exists to prevent, and there is no
  // bigger tier to buy without a second charge (which we never take) — so
  // this gets the SAME CostOffRamp treatment the unpaid all-inviable flow
  // gets, with copy acknowledging the payment. continueDisabled blocks
  // Submit on this same flag so the screen and the gate can't drift.
  // Exclusions mirror checkoutClosed's: over-budget's own off-ramp takes
  // precedence, a Service-routed record keeps its "write us" path, and a
  // sponsored run has no paid tier to gate.
  const paidTierBlocked =
    prescan.kind === "ready" &&
    !prescanOverBudget &&
    !pilotFreeCovered &&
    alreadyPaid &&
    prescan.estimate.service_route !== true &&
    paidTierInviable({
      tiers: prescan.estimate.pwyc_tiers,
      // Prescan's record read, else the entitlement response's (review R1 —
      // the lost-webhook path where prescan ran before the record existed);
      // both name the same payment. Still-unknown fails closed inside the
      // helper (Access ceiling).
      paidTier: prescan.estimate.paid_tier ?? entitledPaidTier,
    });

  // A PAID run (priced tier, not pilot-free) whose self-serve checkout is CLOSED
  // — Stripe isn't wired, or the operator pulled the public_access switch. The
  // /billing/checkout gate would 404, so instead of the tier grid + Authorize we
  // show a request-access off-ramp and suppress the footer (no dead Authorize).
  // Exemptions: a record already paid (this visit's Stripe round-trip, OR the
  // server's own record — `alreadyPaid`) still launches, even if the switch
  // flipped afterward — no checkout is needed either way; over-budget takes
  // precedence (its own off-ramp); a Service-routed record keeps its "write
  // us" path. Unreachable while pilot_free covers runs — a real state once
  // pricing is on and the switch is off, same defense-in-depth as over-budget.
  const checkoutClosed =
    prescan.kind === "ready" &&
    !prescanOverBudget &&
    !pilotFreeCovered &&
    !alreadyPaid &&
    prescan.estimate.service_route !== true &&
    prescan.estimate.self_serve_open !== true;

  const startPreview = async () => {
    setErrorMsg("");
    // A "payment received, drop the file again" note has done its job once
    // the file is submitted; the catch below re-sets it if the server is gone.
    setPayNotice("");
    cancelRequestedRef.current = false;
    previewStatusRetryExhaustedRef.current = null;
    if (!corpusFile) return;
    try {
      JSON.parse(metaJson);
    } catch (e) {
      setErrorMsg(
        `meta.json invalid: ${e instanceof Error ? e.message : String(e)}`,
      );
      return;
    }
    if (submitting) return;
    setSubmitting("preview");
    // Re-read live server state, fire-and-forget, to refresh the email/retention
    // flags + TTL that drive the hand-off's retention note (it updates a beat
    // later if the probe is slow — harmless). This does NOT gate which run
    // surface paints: selectRunStepScreen() + the `fanoutBatched` default
    // (false — sync live view) already pick the first-paint surface, so the
    // probe is not on the submit path and must not delay it.
    void checkHealth(server)
      .then((h) => {
        setFanoutBatched(h.fanout_batched);
        setRetentionDays(h.result_retention_days);
        setReadyEmailEnabled(h.ready_email_enabled);
      })
      .catch(() => {});
    if (accessCode) {
      void lookupLinkedEmail(server, accessCode)
        .then((res) => setLinkedEmail(res.email ?? null))
        .catch(() => {});
    }
    try {
      const r = await postPreview({
        server,
        clientId,
        accessCode,
        corpusFile,
        metaJson,
        adapter,
      });
      setPhase({
        kind: "preview-running",
        previewId: r.preview_run_id,
        previewToken: r.preview_token,
        events: [],
      });
      // Submit-time passphrase, available from the FIRST submit — the
      // hand-off shows the save-this-now card immediately instead of
      // making the user sit through discovery to see it. The server
      // carries this same key to the full run at promotion. In-memory
      // only (never localStorage).
      setRunPassphrase(r.passphrase ?? null);
      setRunStartedAt(r.started_at ?? null);
      saveActiveRun({
        kind: "preview",
        runId: r.preview_run_id,
        runToken: r.preview_token,
        startedAt: r.started_at ?? new Date().toISOString(),
        accessCode,
      });
      // Launch confirmed — the corpus is on the server now, so drop the
      // Checkout stash. No-op for pilot-free runs (nothing was stashed).
      void clearPendingRun();
      attachPreviewStream(r.preview_run_id, r.preview_token);
    } catch (e) {
      // The cap refusal is a TERMINAL state for this code, not an error
      // to retry — the cost step swaps in the no-fresh-starts panel
      // (live, 2026-06-11: an enabled Submit + error strip was a dead end).
      if ((e as ApiError).code === "preview_limit_for_code") {
        setCapBlocked(true);
        setCurrentStep(2);
        return;
      }
      // The server's hard agreement gate — MIRRORED from the /runs/start
      // catch below. Preview carries the same gate (previews.py, 2026-07-07
      // review ③), but only the start path mapped the 403 back to the
      // agreement screen; here it fell through to the generic toast as raw
      // "agreement_not_accepted" (staging 4242 drill, 2026-07-22 — a
      // returning welcomed browser minting a NEW self-serve code is exactly
      // the browser that lands here).
      if (isAgreementGateError(e)) {
        try {
          localStorage.removeItem("aurora.cloud.welcomed");
        } catch {
          /* localStorage blocked — reload still re-gates on the server */
        }
        window.location.reload();
        return;
      }
      const friendly = friendlyLaunchLimitMessage(e as ApiError);
      const unreachable = !friendly && isServerUnreachable(e);
      setErrorMsg(
        friendly ??
          (unreachable
            ? SERVER_UNREACHABLE_MESSAGE
            : e instanceof Error
              ? e.message
              : String(e)),
      );
      // Just paid at Stripe and the server vanished before the run could
      // start (the VPS host was power-cycled for 14 minutes, 2026-10-17): the
      // red strip says what failed; this calm note says the money is safe
      // and the code is coming by email. See lib/server-unreachable.
      if (unreachable && paidThisVisit) setPayNotice(PAID_UNREACHABLE_NOTICE);
      // The wizard already advanced to the run step before this submit
      // resolved; without a run, that step paints an empty handoff shell
      // (stale chips, no way forward) with the refusal as a footnote —
      // return to Review the cost, where the error reads in context.
      setCurrentStep(2);
      return;
    } finally {
      setSubmitting(null);
    }
  };

  // Billing path (run NOT pilot-free): stash the run inputs, mint a Stripe
  // Checkout session, and redirect to it. On return (`?paid=1`) the boot effect
  // rehydrates the stash and launches the run — the code is paid by then, so
  // the server's entitlement gate passes. Survivors on free/minted codes never
  // reach here (pilotFreeCovered short-circuits to startPreview).
  const goToCheckout = async () => {
    if (!corpusFile) return;
    try {
      JSON.parse(metaJson);
    } catch (e) {
      setErrorMsg(
        `meta.json invalid: ${e instanceof Error ? e.message : String(e)}`,
      );
      return;
    }
    if (submitting) return;
    // PWYC: the charge is the tier the user chose on the cost screen, not a
    // size classification. Access requires the hardship attestation (the only
    // gate); continueDisabled blocks un-attested Access before we get here.
    const tierName = prescan.kind === "ready" ? pwycChoice : "";
    if (!tierName) return;
    setPayNotice("");
    setErrorMsg("");
    setSubmitting("preview");
    try {
      const session = await createCheckoutSession({
        server,
        accessCode,
        tierName,
        // Embedded (Option A) whenever the rail is available; the server's
        // fake no-key path answers hosted-shaped regardless, so both shapes
        // are handled below.
        embedded: stripePublishableKey !== null,
      });
      if ("alreadyPaid" in session) {
        // Idempotency: the code is already paid (a prior visit / failed run that
        // didn't burn it). Don't charge again — launch the run directly with the
        // still-in-memory file. paidThisVisit so a later retry also skips
        // checkout; startPreview manages `submitting` and clears the stash.
        void clearPendingRun();
        setPaidThisVisit(true);
        setCurrentStep(STEP_RUN);
        void startPreview();
        return;
      }
      if ("clientSecret" in session) {
        // In-card payment: advance to the Payment step and mount Stripe
        // there. No navigation, no reload — the file survives in memory, so
        // the stash is NOT written on this rail (seam audit 2026-07-22: the
        // return machinery that would consume it only runs on ?paid=1 boots
        // the embedded flow never produces, so a stash here was write-only —
        // case bytes parked in IndexedDB as pure exposure on a possibly-
        // shared device). Any stale stash from an earlier hosted attempt is
        // cleared for the same reason. Crash recovery on this rail is the
        // webhook + safety-net email + paid-code re-entry.
        void clearPendingRun();
        setEmbeddedClientSecret(session.clientSecret);
        setSubmitting(null);
        setCurrentStep(STEP_PAY);
        return;
      }
      // Hosted redirect — the full-page round-trip destroys the in-memory
      // file, so stash the run inputs NOW, immediately before navigating.
      // notifyEmail rides along (launch-audit fix, 2026-07-04): a self-serve
      // code's user-typed override has no server-linked fallback and would
      // otherwise vanish across the reload. continueDisabled already
      // required emailConfirmed, so this is always the CONFIRMED address.
      await savePendingRun({
        file: corpusFile,
        metaJson,
        adapter,
        accessCode,
        tierName,
        notifyEmail,
      });
      // Navigating away — deliberately leave `submitting` set so the button
      // can't re-fire during the redirect handoff.
      window.location.href = session.url;
    } catch (e) {
      // Stripe off server-side (404) or any mint failure: don't redirect.
      // Drop the stash and surface a calm, actionable message.
      await clearPendingRun();
      // Checkout now enforces the agreement gate server-side (seam audit
      // 2026-07-22: money must not move for a code that can't run) — map
      // its 403 to the same clickwrap re-gate the run routes use.
      if (isAgreementGateError(e)) {
        try {
          localStorage.removeItem("aurora.cloud.welcomed");
        } catch {
          /* localStorage blocked — reload still re-gates on the server */
        }
        window.location.reload();
        return;
      }
      const apiErr = e as ApiError;
      setErrorMsg(
        apiErr.status === 404
          ? "Card payment isn't available right now. Email billing@plaintosee.cc and we'll sort it out."
          : "We couldn't start checkout. Please try again.",
      );
      setSubmitting(null);
    }
  };

  // Stripe's in-card onComplete → confirm entitlement server-side, then
  // launch in place. onComplete fires a beat before our webhook lands; the
  // poll absorbs that race, and the server's reconcile fallback makes it
  // converge even on a lost webhook. Deliberately NOT cancelled by
  // navigation: once onComplete has fired the payment is done, so a poll
  // that succeeds while the user wandered Back still launches the run they
  // paid for — that's the right outcome, not a race bug.
  const handleEmbeddedPaid = async () => {
    setErrorMsg("");
    // Freeze navigation while the poll runs (seam audit 2026-07-22): between
    // Stripe's onComplete and our confirmation, paidThisVisit is still false
    // — Back used to land a JUST-CHARGED card on a live "$349 →" CTA.
    setEntitlementPolling(true);
    try {
      for (let attempt = 0; attempt < 10; attempt++) {
        try {
          const r = await getEntitlement({ server, accessCode });
          if (r.paid) {
            // Bank the paid tier for the viability gate (review R1): if the
            // user later Backs out and swaps in a larger file, the re-prescan
            // may still predate the webhook — this poll response is the first
            // server word on WHICH tier the payment bought.
            if (typeof r.paid_tier === "string") setEntitledPaidTier(r.paid_tier);
            // Telemetry beacon (seam audit 2026-07-22): the embedded rail
            // has no ?paid=1 return trip, so without this the DEFAULT rail
            // left no checkout_return rows — the exact inference-from-
            // silence blind spot the beacon was built to close.
            void postCheckoutReturn({
              server,
              kind: "embedded-paid",
              accessCode: accessCode || null,
            });
            setPaidThisVisit(true);
            setCurrentStep(STEP_RUN);
            void startPreview();
            return;
          }
        } catch {
          // transient — keep polling
        }
        await new Promise((res) => setTimeout(res, 1000));
      }
      // Our record hasn't landed after ~10s, but onComplete IS Stripe's
      // client-side word that the payment finished — so flip the paid posture
      // anyway. paidThisVisit turns the cost screen into its paid band
      // ("Submit for analysis →", no price button): without it, a just-paid
      // survivor landed on a "$349 →" CTA with a toast telling them to click
      // it (review finding, 2026-07-22). Enforcement stays server-side — the
      // run-start entitlement gate re-checks, and the checkout idempotency +
      // reconcile paths keep every retry charge-free.
      void postCheckoutReturn({
        server,
        kind: "embedded-poll-timeout",
        accessCode: accessCode || null,
      });
      setPaidThisVisit(true);
      setErrorMsg(
        "Your payment went through, but our confirmation is taking a moment. Press Submit for analysis — you won't be charged twice.",
      );
      setCurrentStep(STEP_COST);
    } finally {
      setEntitlementPolling(false);
    }
  };

  // --- Stripe Checkout return ---------------------------------------------
  // Runs once on mount. `?paid=1` rehydrates the stashed run inputs and arms
  // the auto-launch; `?canceled=1` restores the file so the user lands back on
  // the cost screen without re-dropping. The query param is stripped
  // synchronously so a reload (or a StrictMode double-invoke) can't replay it.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.has("paid");
    const canceled = params.has("canceled");
    if (!paid && !canceled) {
      // Plain-boot janitor: a stash with no Checkout return to consume it is an
      // abandoned attempt (bailed on Stripe, or a failed paid launch + reload).
      // Purge it — stashed case content must not linger in IndexedDB on a
      // possibly-shared device, and IndexedDB can't self-expire while the app
      // is closed. FRESHNESS GUARD (seam audit 2026-07-22): "the legit
      // round-trip is always on stripe.com" is only true single-tab — a
      // SECOND same-origin tab opened while tab A sits at hosted Checkout is
      // a plain boot, and an unconditional clear here destroyed tab A's
      // in-flight stash, downgrading its paid return to "re-drop your file".
      // So a young stash (round-trip-aged) survives a plain boot; anything
      // older clears now, and loadPendingRun's own TTL bounds the rest.
      void loadPendingRun().then((p) => {
        if (p === null || Date.now() - p.savedAt > PENDING_RUN_JANITOR_MIN_AGE_MS) {
          void clearPendingRun();
        }
      });
      return;
    }
    // Strip the return param so a reload / StrictMode double-invoke can't replay.
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.hash,
    );
    void (async () => {
      // loadPendingRun returns null for a stale/empty/unreadable stash, so the
      // decision only sees a USABLE stash or null (unit-tested in
      // lib/checkout-return).
      const pending = await loadPendingRun();
      const action = decideCheckoutReturn({
        paid,
        canceled,
        pending,
        currentAccessCode: accessCode,
      });
      // Diagnostics beacon (2026-07-22): tell the server which branch this
      // return actually took. The Lisa Garg stranding could only be inferred
      // from server-side SILENCE; this makes the next one a logged fact.
      // Fire-and-forget — postCheckoutReturn never throws.
      void postCheckoutReturn({
        server,
        kind: action.kind,
        accessCode: accessCode || null,
      });
      switch (action.kind) {
        case "paid-launch":
          setCorpusFile(action.pending.file);
          setMetaJson(action.pending.metaJson);
          setAdapter(action.pending.adapter);
          setPaidThisVisit(true);
          // Restore the confirmed email across the full-page Stripe round-trip
          // (launch-audit fix, 2026-07-04 — see savePendingRun's call site in
          // goToCheckout). A code WITH a server-linked email doesn't strictly
          // need this — the lookupLinkedEmail effect + auto-confirm re-resolve
          // it fresh on this new mount — but a self-serve code with NO linked
          // address has no other copy of what the user typed, and this run
          // may yet be cancelled and retried (restartRun → back to step 2),
          // where the email block would otherwise show empty again.
          // emailConfirmed is always safe to set true here: continueDisabled
          // already required it before checkout could be reached at all.
          if (action.pending.notifyEmail) setNotifyEmail(action.pending.notifyEmail);
          setEmailConfirmed(true);
          setPayNotice("");
          setCurrentStep(STEP_RUN);
          setAutoLaunchAfterPay(true);
          return;
        case "paid-reload":
          // Stash gone (stale / cleared / different browser): can't auto-run,
          // but the paid code is still in localStorage — let them re-load.
          // Land them ON the drop step so the ask matches the screen, and say
          // the double-charge fear out loud (Ryan, first public walk-in,
          // 2026-07-09: read the old banner as "stuck, maybe paying twice").
          setPaidThisVisit(true);
          setCurrentStep(0);
          // "on its way to your email": the code is emailed by the Stripe
          // webhook handler (payment_email.send_payment_received), which
          // lands seconds after the redirect — or later, if the server is
          // down when Stripe calls and has to retry. Either way the email is
          // the copy that survives a closed tab, so name it here.
          setPayNotice(
            "Payment received — your card is done, and your access code is on its way to your email. Drop the same file again and we'll run your analysis. You won't be charged twice.",
          );
          return;
        case "canceled-restore":
          // The file is back in memory, so drop the stash and sit on the cost
          // screen for a retry or a different file. Same email restore as
          // paid-launch above — a canceled-at-Stripe round-trip is the exact
          // same full-page-reload hazard for a self-serve code's address.
          setCorpusFile(action.pending.file);
          setMetaJson(action.pending.metaJson);
          setAdapter(action.pending.adapter);
          if (action.pending.notifyEmail) setNotifyEmail(action.pending.notifyEmail);
          setEmailConfirmed(true);
          await clearPendingRun();
          setPayNotice(
            "Payment canceled — you can try again, or pick a different file.",
          );
          setCurrentStep(2);
          return;
        case "stash-mismatch":
          // The stash belongs to a DIFFERENT access code than the one now in
          // localStorage (concurrent-tab checkout, or a revisited/bookmarked
          // ?paid=1 URL after the stored code changed) — never restore or
          // auto-launch someone else's corpus under this code. Discard so
          // the stashed corpus is unrecoverable; the current code's session
          // proceeds normally (no chrome to invent — nothing else to do
          // here, the app just continues its normal entry state).
          await clearPendingRun();
          return;
        case "canceled-none":
        case "janitor":
          return;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-shot launch after a `?paid=1` return, once the stashed file has landed
  // in state (startPreview reads corpusFile/metaJson from state, which the boot
  // effect sets asynchronously a render later).
  useEffect(() => {
    if (!autoLaunchAfterPay || !corpusFile || !metaJson) return;
    setAutoLaunchAfterPay(false);
    void startPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLaunchAfterPay, corpusFile, metaJson]);

  const attachPreviewStream = (previewId: string, previewToken: string) => {
    tearDownSse();
    sseCleanupRef.current = subscribeEvents({
      server,
      runId: previewId,
      runToken: previewToken,
      onEvent: (ev) =>
        setPhase((p) => {
          if (p.kind !== "preview-running") return p;
          return { ...p, events: [...p.events, ev] };
        }),
      onTerminal: async (data) => {
        clearActiveRun();
        if (data.chain_state === "preview_ready") {
          // Bounded retry (3 attempts spread over ~10s) before giving up on
          // THIS pass. Hardening A (launch-audit, 2026-07-04) originally
          // folded to preview-failed on the FIRST fetchStatus rejection —
          // but a single transient blip right after a genuine preview_ready
          // doesn't deserve an instant fold: that fold also kills the
          // pre-existing 150s dead-stream poll below (its effect only runs
          // while phase.kind is *-running), turning a blip that would have
          // self-healed in seconds into a whole new preview start (finding
          // 3b, launch-audit review 2026-07-04).
          const STATUS_RETRY_DELAYS_MS = [0, 3000, 7000];
          let status: StatusResponse | null = null;
          let lastError: unknown = null;
          for (const delay of STATUS_RETRY_DELAYS_MS) {
            if (delay > 0) {
              await new Promise((resolve) => setTimeout(resolve, delay));
            }
            try {
              status = await fetchStatus({ server, runId: previewId, runToken: previewToken });
              lastError = null;
              break;
            } catch (e) {
              lastError = e;
            }
          }
          if (status) {
            // A retry recovered (or the very first attempt worked, the
            // common case) — clear any exhaustion this previewId had racked
            // up from an earlier pass and proceed normally. ALSO clear the
            // stale status-fetch errorMsg a failed earlier pass may have
            // left (seam audit 2026-07-22): the auto-promote effect refuses
            // while errorMsg is set, so a recovered-but-uncleared error
            // stalled a PAID run at preview-ready forever, under "Running…"
            // visuals.
            previewStatusRetryExhaustedRef.current = null;
            setErrorMsg("");
            const patterns = status.preview_summary?.patterns ?? [];
            setPhase((p) => {
              if (p.kind !== "preview-running") return p;
              setPreviewSnapshot({ previewId, patterns, events: p.events });
              return {
                kind: "preview-ready",
                previewId,
                previewToken,
                events: p.events,
                patterns,
              };
            });
            return;
          }
          setErrorMsg(
            `failed to fetch preview summary: ${lastError instanceof Error ? lastError.message : lastError}`,
          );
          // Restore the pre-today self-heal semantics: leave phase at
          // preview-running (this catch used to ONLY setErrorMsg and never
          // fold at all). Remember that THIS previewId burned its immediate
          // retry so the dead-stream poll below — the pre-existing self-heal
          // — knows, if its own next status check ALSO fails, that both
          // lines of defense are spent and it's time for the final-fallback
          // fold (hardening A stays, just demoted from first response to
          // last resort). If the poll's own check instead SUCCEEDS, it
          // reattaches the stream as always, which replays this terminal
          // event through this SAME handler for a completely fresh retry
          // window (the ref is cleared above on any success).
          previewStatusRetryExhaustedRef.current = previewId;
        } else {
          setPhase((p) =>
            p.kind === "preview-running"
              ? {
                  kind: "preview-failed",
                  previewId,
                  events: p.events,
                  reason: data.chain_state,
                  cancelled:
                    cancelRequestedRef.current || data.exit_code === 130,
                }
              : p,
          );
        }
      },
    });
  };

  const startConvert = async () => {
    if (phase.kind !== "preview-ready" || !corpusFile) return;
    if (submitting) return;
    setSubmitting("convert");
    setErrorMsg("");
    // Finding 3a (launch-audit review, 2026-07-04): fresh per-invocation —
    // a stale true from a previous cycle must never suppress THIS run's
    // success handling (a new preview-ready always starts clean).
    localStopBeforeStartRef.current = false;
    const abortController = new AbortController();
    convertAbortRef.current = abortController;
    try {
      const r = await postConvert({
        server,
        clientId,
        accessCode,
        corpusFile,
        metaJson,
        adapter,
        previewRunId: phase.previewId,
        previewToken: phase.previewToken,
        signal: abortController.signal,
      });
      if (localStopBeforeStartRef.current) {
        // The user hit local-stop (Cancel while preview-ready — triggerCancel
        // below, "nothing live to tell the server about" at that instant)
        // WHILE this POST was still in flight, and the panel already folded
        // to the stopped view. The POST nonetheless landed and minted a
        // real, billable run server-side (the slow-but-successful race).
        // Do NOT resurrect the running UI over the stopped panel — the
        // guarded setPhase calls below would no-op anyway now that phase
        // isn't "preview-ready" anymore, but skip them outright and instead
        // kill the just-accepted run so it doesn't keep burning after the
        // user was told it stopped. Best-effort: nothing more to do here
        // either way, and the server-side wall-clock cap is the backstop.
        void cancelRun({
          server,
          runId: r.run_id,
          runToken: r.run_token,
        }).catch(() => {
          /* best-effort — see comment above */
        });
        return;
      }
      // Promotion echoes the SAME passphrase minted at preview start (and
      // already on screen). Never let a missing echo wipe the displayed
      // key — keep whatever this session already holds.
      setRunPassphrase((prev) => r.passphrase ?? prev);
      setRunStartedAt((prev) => r.started_at ?? prev);
      saveActiveRun({
        kind: "convert",
        runId: r.run_id,
        runToken: r.run_token,
        startedAt: r.started_at ?? new Date().toISOString(),
        accessCode,
      });
      // Email confirm-for-everyone (front-door spec, 2026-07-04): a
      // confirmed address that overrides (or supplies, for a self-serve
      // code with none) the linked email lives only in notifyEmail until
      // now — this is the FIRST moment a run_id/run_token exist to attach it
      // server-side (the notify-email endpoint requires an active run).
      // Best-effort: a failed push here must never block the run itself —
      // the in-wizard "Change" affordance (ChangeEmailModal) is still there
      // once convert-running to retry.
      if (notifyEmail) {
        // Best-effort but NOT silent: a transient failure loses the only address
        // a self-serve buyer has on file (they paid → would get no ready email),
        // so retry with backoff and warn on final failure (2026-07-07 review, ⑨).
        // ChangeEmailModal stays the manual fallback.
        void attachNotifyEmailWithRetry({
          server,
          runId: r.run_id,
          runToken: r.run_token,
          email: notifyEmail,
        });
      }
      // Guarded/functional (finding 3a, launch-audit review 2026-07-04):
      // never transition a terminal cancelled/failed phase back to running.
      // Belt-and-suspenders alongside the localStopBeforeStartRef check
      // above — if phase moved on to anything other than "preview-ready"
      // while this POST was in flight (a local stop, or any other race),
      // this is a no-op instead of clobbering whatever the user is looking
      // at now.
      if (r.state === "queued") {
        // All full-run slots were busy → accepted into the queue. Show the
        // hand-off and poll for promotion (no SSE stream until it actually runs).
        setPhase((p) =>
          p.kind === "preview-ready"
            ? {
                kind: "convert-queued",
                runId: r.run_id,
                runToken: r.run_token,
                position: r.queue_position ?? null,
              }
            : p,
        );
        startQueuePoll(r.run_id, r.run_token);
      } else {
        setPhase((p) =>
          p.kind === "preview-ready"
            ? {
                kind: "convert-running",
                runId: r.run_id,
                runToken: r.run_token,
                events: [],
              }
            : p,
        );
        attachConvertStream(r.run_id, r.run_token);
      }
    } catch (e) {
      if (localStopBeforeStartRef.current) {
        // The abort above (or a genuine late failure racing the same local
        // stop) landed here instead of the try's success branch — either
        // way the user already saw the stopped panel and there is no run to
        // fold onto a NEW failure over it. Nothing left to do.
        return;
      }
      // The server's hard agreement gate (runs.py). Reachable only if the
      // welcomed flag is set but no current-version acceptance is on file
      // (cleared store, or the agreement version bumped mid-session). Send
      // the user back through the agreement rather than a generic run error:
      // clear welcomed and reload, so Shell re-renders the agreement screen.
      if (isAgreementGateError(e)) {
        try {
          localStorage.removeItem("aurora.cloud.welcomed");
        } catch {
          /* localStorage blocked — reload still re-gates on the server */
        }
        window.location.reload();
        return;
      }
      const friendly = friendlyLaunchLimitMessage(e as ApiError);
      const unreachable = !friendly && isServerUnreachable(e);
      setErrorMsg(
        friendly ??
          (unreachable
            ? SERVER_UNREACHABLE_MESSAGE
            : e instanceof Error
              ? e.message
              : String(e)),
      );
      // Same just-paid reassurance as the preview catch above — the server
      // can vanish between the preview and the promotion too.
      if (unreachable && paidThisVisit) setPayNotice(PAID_UNREACHABLE_NOTICE);
      // No live server run exists after this — postConvert threw (sync or
      // rejected) or came back non-2xx, so /runs/start either never reached
      // the server or was refused. Without this, `phase` stays
      // "preview-ready", which deriveMilestones/deriveRunView already paint
      // as mode "running" (the "brief running flash" they use to bridge the
      // moment between a ready preview and the promotion actually landing —
      // see RunState.tsx's deriveRunView, case "preview-ready"). That flash
      // is only ever meant to last a beat; with no phase transition on
      // failure it becomes PERMANENT — live timer and patterns badge stuck
      // forever, no error surfaced on the run screen (errorMsg isn't
      // rendered there), no retry (the failed panel needs phase.kind to
      // actually be *-failed), and Cancel a silent no-op (triggerCancel only
      // recognizes preview-running/convert-running as live runs). This is
      // the exact failure Rich hit live on staging after a relaunch: no
      // POST /runs/start in server logs, UI stuck on the convert visuals,
      // Cancel doing nothing (launch-audit hardening A, 2026-07-04). Fold to
      // the SAME convert-failed panel a real in-flight cancel/failure lands
      // on, with the existing "Try again" affordance.
      setPhase((p) =>
        p.kind === "preview-ready"
          ? {
              kind: "convert-failed",
              runId: p.previewId,
              events: p.events,
              reason: "start_request_failed",
              cancelled: false,
            }
          : p,
      );
    } finally {
      setSubmitting(null);
    }
  };

  // Auto-promote preview → full. The product UX is one continuous "Run
  // the analysis" step; the preview/convert split is implementation,
  // not user-facing. If preview surfaces ≥1 pattern, we automatically
  // start convert without waiting for a second click. Failures leave
  // the manual Retry button as the escape hatch.
  useEffect(() => {
    if (
      phase.kind === "preview-ready" &&
      phase.patterns.length > 0 &&
      submitting === null &&
      !errorMsg
    ) {
      void startConvert();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind]);

  const attachConvertStream = (runId: string, runToken: string) => {
    tearDownSse();
    sseCleanupRef.current = subscribeEvents({
      server,
      runId,
      runToken,
      onEvent: (ev) =>
        setPhase((p) => {
          if (p.kind !== "convert-running") return p;
          return { ...p, events: [...p.events, ev] };
        }),
      onTerminal: (data) => {
        if (data.chain_state === "run_complete") {
          // Keep the active-run pointer alive (the run's 24h life) so a refresh,
          // a browser-discarded/reloaded tab, or a return visit RESUMES to the
          // completed run — instead of dropping to the setup screen with an
          // already-consumed access code and no way back. Cleared only on an
          // explicit "Done" (handleClose) or a 404/410 purge (resume probe).
          // Mirrors the resume probe's run_complete branch, which was being
          // defeated by the old unconditional clear here.
          setPhase((p) =>
            p.kind === "convert-running"
              ? { kind: "run-complete", runId, runToken, events: p.events }
              : p,
          );
        } else {
          clearActiveRun();
          setPhase((p) =>
            p.kind === "convert-running"
              ? {
                  kind: "convert-failed",
                  runId,
                  events: p.events,
                  reason: data.chain_state,
                  cancelled:
                    cancelRequestedRef.current || data.exit_code === 130,
                }
              : p,
          );
        }
      },
    });
  };

  // Watch a queued run for promotion. A queued run has no SSE stream yet (no
  // subprocess), so we poll /status: while it stays "queued" we just refresh the
  // position; the moment it flips to running we hand off to the SSE stream (which
  // replays from event 0, so nothing is missed). Terminal/gone states are handled
  // too (a fast promote-then-finish, or a restart-wiped run the scheduler gave up
  // on). The interval is torn down by tearDownSse on every transition + unmount.
  const startQueuePoll = (runId: string, runToken: string) => {
    tearDownSse();
    const stopPoll = () => {
      if (queuePollRef.current !== null) {
        window.clearInterval(queuePollRef.current);
        queuePollRef.current = null;
      }
    };
    const tick = async () => {
      try {
        const status = await fetchStatus({ server, runId, runToken });
        if (status.pending_passphrase) {
          setRunPassphrase((prev) => status.pending_passphrase ?? prev);
        }
        const st = status.chain_state;
        if (st === "queued") {
          setPhase((p) =>
            p.kind === "convert-queued"
              ? { ...p, position: status.queue_position ?? null }
              : p,
          );
          return;
        }
        // Left the queue — stop polling and react to the new state.
        stopPoll();
        if (st === "running" || st === "initializing") {
          setRunStartedAt(status.started_at ?? null);
          setPhase((p) =>
            p.kind === "convert-queued"
              ? { kind: "convert-running", runId, runToken, events: [] }
              : p,
          );
          attachConvertStream(runId, runToken);
        } else if (st === "run_complete") {
          setPhase((p) =>
            p.kind === "convert-queued"
              ? { kind: "run-complete", runId, runToken, events: [] }
              : p,
          );
        } else if (st === "run_failed" || st === "preview_failed") {
          clearActiveRun();
          setPhase((p) =>
            p.kind === "convert-queued"
              ? {
                  kind: "convert-failed",
                  runId,
                  events: [],
                  reason: st,
                  cancelled: status.exit_code === 130,
                }
              : p,
          );
        }
      } catch (e) {
        const httpStatus = (e as { status?: number }).status;
        if (httpStatus === 404 || httpStatus === 410) {
          // The run was purged (e.g. a restart wiped it before promotion). Stop,
          // drop the pointer, and send the user back — the code stays valid.
          stopPoll();
          clearActiveRun();
          setPhase({ kind: "setup" });
          setErrorMsg(
            "That analysis is no longer available — your code stays valid, please start again.",
          );
        }
        // Transient network errors: keep polling; the next tick retries.
      }
    };
    void tick(); // immediate first check, then every 5s
    queuePollRef.current = window.setInterval(() => void tick(), 5000);
  };

  // After a beat-1 cancel OR a run failure: everything the user entered is still
  // lifted in App state, so the restart is "back to Review the cost", one click
  // from re-submitting — not a re-walk of the wizard. On a failure the access
  // code is already re-armed server-side (lifecycle._rearm_access_code), so the
  // same path applies. (If the code's two preview starts are spent, the submit
  // comes back with the server's preview_limit_for_code message in plain words.)
  //
  // Factored out of restartRun (cancel-path fix, 2026-07-04) so
  // handleContinue's step-2 branch can run the exact same reset when the
  // user reaches a relaunch a DIFFERENT way — clicking &larr; Back off a
  // stopped/failed run, then Submit, rather than the stopped panel's own
  // "Set up the run again" button. Same state, same fresh-run guarantees;
  // only the navigation differs (this doesn't touch currentStep).
  const resetPhaseForRestart = () => {
    cancelRequestedRef.current = false;
    localStopBeforeStartRef.current = false;
    setRestartedRun(true);
    setErrorMsg("");
    setConfirmLaunch(false);
    // The canceled run's passphrase and clock die with it — a fresh
    // submit mints fresh ones (each run has its own key). Clearing here
    // keeps a failed re-submit from painting the old chips.
    setRunPassphrase(null);
    setRunStartedAt(null);
    setPhase({ kind: "setup" });
  };

  const restartRun = () => {
    resetPhaseForRestart();
    setCurrentStep(2);
  };

  const triggerCancel = async () => {
    // Remember that the stop was the user's own doing — the server records
    // a cancel as *_failed (exit 130), and this flag is what keeps that
    // from rendering as "something went wrong" (live, 2026-06-11).
    cancelRequestedRef.current = true;
    if (phase.kind === "preview-running") {
      await cancelRun({
        server,
        runId: phase.previewId,
        runToken: phase.previewToken,
      });
      return;
    }
    if (phase.kind === "convert-running") {
      await cancelRun({ server, runId: phase.runId, runToken: phase.runToken });
      return;
    }
    // No live server run to cancel — the Cancel button is reachable while
    // phase.kind === "preview-ready" too (deriveRunView paints it as mode
    // "running", the brief flash while auto-promote is expected to land any
    // moment), and that preview run already finished server-side; there is
    // nothing left to tell the server to stop. Before this fix, clicking
    // Cancel here was a silent no-op — no branch matched, so nothing
    // happened at all, not even visibly (launch-audit hardening B,
    // 2026-07-04 — this is exactly what Rich saw when startConvert's fetch
    // had already failed and left the UI stuck on the running visuals). A
    // cancel click must always visibly change something: treat it as a
    // local stop and fold straight to the same stopped panel a real
    // server-side cancel lands on.
    if (phase.kind === "preview-ready") {
      // Finding 3a (launch-audit review, 2026-07-04): auto-promote may
      // already have a REAL /runs/start POST in flight right now (the
      // effect fires the instant preview-ready has patterns, not on this
      // click) — belt-and-braces against it landing after this local stop
      // and clobbering the panel we're about to paint. Belt: abort the
      // in-flight upload if it hasn't finished. Suspenders: flag the local
      // stop so startConvert's own success/catch handlers (which may
      // already be past the point an abort can reach) know to kill the
      // just-accepted run server-side instead of resurrecting the UI.
      localStopBeforeStartRef.current = true;
      convertAbortRef.current?.abort();
      setPhase({
        kind: "preview-failed",
        previewId: phase.previewId,
        events: phase.events,
        reason: "cancelled_locally",
        cancelled: true,
      });
    }
  };

  const [viewerBuilding, setViewerBuilding] = useState(false);
  // Submit-time viewer passphrase (audit B7) — delivered once by
  // /runs/start, held in memory for this session only. The hand-off and
  // Done screens display it; after a refresh the user relies on the copy
  // they were told to save.
  const [runPassphrase, setRunPassphrase] = useState<string | null>(null);

  // When this run started (ISO, server-authoritative) — the hand-off's
  // status line. Set at submit/convert/resume; cleared with the run.
  const [runStartedAt, setRunStartedAt] = useState<string | null>(null);

  // "I've saved my passphrase" ack — lifted from RunHandoff so the unload
  // guard can see it (UX audit finding B). Reset whenever a new passphrase
  // arrives (a new run's key needs its own ack).
  const [passSaved, setPassSaved] = useState(false);
  useEffect(() => {
    // Reset to the PERSISTED ack truth, not blindly false: a resume
    // re-delivers the SAME run's pending key (status.pending_passphrase),
    // and a blind reset clobbered the restored ack one commit later — the
    // gate re-ran its full ceremony for a user who had already confirmed
    // (live, 2026-06-12). A genuinely new run always saves a fresh pointer
    // WITHOUT passAcked, so it still demands its own ack.
    setPassSaved(loadActiveRun()?.passAcked === true);
  }, [runPassphrase]);

  // Browser leave-site confirm while closing would lose something real:
  // the tab is still required (client-side promotion), or an un-acked
  // passphrase is on screen. Policy in lib/unload-guard (unit-tested);
  // removed the moment safe-to-close is genuinely true.
  useEffect(() => {
    const guard = shouldGuardUnload({
      phaseKind: phase.kind,
      hasPassphrase: !!runPassphrase,
      passSaved,
    });
    if (!guard) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [phase.kind, runPassphrase, passSaved]);

  // SSE dead-stream fallback (UX audit C build): EventSource auto-reconnects
  // through transient drops, but a hard-dead stream (laptop sleep, network
  // swap) leaves the tab frozen on "running" while the run finished. Poll
  // status every 2.5 min; if the server says the run left running while the
  // phase still thinks it's live, RE-ATTACH the stream — the replay-from-0
  // delivers the missed terminal event through the normal handlers (no
  // duplicated transition logic; duplicate events in the log are harmless,
  // milestone derivation is existence-based).
  useEffect(() => {
    if (phase.kind !== "preview-running" && phase.kind !== "convert-running") {
      return;
    }
    const isPreview = phase.kind === "preview-running";
    const runId = isPreview ? phase.previewId : phase.runId;
    const runToken = isPreview ? phase.previewToken : phase.runToken;
    const t = window.setInterval(() => {
      void (async () => {
        try {
          const st = await fetchStatus({ server, runId, runToken });
          if (
            st.chain_state === "running" ||
            st.chain_state === "initializing"
          ) {
            return;
          }
          if (isPreview) attachPreviewStream(runId, runToken);
          else attachConvertStream(runId, runToken);
        } catch {
          // Transient — try again next tick. UNLESS this exact preview
          // already burned its own bounded status-fetch retry once
          // (attachPreviewStream's onTerminal, above — finding 3b,
          // launch-audit review 2026-07-04): this poll IS that self-heal,
          // and its own check just failed too, so both lines of defense are
          // spent. Fold to the same recoverable failed panel a real preview
          // failure lands on — the final fallback, reached only after the
          // poll's own window has ALSO come up empty, never as the first
          // response to one transient blip.
          if (isPreview && previewStatusRetryExhaustedRef.current === runId) {
            previewStatusRetryExhaustedRef.current = null;
            setPhase((p) =>
              p.kind === "preview-running"
                ? {
                    kind: "preview-failed",
                    previewId: runId,
                    events: p.events,
                    reason: "status_fetch_failed",
                    cancelled: false,
                  }
                : p,
            );
          }
        }
      })();
    }, 150_000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind, server]);

  // The retention window (days) a completed result stays re-downloadable via the
  // emailed come-back link, from /healthz (single source: the janitor's
  // run_complete TTL). Retention is unconditional now — no opt-out — so this only
  // drives the reassurance copy, never a control.
  const [retentionDays, setRetentionDays] = useState(7);
  // Whether the deployed server runs batched (hours, best-effort) → show the
  // run hand-off instead of the live "keep this tab open" view.
  // Defaults FALSE since the 2026-07-01 sync cutover: sync is the product
  // again, so batch requires the server to EXPLICITLY report
  // fanout_batched=true (internal/cost-experiment boxes only). The fail-safe
  // logic tracks the product default — a silently-failed health probe (a page
  // load racing a deploy restart, prod 2026-06-09) must latch the view that
  // matches what the server is actually doing, which is sync now. (This
  // default has flipped twice; it should always equal the config default in
  // aurora_cloud/config.py — keep them in lockstep.)
  const [fanoutBatched, setFanoutBatched] = useState(false);
  // S13: whether the server will ACTUALLY send the ready email. Defaults
  // FALSE (the flag ships OFF) so the hand-off never promises an email the
  // operator hasn't enabled — the opposite default from fanoutBatched,
  // because here the failure mode of a missed probe is a false promise.
  const [readyEmailEnabled, setReadyEmailEnabled] = useState(false);
  // User-overridden notify address (changed on the run hand-off). null → use the
  // access-code address (linkedEmail). `emailModalOpen` toggles the change modal.
  const [notifyEmail, setNotifyEmail] = useState<string | null>(null);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    checkHealth(server)
      .then((h) => {
        if (cancelled) return;
        setRetentionDays(h.result_retention_days);
        setFanoutBatched(h.fanout_batched);
        setReadyEmailEnabled(h.ready_email_enabled);
      })
      .catch(() => {
        // Health probe failed → leave defaults; the wizard still works.
      });
    return () => {
      cancelled = true;
    };
  }, [server]);

  // The email we already have for this code (from the pilot application). It's
  // the address the server sends the come-back link to, so we name it in the
  // retention note rather than asking the user to retype it. null → no address
  // on file (dev/test codes) → hide the come-back note entirely.
  const [linkedEmail, setLinkedEmail] = useState<string | null>(null);
  useEffect(() => {
    if (!accessCode) return;
    let cancelled = false;
    lookupLinkedEmail(server, accessCode)
      .then((res) => {
        if (!cancelled) setLinkedEmail(res.email ?? null);
      })
      .catch(() => {
        // Non-blocking: no address shown → the come-back note stays hidden.
      });
    return () => {
      cancelled = true;
    };
  }, [server, accessCode]);
  // The address a come-back link actually goes to: a user-set notify override
  // wins, else the access-code's linked address — mirroring the server's
  // `notify_email or _lookup_email`, so the reassurance names the real inbox
  // (not the stale linked address) when the user has changed it.
  const backupEmailAddr = notifyEmail ?? linkedEmail;
  // We'll email a come-back link iff the server's come-back email is on AND we
  // have an address on file for this code. Drives the reassurance copy only.
  const backupAvailable = readyEmailEnabled && !!backupEmailAddr;

  // Email confirm-for-everyone (front-door spec, 2026-07-04; integrated into
  // the cost pane 2026-07-04 per staging feedback — see EmailConfirmBlock's
  // doc comment for why the old full-screen gate got replaced): a "Where
  // should we send your analysis?" ask for EVERY entrant — pre-filled +
  // confirmable when linkedEmail exists, required when it doesn't (self-serve
  // codes carry none). Reuses notifyEmail as the override slot: unchanged
  // from linkedEmail -> no override (the code's linked address is already
  // correct server-side); anything else -> set it.
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const handleEmailConfirmed = (value: string) => {
    const trimmed = value.trim();
    if (linkedEmail && trimmed.toLowerCase() === linkedEmail.trim().toLowerCase()) {
      setNotifyEmail(null);
    } else {
      setNotifyEmail(trimmed);
    }
    setEmailConfirmed(true);
  };
  // Prefilled case auto-confirms (Rich, 2026-07-04): a linked address is
  // already correct server-side, so the user shouldn't have to press a
  // button just to accept it — only the (uncommon) override path via the
  // block's "Change" affordance calls handleEmailConfirmed explicitly.
  // Guarded on !emailConfirmed so this never re-fires after that override
  // (or after the user's own confirm) and clobbers it back to the original.
  useEffect(() => {
    if (linkedEmail && !emailConfirmed) {
      handleEmailConfirmed(linkedEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once per linkedEmail resolution; emailConfirmed/handleEmailConfirmed are guards, not triggers
  }, [linkedEmail]);

  // Download the encrypted single-file viewer.html and surface its
  // passphrase. The server builds the bundle on demand by running the
  // four-step pipeline (template → run-files.json → corpus.json →
  // assemble+encrypt) — ~5-10s wall on demo-charlie. The passphrase
  // returned in the X-Aurora-Passphrase header decrypts the bundle when
  // the user opens it in any browser.
  const triggerViewerDownload = async () => {
    if (phase.kind !== "run-complete") return;
    if (viewerBuilding) return;
    setErrorMsg("");
    setViewerBuilding(true);
    try {
      // Pull a friendly label from the meta.json the user supplied (or
      // the case id slug as a fallback) so the viewer chrome shows
      // something meaningful instead of the cloud_run_id.
      let label: string | undefined;
      try {
        const meta = JSON.parse(metaJson);
        label = meta.id ?? undefined;
      } catch {
        // Bad meta.json — server will fall back to corpus_slug. Fine.
      }
      const { blob, passphrase, consumed } = await downloadViewerBundle({
        server,
        runId: phase.runId,
        runToken: phase.runToken,
        label,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // The bundle is a zip (viewer + readme); a stable, friendly name reads
      // as a deliverable. Browsers de-dupe repeat downloads as "…(1).zip".
      a.download = "aurora-analysis.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setPhase((p) =>
        p.kind === "run-complete"
          ? {
              ...p,
              viewer: resolveViewerAfterDownload(p.viewer, {
                sizeBytes: blob.size,
                // New-flow downloads are keyless (the key was delivered at
                // submit); if this session still holds it, keep showing it
                // on the Done screen as a courtesy repeat.
                passphrase: passphrase || runPassphrase || "",
                consumed,
              }),
            }
          : p,
      );
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setViewerBuilding(false);
    }
  };

  // --- Wizard navigation ---

  // NOTE: Run (index 3) → Download (index 4) is NOT auto-advanced. When the
  // run completes the user stays on the read-the-messages surface to review
  // the surfaced patterns, and clicks Continue to reach Download on their own
  // (handleContinue's step-4 branch). Auto-flipping skipped past the
  // result before the user could see it.

  // Cost-estimate lifecycle. ONE self-contained effect, driven by file IDENTITY:
  // the size check (re)fires whenever corpusFile differs from the file the last
  // fetch was launched for (prescanFetchedForRef), resets to idle when the corpus
  // is cleared, and otherwise skips. This replaces a fragile two-effect split
  // (one reset prescan→idle on file change, the other fired on idle) whose
  // same-flush ordering read a STALE prescan.kind and could strand the wizard on
  // "Checking the size…" after a file replacement — e.g. load A, advance, go back,
  // load B: the fetch effect saw A's "ready" in B's render and bailed.
  //
  // Firing on ingest (not just the cost screen) lets the over-budget off-ramp
  // appear at the Source step — before "Confirm the parties" spends a real Haiku
  // classify call on a record we'll never run.
  //
  // prescan.kind is intentionally NOT a dep: the effect mutates it, and depending
  // on it would fire the cleanup (cancelled=true) on that mutation and swallow the
  // in-flight result (the old "hung on Sizing up… forever" bug). Re-fires come from
  // corpusFile (identity), server, accessCode, or prescanNonce (retry). The
  // fetch/skip/reset decision is the pure prescanAction (unit-tested).
  useEffect(() => {
    const action = prescanAction({
      corpusFile,
      fetchedFor: prescanFetchedForRef.current,
    });
    if (action === "reset") {
      prescanFetchedForRef.current = null;
      setPrescan((p) => (p.kind === "idle" ? p : { kind: "idle" }));
      return;
    }
    if (action === "skip") return;
    // action === "fetch": a corpus we haven't sized yet, or a forced retry.
    const fileForThisFetch = corpusFile as File;
    prescanFetchedForRef.current = fileForThisFetch;
    let cancelled = false;
    setPrescan({ kind: "loading" });
    // Time-box the size check. It gates advancement at the Source step, so a hung
    // /prescan (slow server, dropped connection — a pending fetch never rejects on
    // its own) would otherwise strand the wizard on "Checking the size…" with no
    // way forward. On timeout we abort and fail open to `error`, which lets the
    // user retry; the chain's own pre-flight cost gate is the real ceiling.
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20_000);
    postPrescan({
      server,
      corpusFile: fileForThisFetch,
      accessCode,
      // Only ever set for THIS file (WeakMap keyed by it), so a CSV that
      // replaced a PDF cannot inherit the PDF's page count.
      sourcePageCount: pdfPageCounts.get(fileForThisFetch),
      signal: controller.signal,
    })
      .then((estimate) => {
        if (cancelled) return;
        // Pin this ready result to the file it was computed for, so the
        // classify gate can reject a stale ready after a file replacement.
        prescanReadyForRef.current = fileForThisFetch;
        setPrescan({ kind: "ready", estimate });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const friendly =
          e instanceof DOMException && e.name === "AbortError"
            ? undefined
            : friendlyLaunchLimitMessage(e as ApiError);
        const message =
          friendly ??
          (e instanceof DOMException && e.name === "AbortError"
            ? "size check timed out"
            : e instanceof Error
              ? e.message
              : String(e));
        setPrescan({ kind: "error", message });
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prescan.kind intentionally excluded; see comment above
  }, [corpusFile, server, accessCode, prescanNonce]);

  // Retry after an error: clear the fetched-for ref so prescanAction re-fetches
  // the SAME file, then bump the nonce to re-run the effect (a state change is
  // what re-runs it — corpusFile is unchanged on retry).
  const retryPrescan = () => {
    prescanFetchedForRef.current = null;
    setPrescanNonce((n) => n + 1);
  };

  // Drop run state when the corpus file changes, so nothing leaks across a file
  // switch (stale preview patterns, a completed prior run, a half-open cancel
  // confirm). Defensive against the corpus↔stale-state clobbering class.
  // Ref-guarded so it only fires on a real identity change. NOTE: classify is
  // deliberately NOT reset here — the classify effect (declared earlier, so it
  // runs first on a file change) already re-establishes it for the new file;
  // resetting it here would run second and clobber that fresh result back to
  // idle (the same effect-ordering trap as the old prescan bug). prescan has its
  // own file-identity effect too.
  const runStateForFileRef = useRef<File | null>(null);
  useEffect(() => {
    if (runStateForFileRef.current === corpusFile) return;
    runStateForFileRef.current = corpusFile;
    setPreviewSnapshot(null);
    setConfirmCancel(false);
    setPhase((p) => (p.kind === "setup" ? p : { kind: "setup" }));
  }, [corpusFile]);

  // Slide animation state. When currentStep changes, briefly mount both
  // the outgoing and incoming panes, then run a CSS transition to slide
  // them. Two rAFs ensure the browser paints the "armed" (off-screen)
  // state before flipping to "running" (on-screen) so the transition
  // fires; one rAF isn't enough in React's batching model.
  const [anim, setAnim] = useState<{
    outgoing: WizardStep | null;
    direction: "forward" | "back";
    animPhase: "idle" | "armed" | "running";
  }>({ outgoing: null, direction: "forward", animPhase: "idle" });
  const prevStepRef = useRef<WizardStep>(currentStep);

  // useLayoutEffect (NOT useEffect) so the armed state is committed
  // BEFORE the browser paints. With useEffect, the first paint after
  // currentStep changes shows the new pane at translateX(0) (active)
  // for one frame before the armed state pushes it to translateX(100%)
  // — that one-frame flash reads as "new pane appears at center, then
  // both panes are visible, then old pane slides out." useLayoutEffect
  // synchronously runs the armed setAnim before paint, so the user
  // never sees the new pane at active until the slide animation
  // delivers it there.
  useLayoutEffect(() => {
    if (prevStepRef.current === currentStep) return;
    const direction: "forward" | "back" =
      currentStep > prevStepRef.current ? "forward" : "back";
    const outgoing = prevStepRef.current;
    prevStepRef.current = currentStep;
    setAnim({ outgoing, direction, animPhase: "armed" });
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setAnim((a) =>
          a.outgoing === outgoing && a.animPhase === "armed"
            ? { ...a, animPhase: "running" }
            : a,
        );
      });
    });
    const settle = window.setTimeout(() => {
      setAnim({ outgoing: null, direction, animPhase: "idle" });
    }, 700);
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.clearTimeout(settle);
    };
  }, [currentStep]);

  const paneClass = (step: WizardStep): string => {
    if (step === currentStep) {
      if (anim.animPhase === "armed") {
        return `work-pane ${anim.direction === "forward" ? "enter-from-right" : "enter-from-left"}`;
      }
      // During the running phase, the incoming pane gets the
      // `.incoming` modifier so CSS can apply transition-delay
      // (stagger so it arrives after the outgoing has left, not
      // crossing in the middle). The modifier is dropped at settle.
      if (anim.animPhase === "running" && anim.outgoing !== null) {
        return "work-pane active incoming";
      }
      return "work-pane active";
    }
    if (step === anim.outgoing) {
      if (anim.animPhase === "armed") return "work-pane active";
      if (anim.animPhase === "running") {
        return `work-pane ${anim.direction === "forward" ? "exit-left" : "exit-right"}`;
      }
      return "work-pane hidden";
    }
    return "work-pane hidden";
  };

  const stepDone: Record<WizardStep, boolean> = {
    // Drop — a corpus is loaded either via the PDF-ingest path (pdfIngest =
    // done) or via a direct CSV — the dev preload buttons + future
    // CSV upload — which sets corpusFile directly without going
    // through the PDF pipeline. Either path counts as done.
    // The phase clause covers RESUME (reload mid-run, deep link, restored
    // completion): the corpus is memory-only, so a fresh mount has no
    // corpusFile — but a run in any phase proves a file was dropped. Without
    // it, step 1 sat unfilled on every resumed session while 2-4 were
    // checked (persistent live annoyance, fixed 2026-06-12). Mirrors the
    // same clause on steps 1 and 2.
    0:
      pdfIngest.kind === "done" ||
      (corpusFile !== null && metaJson.trim() !== "") ||
      phase.kind !== "setup",
    // Parties — confirmed in-session, OR you've moved past it (you can't
    // reach the cost screen+ without clearing it), OR the run has started.
    // The `currentStep > 1` clause is what makes the check appear the
    // moment you advance to the cost screen — relying on
    // `classify.confirmed` alone left it dark until the run started. The
    // phase clause still covers resume, where a fresh mount restores
    // `phase` but leaves `classify` at "idle".
    1:
      classify.kind === "confirmed" ||
      currentStep > 1 ||
      phase.kind !== "setup",
    // Cost — done once you've moved past it, OR the run has started. NOT keyed
    // on `prescan.kind === "ready"`: the size check now runs eagerly the moment
    // a file loads (to gate the billable classify on Source), so a ready
    // estimate is true while you're still on Drop — which lit this check green
    // before the user ever saw the cost screen, and out of order (ahead of
    // Parties). `currentStep > 2` mirrors the Parties (step 1) pattern.
    2: currentStep > 2 || phase.kind !== "setup",
    // Payment — done the moment this visit's charge clears, or when the code
    // arrived already paid (the rail hides the step then, but the record
    // still needs every key). A launched run also proves payment cleared.
    3: paidThisVisit || alreadyPaid || phase.kind !== "setup",
    4: phase.kind === "run-complete",
    5: false,
  };

  const runInFlight =
    phase.kind === "preview-running" || phase.kind === "convert-running";

  const helperText: string = (() => {
    if (currentStep === 0) {
      if (pdfIngest.kind === "done") {
        return "Looks wrong? Choose a different file.";
      }
      if (pdfIngest.kind === "running")
        return "Reading your file — extracting messages, parties, and time zone";
      if (pdfIngest.kind === "error")
        return "PDF could not be read — try a different file";
      return "Drop a file to continue";
    }
    if (currentStep === 1) {
      if (classify.kind === "running")
        return "Scanning your messages for the people involved…";
      // The editor itself is the instruction now — no footer helper text.
      if (classify.kind === "ready" || classify.kind === "confirmed") return "";
      if (classify.kind === "error")
        return "Couldn't scan for people — usually temporary; use Scan again above.";
      return "";
    }
    if (currentStep === 2) {
      if (prescan.kind === "loading") return "Sizing up your messages…";
      if (prescan.kind === "error")
        return "Couldn't get an estimate — try again.";
      if (prescan.kind === "ready") {
        if (prescan.estimate.service_route === true) {
          return "This record is larger than our self-serve sizes — write us above.";
        }
        if (pilotFreeCovered) {
          return ready
            ? "Sponsored — no payment needed."
            : `Missing: ${missing.join(", ")}`;
        }
        if (alreadyPaid) {
          return ready
            ? "Already paid — no new charge."
            : `Missing: ${missing.join(", ")}`;
        }
        return ready
          ? "Review the cost, then authorize to start."
          : `Missing: ${missing.join(", ")}`;
      }
      return "";
    }
    if (currentStep === STEP_RUN) {
      if (phase.kind === "setup") return "Usually 15–30 minutes start to finish.";
      if (phase.kind === "preview-running") return "Surfacing patterns…";
      if (phase.kind === "preview-ready")
        return "Patterns surfaced — grounding next…";
      if (phase.kind === "convert-running")
        return "Grounding patterns in verbatim quotes…";
      if (phase.kind === "preview-failed" || phase.kind === "convert-failed")
        return phase.cancelled
          ? "Run stopped."
          : "Analysis ran into a problem.";
      if (phase.kind === "run-complete")
        return "Review the patterns, then continue.";
      return "";
    }
    if (currentStep === STEP_DONE) {
      if (phase.kind === "run-complete" && phase.viewer)
        return "Save the encrypted file somewhere private.";
      if (phase.kind === "run-complete")
        return "Download your encrypted viewer below.";
      return "No analysis ready — return to step 4 to run it.";
    }
    return "";
  })();

  const nextLabel: string = (() => {
    if (currentStep === 0) return "Continue →";
    if (currentStep === 1) return "Looks right →";
    if (currentStep === 2) {
      if (prescan.kind === "loading") return "Sizing up…";
      if (prescan.kind === "error") return "Continue →";
      if (
        prescan.kind === "ready" &&
        prescan.estimate.service_route === true
      ) {
        return "Write us first";
      }
      if (pilotFreeCovered || alreadyPaid) return "Submit for analysis →";
      // PWYC: the CTA carries the CHOSEN tier + its live price ("You'll see
      // the full total before you pay" — Stripe Checkout is next).
      const chosen =
        prescan.kind === "ready"
          ? prescan.estimate.pwyc_tiers.find((t) => t.name === pwycChoice)
          : undefined;
      return chosen
        ? `Continue with ${chosen.name} — $${livePrices?.[chosen.name] ?? chosen.price_usd} →`
        : "Authorize and submit →";
    }
    if (currentStep === STEP_PAY) {
      // Stripe's own Pay button IS the action on this step; the footer
      // Continue is hidden (see the cta render) and this label is unused
      // chrome kept non-empty only for safety.
      return "Waiting on payment…";
    }
    if (currentStep === STEP_RUN) {
      if (phase.kind === "run-complete") return "Continue →";
      // Terminal-but-not-complete: the run is over; "Running…" would lie
      // (stuck-button live report, 2026-06-11). Back is the way out.
      if (phase.kind === "preview-failed" || phase.kind === "convert-failed")
        return "Continue →";
      return "Running…";
    }
    if (currentStep === STEP_DONE) return "Done";
    return "";
  })();

  // Terminal-but-not-complete (cancelled or failed, never run-complete): the
  // server always re-arms the access code on a stop or a failure regardless
  // of which phase it happened in (see resetPhaseForRestart's comment above)
  // — a genuinely exhausted code is refused for real at the actual restart
  // attempt (preview_limit_for_code -> capBlocked), never guessed here. Both
  // continueDisabled and decideCostStepAction treat this exactly like a
  // fresh "setup" phase, so navigating &larr; Back from the stopped/failed
  // run to the cost step leaves Submit alive instead of landing on a dead
  // run screen (cancel-path fix, 2026-07-04 — Back-to-cost-step trap).
  const phaseCancelledOrFailed =
    phase.kind === "preview-failed" || phase.kind === "convert-failed";

  // Run-step (step 3) view model. phase.kind + SSE events → calm run state /
  // results / failure. COUNTS ONLY during the run — quotes and rationale are
  // the privacy hazard, deferred to the encrypted viewer; pattern names appear
  // only on the completed results card. `run-complete` has no patterns field,
  // so names/count come from the previewSnapshot that outlives the preview.
  // Declared ABOVE continueDisabled because the footer gate consumes
  // holdForPassAck (see the step-3 branch below).
  const runEvents: SseEvent[] = "events" in phase ? phase.events : [];
  // Discovery's list minus what the fan-out did not deliver — the same set the
  // encrypted viewer carries (see undeliveredPatternIds). Feeds both the count
  // and the names on the results card, so they can never disagree with the
  // packet the way discovery's raw list did (nine listed, eight delivered).
  const undeliveredIds = undeliveredPatternIds(runEvents);
  const deliveredPatterns: Pattern[] = (previewSnapshot?.patterns ?? []).filter(
    (p) => !undeliveredIds.has(p.pattern_id),
  );
  const runPatternCount: number | null =
    phase.kind === "preview-ready"
      ? phase.patterns.length
      : phase.kind === "convert-running" || phase.kind === "run-complete"
        ? previewSnapshot
          ? deliveredPatterns.length
          : null
        : null;
  const runView = deriveRunView(
    phase.kind,
    runEvents,
    runPatternCount,
    (phase.kind === "preview-failed" || phase.kind === "convert-failed") &&
      phase.cancelled === true,
  );
  const runStepScreen = selectRunStepScreen(runView.mode, fanoutBatched);
  // Irrecoverable-loss guard (pre-launch audit fix, 2026-07-04): a
  // completed/empty/cancelled run must not paint over an un-acked
  // passphrase — see shouldHoldForPassAck's doc comment for the two ways
  // this used to lose it (mid-ceremony, or the gate never showing at all).
  const holdForPassAck = shouldHoldForPassAck(
    runStepScreen,
    !!runPassphrase,
    passSaved,
  );

  const continueDisabled: boolean = (() => {
    if (runInFlight && currentStep !== STEP_RUN) return true;
    // Payment confirmation in flight — no action anywhere until the poll
    // resolves (seam audit 2026-07-22: a live price CTA on a just-charged
    // card is the one button that must not exist).
    if (entitlementPolling) return true;
    if (currentStep === 0) {
      // Drop — PDF-ingest path OR direct-CSV path (preload buttons +
      // future CSV upload) both count as ready-to-advance.
      const csvLoaded = corpusFile !== null && metaJson.trim() !== "";
      if (pdfIngest.kind !== "done" && !csvLoaded) return true;
      // Over-budget → the off-ramp is shown right here; block the advance to
      // Parties (no wasted Haiku call). Hold the advance until the size check
      // resolves; a prescan ERROR fails open (the chain gate is the backstop).
      if (prescanOverBudget) return true;
      if (prescan.kind === "idle" || prescan.kind === "loading") return true;
      return false;
    }
    if (currentStep === 1) {
      return classify.kind !== "ready" && classify.kind !== "confirmed";
    }
    if (currentStep === 2) {
      // Cost screen — can only authorize once the estimate is back AND
      // the chain hasn't already launched. The phase.kind === "setup"
      // gate matters because Back is enabled after preview-ready: a
      // user backing into step 3 must not be able to re-click
      // Authorize and fire a SECOND startPreview() (would mint a new
      // run, spend the code twice, and orphan the in-flight state).
      // A Service-routed record blocks advancement entirely — user must
      // email us before the analysis can launch on records that size.
      if (
        prescan.kind === "ready" &&
        prescan.estimate.service_route === true
      ) {
        return true;
      }
      // Email confirm-for-everyone (front-door spec, 2026-07-04): gates
      // BEFORE the tier/attestation checks below — nothing about payment is
      // actionable until the user has confirmed where results go. Mirrors
      // the Access-attestation gate's shape: a screen inside this SAME pane
      // (see data-pane="2") must be satisfied before Continue does anything.
      if (prescan.kind === "ready" && !emailConfirmed) {
        return true;
      }
      // Access is gated on the hardship attestation — the ONLY gate on the
      // tier (no documents, no income field, ever). Applies only when this
      // click would actually charge (not sponsored / not already paid) — an
      // already-paid run never shows the tier cards, so pwycChoice is moot,
      // but the exclusion is kept as defense-in-depth (mirrors pilotFreeCovered).
      if (
        prescan.kind === "ready" &&
        !pilotFreeCovered &&
        !alreadyPaid &&
        pwycChoice === "Access" &&
        !pwycAccessAttested
      ) {
        return true;
      }
      // Door 2 (per-tier cost gate): the click must carry a VIABLE tier —
      // and that holds regardless of alreadyPaid (review fix, 2026-08-05).
      // A paid code re-scoped to a larger file must not keep a live Submit
      // for a run the server marked underwater; paidTierBlocked routes it
      // to the off-ramp (the footer is suppressed there too — this gate is
      // the belt to that suspender).
      if (paidTierBlocked) return true;
      // Unpaid: a cleared selection (reconcileTierChoice zeroed it after a
      // re-prescan) or a tier the server marked inviable cannot authorize —
      // the user picks another card, or uses the disabled card's email path.
      // Same would-actually-charge exclusions as the attestation gate above.
      if (prescan.kind === "ready" && !pilotFreeCovered && !alreadyPaid) {
        const chosen = prescan.estimate.pwyc_tiers.find(
          (t) => t.name === pwycChoice,
        );
        if (!chosen || !tierIsViable(chosen)) return true;
      }
      // phase.kind !== "setup" blocks a second launch while a run is still
      // in flight or already delivered (run-complete) — but a run that
      // stopped (cancelled or failed) is over, not in flight, so it must
      // NOT hold Submit dead here (see phaseCancelledOrFailed above).
      return (
        prescan.kind !== "ready" ||
        !ready ||
        submitting !== null ||
        (phase.kind !== "setup" && !phaseCancelledOrFailed)
      );
    }
    if (currentStep === STEP_PAY) {
      // Stripe's in-card Pay button is the only action here; the wizard
      // advances itself on onComplete. Footer Continue stays dead (and
      // hidden — see the cta render).
      return true;
    }
    if (currentStep === STEP_RUN) {
      // Read-the-messages — terminal until the run completes; the user
      // then advances to Download on their own. holdForPassAck ALSO holds
      // this footer shut: the screen-level guard keeps the results pane
      // behind the passphrase gate, but this Continue used to enable on
      // run-complete alone — clicking it unmounted the gate and the
      // once-shown passphrase was gone, server copy already discarded
      // (staging 4242 drill, 2026-07-22: a canned-fast run made the window
      // obvious; on prod it's any user mid-ceremony at completion).
      return phase.kind !== "run-complete" || holdForPassAck;
    }
    if (currentStep === STEP_DONE) {
      // Download is terminal — when the viewer is delivered the full-screen
      // DoneScreen takes over (the inline CTA is suppressed below). When the
      // viewer is NOT delivered the user navigated here manually; the inline
      // CTA stays disabled because handleContinue has no branch for it.
      return true;
    }
    return true;
  })();

  const runLocksWizard =
    phase.kind === "preview-running" ||
    phase.kind === "preview-ready" ||
    phase.kind === "convert-running" ||
    phase.kind === "run-complete";
  const backDisabled =
    currentStep === 0 ||
    runInFlight ||
    entitlementPolling ||
    (currentStep >= 3 && runLocksWizard);

  const handleContinue = () => {
    if (continueDisabled) return;
    if (currentStep === 0) {
      setCurrentStep(1);
      return;
    }
    if (currentStep === 1) {
      // Persist the editor's latest grounding (seeded nicknames included) on the
      // way out, whether or not the user edited anything.
      if (classify.kind === "ready" || classify.kind === "confirmed")
        confirmClassify();
      setCurrentStep(2);
      return;
    }
    if (currentStep === 2) {
      // A stopped (cancelled/failed) run reached via &larr; Back is a
      // relaunch, not a resume — run the SAME reset restartRun's own button
      // does (fresh phase, fresh passphrase slot, restartedRun flagged) so
      // the launch that follows behaves identically regardless of which of
      // the two paths (stopped-panel button vs. Back-then-Submit) got the
      // user here (cancel-path fix, 2026-07-04). Must happen before
      // decideCostStepAction below reads phase.kind — this ONLY touches
      // App state, not currentStep (already 2).
      if (phaseCancelledOrFailed) {
        resetPhaseForRestart();
      }
      // Cost screen — decideCostStepAction (unit-tested in lib/cost-step-action)
      // routes the click: a PAID run goes straight to Stripe (no pre-confirm —
      // Checkout IS the commitment step); pilot-free / already-paid keep the
      // explicit two-step confirm (UX audit F — no silent spend) because the
      // click immediately spends the code and starts the ~1hr run. The
      // phaseSettled check is defense-in-depth against the back-from-run
      // re-fire — cancelled/failed phases are treated as NOT settled (a
      // stopped run is over, not in flight; see phaseCancelledOrFailed).
      const action = decideCostStepAction({
        phaseSettled: phase.kind !== "setup" && !phaseCancelledOrFailed,
        pilotFreeCovered,
        // The lib's `paidThisVisit` field means "no checkout needed, arm the
        // two-step confirm" — true for either signal that means that: this
        // visit's Stripe round-trip, OR the server's own already-paid record
        // (see `alreadyPaid` above). Deliberately NOT changing the lib's
        // decision table for this — it's still exactly two paid-without-
        // checkout signals collapsed into one boolean, which is what the
        // field already models.
        paidThisVisit: alreadyPaid,
        confirmArmed: confirmLaunch,
        selfServeOpen:
          prescan.kind === "ready" && prescan.estimate.self_serve_open === true,
      });
      if (action === "advance") {
        setCurrentStep(STEP_RUN);
        return;
      }
      if (action === "checkout") {
        void goToCheckout();
        return;
      }
      if (action === "closed") {
        // Checkout is closed for this paid run — the request-access panel is
        // shown in the step body and the footer is suppressed, so this is
        // defense-in-depth: never fall through to goToCheckout (it would 404).
        return;
      }
      if (action === "arm-confirm") {
        setConfirmLaunch(true);
        return;
      }
      // launch
      setConfirmLaunch(false);
      setCurrentStep(STEP_RUN);
      void startPreview();
      return;
    }
    if (currentStep === STEP_RUN) {
      // Run finished — the user reviews the surfaced patterns here, then
      // advances to Download on their own (no auto-flip). continueDisabled
      // keeps this inert until run-complete.
      setCurrentStep(STEP_DONE);
      return;
    }
    // Step 4 (Download): terminal — the DoneScreen takes over once the
    // viewer is delivered.
  };

  const handleBack = () => {
    if (backDisabled) return;
    setConfirmLaunch(false);
    if (currentStep === STEP_RUN) {
      // Run's "back" destination is the cost screen in BOTH rail shapes:
      // covered journeys never had a Payment pane to land on, and paid
      // journeys must not land back inside a consumed checkout mount.
      setCurrentStep(STEP_COST);
      return;
    }
    if (currentStep > 0) setCurrentStep((currentStep - 1) as WizardStep);
  };

  // "Use a different file" from the Source-step over-budget off-ramp: clear the
  // ingest + estimate so the drop zone reappears for a smaller record. (There's
  // no step before Source, so the off-ramp's back action resets rather than
  // navigates.)
  const resetIngest = () => {
    setCorpusFile(null);
    setPdfIngest({ kind: "idle" });
    setCsvIngest({ kind: "idle" });
    setPrescan({ kind: "idle" });
    // Swapping files abandons any stashed (paid-but-unlaunched) corpus — drop it
    // so the old file's content doesn't linger in IndexedDB.
    void clearPendingRun();
  };

  // Closing-card action — terminal state on the Download step (index 4)
  // once the viewer has been delivered. Pilot framing: one code, one
  // analysis, then
  // back to the marketing site. The access code was already consumed
  // by the server at run-launch (`mark_access_code_used`); clear the
  // local state so a future return-visit hits Landing as a new
  // applicant (no zombie code/email lingering). activeRun is already
  // cleared on terminal phase; only the identity/handshake keys
  // need wiping.
  const handleClose = () => {
    try {
      // Explicit finish: drop the resume pointer so a return visit starts
      // clean rather than reopening the completed run's Download step.
      clearActiveRun();
      void clearPendingRun(); // never leave case content stashed at session end
      localStorage.removeItem("aurora.cloud.accessCode");
      localStorage.removeItem("aurora.cloud.welcomed");
      localStorage.removeItem("aurora.cloud.email");
    } catch {
      // localStorage unavailable; the navigation alone still does the
      // right thing because the marketing site doesn't read these keys.
    }
    // Prod: hand the user back to the marketing site. Staging: reload the
    // app's own root — state was just cleared, so this IS the fresh start
    // (bouncing a tester to prod marketing left them no way back in,
    // live 2026-08-05).
    window.location.href = finishHref();
  };

  // Inline CTA — rendered at the end of every pane's .pane-inner so the
  // action button hugs the content the user just read (tier grid, drop
  // zone, classifier card) instead of floating at a detached page-footer
  // bar. Pattern ported from the desktop Tauri wizard (.wiz-cta).
  const runPatternNames: string[] = deliveredPatterns.map((p) =>
    slugToTitle(p.pattern_id),
  );

  // Shared Back/Continue footer. NOT rendered on the terminal Download step
  // (step 4) — see that pane: the only action there is the Download button, so a
  // nav footer would be dead chrome ("back to what?" once the analysis is done).
  // On the import step (0) the wizard "Back" has nowhere to go — but once a
  // source is ingested the user can still be stuck on the "done" summary with
  // no way out (the confirm/recover/timezone sub-steps all offer "← Choose a
  // different file"; the done state didn't). Repurpose the otherwise-dead Back
  // as that same escape (undo the ingest → drop zone). Scoped to step 0; every
  // other step keeps plain wizard Back.
  const sourceIngested = currentStep === 0 && pdfIngest.kind === "done";
  const leftLabel = sourceIngested ? "← Choose a different file" : "← Back";
  const onLeftClick = sourceIngested ? resetIngest : handleBack;
  const leftDisabled = sourceIngested ? runInFlight : backDisabled;
  const cta = (
    <div
      className={`wiz-cta${
        currentStep === 1 &&
        (classify.kind === "ready" || classify.kind === "confirmed")
          ? " wiz-cta--bare"
          : ""
      }${
        // Density pass 2026-07-04: the cost step's .aurora-card is a fixed
        // height (min(740px, 100vh-160px)) — its content has to fit that
        // budget, not just look nice. Scoped modifier so every other
        // step's footer gap is untouched.
        currentStep === 2 ? " wiz-cta--tight" : ""
      }`}
    >
      {confirmLaunch && currentStep === 2 ? (
        <>
          <span className="rs-cancelconfirm-msg">
            Ready to start? This uses your access code — it works once, and
            the analysis can't be un-run. It usually takes 15–30 minutes;
            we'll show you a passphrase to save, and you can leave once
            it's safe.
          </span>
          <div className="actions-right">
            <button
              type="button"
              className="ghost"
              onClick={() => setConfirmLaunch(false)}
            >
              Not yet
            </button>
            <button
              type="button"
              className="primary"
              onClick={handleContinue}
            >
              Yes — start my analysis
            </button>
          </div>
        </>
      ) : (
        <>
          <span className="helper">{helperText}</span>
          <div className="actions-right">
            <button
              type="button"
              className="ghost"
              onClick={onLeftClick}
              disabled={leftDisabled}
            >
              {leftLabel}
            </button>
            {!(
              (currentStep === STEP_RUN &&
                (phase.kind === "preview-failed" ||
                  phase.kind === "convert-failed") &&
                phase.cancelled === true) ||
              // Payment: Stripe's own Pay button is the action — a dead
              // "Continue" next to it reads as a second, competing CTA.
              currentStep === STEP_PAY
            ) && (
              <button
                type="button"
                className="primary"
                onClick={handleContinue}
                disabled={continueDisabled}
              >
                {nextLabel}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );

  const handleStepperClick = (target: WizardStep) => {
    if (runInFlight) return;
    if (entitlementPolling) return;
    if (target === currentStep) return;
    // A completed payment's pane is consumed chrome (the checkout mount is
    // destroyed) — the rail entry stays as a checkmark, not a destination.
    if (target === STEP_PAY && paidThisVisit) return;
    if (target < currentStep) {
      // No walking back into the setup steps once a run exists — the code
      // is spent; those screens imply edits that can't happen (Rich,
      // 2026-06-10 live pass).
      if (runLocksWizard) return;
      setCurrentStep(target);
      return;
    }
    for (let i = currentStep; i < target; i++) {
      if (!stepDone[i as WizardStep]) return;
    }
    setCurrentStep(target);
  };


  // Titlebar right-meta — when a file is loaded, shows the record size
  // so the user is reminded which case they're inside. Empty before
  // ingest. Kept to message_count only (no party names) so the meta
  // line doesn't grow unbounded.
  const titleMeta: string =
    pdfIngest.kind === "done"
      ? `${pdfIngest.stats.message_count.toLocaleString()} messages`
      : "";

  const stepperItems: Array<{
    step: WizardStep;
    label: string;
    sublabel: string;
  }> = [
    { step: STEP_DROP, label: "Drop your messages", sublabel: "choose a file" },
    {
      step: STEP_PARTIES,
      label: "Confirm the parties",
      sublabel: "Aurora reads who's involved",
    },
    {
      step: STEP_COST,
      label: "Review the cost",
      // Once payment is on file the rail says so — the old static sublabel
      // left a paid user staring at "choose what you pay" mid-re-upload
      // (Ryan's P1, 2026-07-09).
      sublabel: alreadyPaid ? "paid ✓" : "choose what you pay",
    },
    // Payment (Option A, 2026-07-22) — present only on paying self-serve
    // journeys with the embedded rail available; covered codes skip
    // STEP_COST → STEP_RUN and this entry never renders, keeping the
    // counter and checkmarks honest in both shapes.
    ...(showPaymentStep
      ? [
          {
            step: STEP_PAY,
            label: "Payment",
            sublabel: paidThisVisit ? "paid ✓" : "handled by Stripe",
          },
        ]
      : []),
    // The journey ENDS at the run step for most users: the result arrives
    // by email (deep link) or, for tab-keepers, as the full-screen Done
    // bookend — neither renders inside the wizard chrome. Listing a 5th
    // "Download" step promised a destination the stepper never reaches
    // (UX audit G / Rich #9).
    {
      step: STEP_RUN,
      label: "Run the analysis",
      // Only promise the email when the server will actually send one: flag
      // ON **and an address exists** (linked to the code, or user-set). The
      // flag-only gate burned a live walkthrough (2026-07-02): an unlinked
      // code ran with "emailed to you" showing, and the ready-email path
      // correctly skipped the send — promise shown, nothing delivered.
      // Duration tracks the transport: sync (the product) is 15–30 min; a
      // batch-opted-in box (internal) is honest about the hours-long hand-off.
      sublabel: (() => {
        const willEmail =
          readyEmailEnabled && Boolean(notifyEmail ?? linkedEmail);
        if (fanoutBatched)
          return willEmail
            ? "the reading takes a few hours · emailed to you"
            : "the reading takes a few hours";
        return willEmail
          ? "the reading takes 15–30 min · emailed to you"
          : "the reading takes 15–30 min";
      })(),
    },
  ];

  // Counter follows the VISIBLE step list (4 since the unreachable
  // "Download" entry was dropped — the chip read "STEP 4 OF 5" while the
  // sidebar showed 4, Rich's 2026-06-10 night pass). The post-complete
  // download pane (currentStep 4) reads "Step 4 of 4", not a phantom 5th.
  const stepCounter = `Step ${Math.min(currentStep + 1, stepperItems.length)} of ${stepperItems.length}`;

  // Terminal bookend — once the encrypted viewer is in the user's
  // Downloads folder the wizard chrome falls away and we land on the
  // full-screen completion screen (mirrors the full-screen entry). This
  // is where the only copy of the passphrase lives.
  if (phase.kind === "run-complete" && phase.viewer) {
    return (
      <DoneScreen
        viewer={phase.viewer}
        onClose={handleClose}
        onRedownload={triggerViewerDownload}
        redownloading={viewerBuilding}
        backupEmail={backupEmailAddr ?? undefined}
        backupEmailed={backupAvailable}
        retentionDays={retentionDays}
        mobile={isMobileUa(navigator.userAgent, navigator.maxTouchPoints)}
      />
    );
  }

  // Deep-link entry (the "your analysis is ready" email): gate-free landing,
  // ABOVE the access-code gate. Once the user downloads, phase.viewer is set
  // and the DoneScreen bookend above takes over. Ordered after DoneScreen so
  // the post-download state wins.
  if (deepLink) {
    return (
      <DeepLinkLanding
        state={deepLinkState}
        onDownload={triggerViewerDownload}
        downloading={viewerBuilding}
        error={errorMsg || undefined}
        onRetry={() => {
          // In-place resolver re-run, NOT a reload — a reload on a down
          // connection swaps this screen for a browser error page.
          setDeepLinkState("loading");
          setDeepLinkNonce((n) => n + 1);
        }}
        mobile={isMobileUa(navigator.userAgent, navigator.maxTouchPoints)}
      />
    );
  }

  // Queued full run → full-screen hand-off (close the tab, we'll email you).
  // A takeover like the deep-link landing, so the queued wait doesn't have to
  // thread through the wizard's run-step state. The poll flips this to
  // convert-running on promotion, dropping back into the normal run step.
  if (phase.kind === "convert-queued") {
    return <QueuedScreen position={phase.position} />;
  }

  return (
    <main className="aurora-page">
      <div className="landing-wrap aurora-wrap">
        <header className="landing-mast aurora-mast--bare">
          {/* Plain navigation home (NOT handleClose): keeps the stored code +
              resume pointer so a return visit resumes; the beforeunload
              leave-guard still intercepts while a run needs this tab. On
              staging "home" is the app root, not prod marketing. */}
          <a
            className="landing-brand"
            href={brandHomeHref()}
            aria-label={
              isStagingHost() ? "Aurora — home" : "Aurora — back to plaintosee.cc"
            }
          >
            <img
              className="landing-logo"
              src="/aurora-icon.png"
              alt="Aurora logo"
            />
            <div className="landing-wordmark">
              Aurora
              <small>Pattern analysis for coercive control</small>
            </div>
          </a>
          <div></div>
          <div className="aurora-mast-right">
            {siteMode && siteMode !== "live" && (
              <span
                className={`aurora-mode-chip aurora-mode-chip--${siteMode}`}
                title="Operator view — current run mode. Users don't see this."
              >
                {siteMode === "demo" ? "Demo mode" : "Pilot mode"}
              </span>
            )}
            {/* Tester escape (staging + local dev only; prod never renders
                it): a full clean start from anywhere in the wizard, same
                semantics as the Download pane's "You're done" link. Exists
                because a tester loops codes in one browser, and mid-wizard
                there is deliberately no code entry (live, 2026-08-06).
                MODE !== "test": vitest sets DEV=true, and a button whose
                click navigates (handleClose) must not sit in every App
                test's render. */}
            {(isStagingHost() ||
              (import.meta.env.DEV && import.meta.env.MODE !== "test")) && (
              <button
                type="button"
                className="landing-link landing-link-btn"
                onClick={handleClose}
              >
                Start over
              </button>
            )}
            <FeedbackButton server={server} accessCode={accessCode} />
          </div>
        </header>

        <section className="aurora-card">
          <div className="aurora-card-grid">
            <div className="aurora-titlebar">
              {/* Left zone sits over the workflow sidebar and carries the
              "Workflow" heading so the stepper content starts at the top
              of its column instead of below an empty band. Right zone
              sits over the work area; pill left-aligns with the in-pane
              content padding below. */}
              <div className="aurora-titlebar-left">
                <span className="stepper-head">Workflow</span>
              </div>
              <div className="aurora-titlebar-right">
                <span className="aurora-step-pill">{stepCounter}</span>
                {titleMeta && (
                  <span className="aurora-titlebar-meta">{titleMeta}</span>
                )}
              </div>
            </div>

            <aside className="wizard-stepper">
              {stepperItems.map(({ step, label, sublabel }) => {
                const classes = [
                  "step-item",
                  step === currentStep ? "current" : "",
                  stepDone[step] ? "done" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <button
                    key={step}
                    type="button"
                    className={classes}
                    onClick={() => handleStepperClick(step)}
                    disabled={runInFlight}
                  >
                    <span className="dot">
                      {stepDone[step] ? "✓" : step + 1}
                    </span>
                    <div>
                      <div className="label">{label}</div>
                      <div className="sublabel">{sublabel}</div>
                    </div>
                  </button>
                );
              })}
            </aside>

            <section
              className={`wizard-work${errorMsg || resuming || payNotice ? " has-toast" : ""}`}
            >
              {/* === STEP 0 — Drop your messages === */}
              <section className={paneClass(0)} data-pane="0">
                <div className="pane-inner">
                  {prescan.kind === "ready" && prescanOverBudget ? (
                    /* Over-budget → the off-ramp takes over the whole step as
                       its own screen (no drop zone / ingest details / dev
                       affordances), and we never reached Parties. An
                       already-paid code gets the same payment acknowledgment
                       the paid-tier-blocked branch shows (review R2) — an
                       all-tiers-inviable corpus on a paid code must not read
                       as if the payment vanished. */
                    <CostOffRamp
                      estimate={prescan.estimate}
                      onBack={resetIngest}
                      accessCode={accessCode}
                      alreadyPaid={alreadyPaid}
                    />
                  ) : phase.kind !== "setup" ? (
                    /* A run exists (in flight, finished, or failed). The drop
                       zone is a trap here: ingesting a new file severs the
                       resume pointer to the paid run, and the burned invite
                       code can't launch another one anyway (audit B6). Redirect
                       to where the run lives instead. */
                    <>
                      <h1 className="step-title">
                        {phase.kind === "run-complete"
                          ? "Your analysis is ready"
                          : phase.kind === "preview-failed" ||
                              phase.kind === "convert-failed"
                            ? "Your analysis hit a problem"
                            : "Your analysis is already underway"}
                      </h1>
                      <p className="step-lede">
                        {phase.kind === "run-complete"
                          ? "This invite's analysis is finished. Head to the download step to save your file."
                          : phase.kind === "preview-failed" ||
                              phase.kind === "convert-failed"
                            ? "See what happened on the analysis step — and reach us from there if you need a hand."
                            : "Your messages are being analyzed. Dropping a new file here would lose your place in that analysis — and each invite covers one analysis."}
                      </p>
                      <div className="actions">
                        <button
                          type="button"
                          className="primary"
                          onClick={() =>
                            setCurrentStep(
                              phase.kind === "run-complete" ? 4 : 3,
                            )
                          }
                        >
                          {phase.kind === "run-complete"
                            ? "Go to your download"
                            : "View your analysis"}
                        </button>
                      </div>
                    </>
                  ) : pdfIngest.kind === "timezone" ? (
                    /* The export carries no time zone of its own (AppClose),
                       so its wall-clock times can't be placed on a timeline
                       until the user says which zone they're in. Same shared
                       prompt the CSV paths use; asked once, then remembered
                       on the case. */
                    <TimeZonePrompt
                      filename={pdfIngest.sourceName}
                      selected={pdfIngest.selectedZone}
                      onSelect={(z) =>
                        setPdfIngest((prev) =>
                          prev.kind === "timezone"
                            ? { ...prev, selectedZone: z }
                            : prev,
                        )
                      }
                      onContinue={(z) =>
                        finishPdfWithZone(
                          pdfIngest.sourceName,
                          pdfIngest.corpusId,
                          pdfIngest.pages,
                          pdfIngest.lines,
                          pdfIngest.format,
                          z,
                        )
                      }
                      onBack={resetIngest}
                    />
                  ) : pdfIngest.kind === "confirm" ||
                    pdfIngest.kind === "recover" ? (
                    /* Detection wasn't confident (confirm) or recognized
                       nothing (recover): a focused format-confirm screen takes
                       over the step. Choosing a different file resets ingest
                       back to the drop zone. */
                    <FormatConfirmStep
                      mode={pdfIngest.kind}
                      filename={pdfIngest.sourceName}
                      choices={
                        pdfIngest.kind === "confirm" ? pdfIngest.choices : undefined
                      }
                      selected={pdfIngest.selected}
                      note={
                        pdfIngest.kind === "recover"
                          ? pdfIngest.note
                          : undefined
                      }
                      accessCode={accessCode}
                      onSelect={selectPdfFormat}
                      onContinue={confirmPdfFormat}
                      onChooseDifferent={resetIngest}
                    />
                  ) : csvIngest.kind === "column-map" ? (
                    /* Arbitrary tabular CSV — match columns to roles (Phase 3,
                       Piece B). Field-first rows; the inline example updates on
                       every edit. */
                    <ColumnMappingStep
                      filename={csvIngest.sourceName}
                      headers={csvIngest.headers}
                      sampleRows={csvIngest.sampleRows}
                      rowCount={csvIngest.rowCount}
                      mapping={csvIngest.mapping}
                      suggested={csvIngest.suggested}
                      accessCode={accessCode}
                      onChange={(m) =>
                        setCsvIngest((prev) =>
                          prev.kind === "column-map"
                            ? { ...prev, mapping: m }
                            : prev,
                        )
                      }
                      onContinue={confirmColumnMapping}
                      onBack={resetIngest}
                    />
                  ) : csvIngest.kind === "timezone" ? (
                    /* Naive timestamps with no zone — the shared tz prompt
                       (Phase 3, Piece C). Solicited once, remembered on the
                       case. */
                    <TimeZonePrompt
                      filename={csvIngest.sourceName}
                      selected={csvIngest.selectedZone}
                      onSelect={(z) =>
                        setCsvIngest((prev) =>
                          prev.kind === "timezone"
                            ? { ...prev, selectedZone: z }
                            : prev,
                        )
                      }
                      onContinue={(z) =>
                        finishCsvWithZone(
                          csvIngest.sourceName,
                          csvIngest.corpusId,
                          csvIngest.text,
                          csvIngest.via,
                          csvIngest.mapping,
                          z,
                        )
                      }
                      onBack={resetIngest}
                    />
                  ) : (
                  <>
                  {pdfIngest.kind === "done" ? (
                    <>
                      <h1 className="step-title">Here&rsquo;s what we found</h1>
                      <p className="step-lede">
                        Check that this looks like the right export — the
                        people, the dates, the number of messages. If it
                        does, continue.
                      </p>
                    </>
                  ) : (
                    <>
                      <h1 className="step-title">Drop your messages</h1>
                      <p className="step-lede">
                        Read right here in your browser — nothing leaves your
                        computer until you run it.
                      </p>
                    </>
                  )}

                  <label
                    className="drop-target"
                    onClick={() => notePickerOpened()}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add("dragging");
                    }}
                    onDragLeave={(e) =>
                      e.currentTarget.classList.remove("dragging")
                    }
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove("dragging");
                      const f = e.dataTransfer.files?.[0];
                      if (!f) return;
                      void acceptChosenFile(f);
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,application/pdf,.csv,text/csv"
                      hidden
                      onChange={(e) => {
                        const f = e.target.files?.[0] ?? null;
                        if (!f) {
                          setCorpusFile(null);
                          return;
                        }
                        pickerFileArrivedRef.current = true;
                        void acceptChosenFile(f);
                      }}
                    />
                    {corpusFile ? (
                      <div className="drop-target-loaded">
                        <div className="drop-target-filename">
                          {pdfIngest.kind === "done" ||
                          pdfIngest.kind === "running"
                            ? pdfIngest.sourceName
                            : corpusFile.name}
                        </div>
                        <div className="drop-target-meta">
                          {(corpusFile.size / 1024).toFixed(1)} KB
                        </div>
                      </div>
                    ) : (
                      <div className="drop-target-prompt">
                        <div className="drop-target-headline">
                          Drop your messages file here
                        </div>
                        <div className="drop-target-sub">
                          or click to choose a file
                        </div>
                        <div className="drop-target-accepts">
                          PDF or CSV files
                        </div>
                      </div>
                    )}
                  </label>

                  {!corpusFile && <FormatHelp />}

                  {pdfIngest.kind === "running" && (
                    <div className="ingest-progress">
                      <span
                        className="aurora-spinner aurora-spinner-lg"
                        aria-hidden="true"
                      />
                      <div className="ingest-progress-body">
                        <div className="ingest-progress-headline">
                          Reading <code>{pdfIngest.sourceName}</code>…
                        </div>
                        <div className="ingest-progress-detail">
                          {pdfIngest.totalPages > 0 ? (
                            <>
                              Page {pdfIngest.pageNumber} of{" "}
                              {pdfIngest.totalPages} · extracting messages,
                              parties, and time zone
                            </>
                          ) : (
                            <>Extracting messages, parties, and time zone</>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {pdfIngest.kind === "done" && (
                    <dl className="ingest-summary">
                      <dt>Format</dt>
                      <dd>
                        {/* Derived from PDF_FORMAT_INFO, not a ternary ladder —
                            the ladder this replaced labelled AppClose
                            "Generic CSV". generic_csv is the only non-PDF
                            adapter that reaches this summary. */}
                        {PDF_ADAPTER_LABEL[
                          pdfIngest.adapter as keyof typeof PDF_ADAPTER_LABEL
                        ] ?? "Generic CSV"}
                      </dd>
                      <dt>Messages</dt>
                      <dd>
                        {pdfIngest.stats.message_count.toLocaleString()}
                        {pdfIngest.adapter === "civil_communicator" &&
                          (pdfIngest.stats as CcIngestResult["stats"])
                            .rejected_count > 0 && (
                            <span className="muted">
                              {" "}
                              (
                              {
                                (pdfIngest.stats as CcIngestResult["stats"])
                                  .rejected_count
                              }{" "}
                              rejected)
                            </span>
                          )}
                        {pdfIngest.adapter === "generic_csv" &&
                          (pdfIngest.stats as GenericCsvIngestResult["stats"])
                            .rejected_count > 0 && (
                            <span className="muted">
                              {" "}
                              (
                              {
                                (
                                  pdfIngest.stats as GenericCsvIngestResult["stats"]
                                ).rejected_count
                              }{" "}
                              rejected)
                            </span>
                          )}
                      </dd>
                      <dt>Between</dt>
                      <dd>
                        {pdfIngest.stats.party_a} ↔ {pdfIngest.stats.party_b}
                      </dd>
                      {pdfIngest.stats.date_range.start && (
                        <>
                          <dt>Date range</dt>
                          <dd>
                            {humanDateRange(
                              pdfIngest.stats.date_range.start,
                              pdfIngest.stats.date_range.end,
                            )}
                          </dd>
                        </>
                      )}
                      {"timezone" in pdfIngest.stats &&
                        pdfIngest.stats.timezone && (
                          <>
                            <dt>Time zone</dt>
                            <dd
                              title={
                                (pdfIngest.stats as IngestResult["stats"])
                                  .timezone
                              }
                            >
                              {humanTimezone(
                                (pdfIngest.stats as IngestResult["stats"])
                                  .timezone,
                              )}{" "}
                              {"timezone_assumed" in pdfIngest.stats &&
                              (
                                pdfIngest.stats as {
                                  timezone_assumed?: boolean;
                                }
                              ).timezone_assumed ? (
                                <span className="warn">
                                  — the file didn't say its time zone, so we
                                  assumed this one. If it's wrong, write to us
                                  before running.
                                </span>
                              ) : (
                                <span className="muted">
                                  — used to date-stamp your messages
                                </span>
                              )}
                            </dd>
                          </>
                        )}
                      {pdfIngest.stats.out_of_set_senders.length > 0 && (
                        <>
                          <dt>Heads-up</dt>
                          <dd className="warn">
                            {pdfIngest.stats.out_of_set_senders.length}{" "}
                            sender(s) not in the declared parties
                          </dd>
                        </>
                      )}
                    </dl>
                  )}

                  {pdfIngest.kind === "done" && pdfIngest.warnings.length > 0 && (
                    <div className="phase-banner">
                      {pdfIngest.warnings.map((w) => (
                        <div key={w} className="warn">
                          ⚠ {w}
                        </div>
                      ))}
                    </div>
                  )}

                  {pdfIngest.kind === "error" && (
                    <div className="phase-banner err">
                      <strong>PDF ingest failed.</strong> {pdfIngest.message}
                    </div>
                  )}

                  {DEV_AFFORDANCES_ENABLED && (
                    <div className="dev-affordances">
                      <label className="dev-adapter">
                        <span>Adapter override</span>
                        <select
                          value={adapter}
                          onChange={(e) => setAdapter(e.target.value)}
                        >
                          <option value="generic_csv">generic_csv</option>
                          <option value="ofw">ofw</option>
                          <option value="civil_communicator">
                            civil_communicator
                          </option>
                          <option value="two_houses">two_houses</option>
                          <option value="talking_parents">
                            talking_parents
                          </option>
                        </select>
                      </label>
                      <div className="sample-buttons">
                        <button
                          type="button"
                          className="primary-ghost"
                          onClick={loadDemoCharliePdf}
                        >
                          Load demo-charlie PDF (browser ingest)
                        </button>
                        <button
                          type="button"
                          className="ghost"
                          onClick={loadDemoCharlie}
                        >
                          Load demo-charlie CSV
                        </button>
                        <button
                          type="button"
                          className="ghost"
                          onClick={loadSample}
                          title="2-row banal CSV — for API-surface testing in fake mode; produces 0 patterns against real Opus"
                        >
                          Load trivial fixture
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Size check fires the moment ingest finishes; when it
                      comes back over-budget the branch above replaces this
                      whole step with the off-ramp screen — before Parties. */}
                  {pdfIngest.kind === "done" &&
                    (prescan.kind === "idle" || prescan.kind === "loading") && (
                      <div className="cost-loading">
                        <span className="aurora-spinner" aria-hidden="true" />
                        <span>Checking the size…</span>
                      </div>
                    )}

                  {currentStep === 0 && cta}
                  </>
                  )}
                </div>
              </section>

              {/* === STEP 1 — Confirm parties === */}
              <section className={paneClass(1)} data-pane="1">
                <div className="pane-inner">
                  <h1 className="step-title">Who's in this case?</h1>
                  <p className="step-lede">
                    Aurora read your messages and matched the two people, their
                    children, and the nicknames it saw them go by. Check it over
                    and fix anything — then continue.
                  </p>

                  {classify.kind === "idle" && (
                    <div className="phase-banner muted classify-banner">
                      <span className="aurora-spinner" aria-hidden="true" />
                      <span>
                        Preparing to identify the people in your messages…
                      </span>
                    </div>
                  )}

                  {classify.kind === "running" && (
                    <div className="phase-banner muted classify-banner">
                      <span className="aurora-spinner" aria-hidden="true" />
                      <span>
                        Scanning your messages for the people involved…
                        <span className="muted small">
                          {" "}
                          Usually takes a few seconds.
                        </span>
                      </span>
                    </div>
                  )}

                  {(classify.kind === "ready" ||
                    classify.kind === "confirmed") && (
                    // Land directly on the shared people editor (mirrors desktop's
                    // PeoplePane) — no summary gate — so the user always sees and
                    // confirms the matched nicknames. `key` remounts it on a fresh
                    // classification (the editor seeds once at mount).
                    <ConfirmPeoplePanel
                      key={classifyEpoch}
                      result={classify.result}
                      initialGrounding={classifyGrounding}
                      onGroundingChange={setClassifyGrounding}
                      getExcerpts={excerptSearcher}
                    />
                  )}

                  {classify.kind === "error" && (
                    <div className="phase-banner err classify-banner">
                      <strong>Couldn't scan for people.</strong>{" "}
                      {classify.message}
                      <p className="muted small">
                        Your file is already read — this scan happens on our
                        side, and the problem is usually temporary.
                      </p>
                      <button
                        type="button"
                        className="ghost"
                        onClick={() => setClassifyNonce((n) => n + 1)}
                      >
                        Scan again
                      </button>
                    </div>
                  )}
                  {currentStep === 1 && cta}
                </div>
              </section>

              {/* === STEP 2 — Review the cost === */}
              <section className={paneClass(2)} data-pane="2">
                <div className="pane-inner">
                  <h1 className="step-title">
                    Review the cost
                    <InfoPopover label="How are costs calculated?" align="left">
                      <strong>How are costs calculated?</strong>
                      <p>
                        One analysis, one flat price — and you choose what you
                        pay. The size of your record doesn't change the price;
                        we check it only to make sure it fits our self-serve
                        service. The number you pick here is the number you get
                        charged.
                      </p>
                      <p>
                        A sacrifice is something you'd feel and budget around;
                        a hardship means going without something you need —
                        pick the tier that matches which one applies to you.
                      </p>
                    </InfoPopover>
                  </h1>
                  {/* Pricing lede is suppressed when the pilot-free band OR the
                already-paid band is showing — both carry their own cost
                message, and a "what you see here is what you pay" line would
                contradict either (there's no choice left to make once a code
                is already paid). Still shown for Contact-tier and non-pilot,
                non-paid pricing. Density pass 2026-07-04 (Rich: "even the two
                subtitle texts are crazy long") — cut to one short clause. */}
                  {!pilotFreeCovered && !alreadyPaid && (
                    <p className="step-lede">
                      One flat charge — you choose what you pay.
                    </p>
                  )}

                  {prescan.kind === "loading" && (
                    <div className="cost-loading">
                      <span className="aurora-spinner" aria-hidden="true" />
                      <span>Sizing up your messages…</span>
                    </div>
                  )}

                  {prescan.kind === "error" && (
                    <div className="cost-error">
                      <p>
                        <strong>We couldn't get an estimate.</strong>{" "}
                        {prescan.message}
                      </p>
                      <button
                        type="button"
                        className="ghost"
                        onClick={retryPrescan}
                      >
                        Try again
                      </button>
                    </div>
                  )}

                  {/* Integrated (Rich, staging feedback 2026-07-04): the email
                      ask renders ABOVE the cost content, in the SAME pane, not
                      as a gate that replaces it — see EmailConfirmBlock's doc
                      comment. Shown above every content variant except the
                      over-budget / paid-tier-blocked / out-of-starts dead
                      ends, where an address wouldn't do anything (no run is
                      possible either way). */}
                  {prescan.kind === "ready" &&
                    !prescanOverBudget &&
                    !paidTierBlocked &&
                    !capBlocked && (
                      <EmailConfirmBlock
                        linkedEmail={linkedEmail}
                        address={notifyEmail ?? linkedEmail}
                        onConfirm={handleEmailConfirmed}
                        onInvalid={() => setEmailConfirmed(false)}
                      />
                    )}

                  {prescan.kind === "ready" &&
                    !prescanOverBudget &&
                    !paidTierBlocked &&
                    !checkoutClosed && (
                      <PwycCostPanel
                        estimate={prescan.estimate}
                        choice={pwycChoice}
                        onChoice={(name) => {
                          setPwycChoice(name);
                          // switching tiers always re-arms the Access gate
                          setPwycAccessAttested(false);
                        }}
                        attested={pwycAccessAttested}
                        onAttested={setPwycAccessAttested}
                        paidMode={!pilotFreeCovered && !alreadyPaid}
                        alreadyPaid={alreadyPaid}
                        livePrices={livePrices}
                      />
                    )}

                  {/* Priced run whose self-serve checkout is closed → request-
                      access off-ramp REPLACES the tier grid + Authorize (no dead
                      pay button that would 404). Same treatment as over-budget.
                      capBlocked (out of starts) is mutually exclusive with this
                      today, but guarded so its terminal panel wins if both ever
                      hold. */}
                  {currentStep === 2 && checkoutClosed && !capBlocked && (
                    <CheckoutClosedPanel
                      accessCode={accessCode}
                      onBack={handleBack}
                    />
                  )}

                  {/* Backstop. Over-budget is decided at the Source step now
                      (prescan fires on ingest and blocks the advance to
                      Parties), so a user can't normally reach the cost screen
                      over budget — this stays as defense-in-depth in case that
                      gating ever regresses, so they'd still get the off-ramp
                      here instead of a TierGrid + Authorize for a run that aborts. */}
                  {prescan.kind === "ready" && prescanOverBudget && (
                    <CostOffRamp
                      estimate={prescan.estimate}
                      onBack={handleBack}
                      accessCode={accessCode}
                      // Same payment acknowledgment as the paid-tier-blocked
                      // branch below (review R2) — the two off-ramp variants
                      // must not disagree about a payment on file.
                      alreadyPaid={alreadyPaid}
                    />
                  )}

                  {/* Already-paid × inviable paid tier (review fix,
                      2026-08-05): the payment on file is for a tier this
                      record's run cost exceeds — the SAME off-ramp the
                      unpaid flow gets REPLACES the paid band + Submit (no
                      live CTA for an underwater run), with copy
                      acknowledging the payment. capBlocked's terminal panel
                      wins if both ever hold (same guard as checkout-closed). */}
                  {prescan.kind === "ready" && paidTierBlocked && !capBlocked && (
                    <CostOffRamp
                      estimate={prescan.estimate}
                      onBack={handleBack}
                      accessCode={accessCode}
                      alreadyPaid
                    />
                  )}

                  {/* Off-ramp carries its own actions (email + use-a-different-
                      file); the standard Back/Authorize footer is suppressed so
                      there's no dead "Authorize" the user can't act on. */}
                  {/* Out of preview starts: a terminal panel with the two
                      real exits (email us / new code) REPLACES the submit
                      footer — an enabled Submit that 429s was a dead end
                      (live, 2026-06-11). */}
                  {currentStep === 2 && !prescanOverBudget && capBlocked && (
                    <CapBlockedPanel
                      accessCode={accessCode}
                      onNewCode={() => {
                        // Same key Shell uses (aurora.cloud.accessCode) —
                        // clear both stores, reload lands on code entry.
                        try {
                          localStorage.removeItem("aurora.cloud.accessCode");
                          sessionStorage.removeItem("aurora.cloud.accessCode");
                        } catch {
                          // storage unavailable — reload still re-gates
                        }
                        window.location.reload();
                      }}
                    />
                  )}
                  {/* Shown alongside the email block now (integrated, not a
                      gate) — continueDisabled itself still checks
                      emailConfirmed, so Continue/Authorize stays disabled
                      until the address is valid/confirmed. Off-ramp panels
                      above (checkout-closed, cap-blocked) carry their own
                      actions and still suppress this footer. */}
                  {currentStep === 2 &&
                    !prescanOverBudget &&
                    !paidTierBlocked &&
                    !capBlocked &&
                    !checkoutClosed &&
                    cta}
                </div>
              </section>

              {/* === STEP — Payment (Option A: Stripe Embedded Checkout
                  mounted in-card; present only on paying self-serve
                  journeys — see showPaymentStep) === */}
              <section className={paneClass(STEP_PAY)} data-pane="3">
                <div className="pane-inner">
                  <h1 className="step-title">
                    Payment
                    <InfoPopover label="About payment" align="left">
                      <strong>How payment works</strong>
                      <p>
                        Payment is handled entirely by Stripe — your card
                        details never touch Aurora. Your messages stay on
                        this page while you pay, and the analysis starts the
                        moment the payment completes.
                      </p>
                    </InfoPopover>
                  </h1>
                  {/* No tier/price here — Stripe's frame already leads with
                      the product name + amount, and repeating it doubled the
                      visual weight (Rich, 2026-07-22). */}
                  {/* The address rides the point-of-no-return lede (drill
                      finding, 2026-07-22): in the embedded flow payment IS
                      the submit, so this is the last calm moment to catch a
                      wrong notify address — Rich's browser autofilled one he
                      never read. Always non-null here in practice (checkout
                      requires emailConfirmed), but degrade to the plain lede
                      rather than render "to ." Gated on the ACTIVE step like
                      the Stripe mount below — hidden panes stay in the DOM,
                      and an always-rendered address here duplicates the cost
                      step's confirm line. */}
                  <p className="step-lede">
                    {currentStep === STEP_PAY && (notifyEmail ?? linkedEmail) ? (
                      <>
                        Your analysis starts the moment payment completes
                        &mdash; we&rsquo;ll email it to{" "}
                        <strong>{notifyEmail ?? linkedEmail}</strong>.
                      </>
                    ) : (
                      <>Your analysis starts the moment payment completes.</>
                    )}
                  </p>
                  {currentStep === STEP_PAY &&
                    stripePublishableKey !== null &&
                    embeddedClientSecret !== null && (
                      <PaymentStep
                        publishableKey={stripePublishableKey}
                        clientSecret={embeddedClientSecret}
                        onComplete={() => void handleEmbeddedPaid()}
                        onMountError={() =>
                          void postCheckoutReturn({
                            server,
                            kind: "embedded-mount-error",
                            accessCode: accessCode || null,
                          })
                        }
                      />
                    )}
                  {/* Hidden panes stay in the DOM — gate the shared cta on
                      the step or every screen grows a second footer. */}
                  {currentStep === STEP_PAY && cta}
                </div>
              </section>

              {/* === STEP 3 — Read the messages === */}
              <section className={paneClass(STEP_RUN)} data-pane="4">
                <div className="pane-inner">
                  {holdForPassAck ? (
                    // Irrecoverable-loss guard (pre-launch audit fix,
                    // 2026-07-04): the run already finished underneath this
                    // gate (or never showed it at all — no linked email) —
                    // hold here until the user acks before results/empty/
                    // cancelled swap in. runComplete swaps the "is running"
                    // lede for a "just finished" one since it's no longer
                    // true.
                    <PassphraseGate
                      key={runPassphrase}
                      passphrase={runPassphrase!}
                      restarted={restartedRun}
                      runComplete
                      onSaved={() => {
                        setPassSaved(true);
                        markActiveRunPassAcked();
                      }}
                    />
                  ) : runStepScreen === "results" ? (
                    <ResultsSummary
                      patternNames={runPatternNames}
                      patternCount={runView.patternCount ?? 0}
                      grounded={runView.grounded}
                      groundedTotal={runView.groundedTotal}
                    />
                  ) : runStepScreen === "empty" ? (
                    <EmptyResult />
                  ) : runStepScreen === "cancelled" ? (
                    // Cancel-path fix, 2026-07-04 (launch-audit RVCZ- repro):
                    // this used to key the whole restartable-vs-used-up read
                    // on phase.kind (preview-failed → restartable, convert-
                    // failed → "already been used", dead end) — a stand-in
                    // for server truth that was simply wrong: the lifecycle
                    // re-arms the access code on ANY stop or failure
                    // regardless of which phase it happened in (see
                    // restartRun's comment below), confirmed live for a
                    // paid self-serve code stopped mid-convert (status
                    // 'active', used_at None right after). The one real
                    // "no fresh starts left" refusal already has its own
                    // server-truth-driven panel (CapBlockedPanel, shown when
                    // the actual restart attempt 429s with
                    // preview_limit_for_code) — this screen never needs to
                    // guess it. The headline stays neutral ("this run was
                    // stopped", not "you stopped it") because a stop can
                    // arrive with no user click at all — an operator-console
                    // cancel or a crash reads identically here.
                    <>
                      <div className="phase-banner muted" role="status">
                        <strong>This run was stopped before it finished.</strong>{" "}
                        Your access code is still good — pick up right where
                        you left off.
                      </div>
                      <div className="cancel-restart-row">
                        <button
                          type="button"
                          className="primary"
                          onClick={restartRun}
                        >
                          Set up the run again &rarr;
                        </button>
                        <span className="cancel-restart-hint">
                          Want to change the file or names first? Use
                          &larr;&nbsp;Back.
                        </span>
                      </div>
                    </>
                  ) : runStepScreen === "failed" ? (
                    <>
                      <div className="phase-banner err">
                        <strong>
                          Something went wrong {runView.failedReason}.
                        </strong>{" "}
                        Your access code still works — you can try again right
                        now, or send feedback from the header and we'll look
                        into it.
                      </div>
                      <div className="cancel-restart-row">
                        <button
                          type="button"
                          className="primary"
                          onClick={restartRun}
                        >
                          Try again &rarr;
                        </button>
                        <span className="cancel-restart-hint">
                          Your file and details are still here. Want to change
                          them first? Use &larr;&nbsp;Back.
                        </span>
                      </div>
                    </>
                  ) : runStepScreen === "handoff" ? (
                    // Batched: a calm hand-off, not a live monitor (the run is up
                    // to an hour; we email you when it's ready). This covers the
                    // whole non-terminal run-step lifecycle — including the
                    // "setup" beat right after Submit, which used to fall through
                    // to the legacy live view. RunState below is non-batched only.
                    // The pre-convert nuance lives INSIDE RunHandoff: until the
                    // convert is actually running server-side, preview→convert
                    // promotion is client-side (the open tab re-uploads the
                    // corpus), so closing the tab would strand the run (audit
                    // B5) — convertRunning gates "safe to close" + shows a
                    // keep-this-tab-open-for-now note instead.
                    <RunHandoff
                      // Keyed by run identity: internal choices (the gate's
                      // arm-checkbox, "Stay and watch") must not leak from a
                      // canceled/finished run into the next one in this tab.
                      key={
                        phase.kind === "preview-running" ||
                        phase.kind === "preview-ready"
                          ? phase.previewId
                          : phase.kind === "convert-running"
                            ? phase.runId
                            : "handoff"
                      }
                      email={notifyEmail ?? linkedEmail}
                      restarted={restartedRun}
                      // Only the full run carries a run_id/token to set the
                      // notify address on — offer "Change" once it's running.
                      onChangeEmail={
                        phase.kind === "convert-running"
                          ? () => setEmailModalOpen(true)
                          : undefined
                      }
                      passphrase={runPassphrase}
                      convertRunning={phase.kind === "convert-running"}
                      passSaved={passSaved}
                      onPassSavedChange={(v) => {
                        setPassSaved(v);
                        // Survive reloads: the gate ceremony happened once.
                        if (v) markActiveRunPassAcked();
                      }}
                      events={"events" in phase ? phase.events : []}
                      startedAt={runStartedAt}
                      readyEmailEnabled={readyEmailEnabled}
                      howHeard={{
                        state: howHeardState,
                        onAnswer: (s, d) => resolveHowHeard(s, d),
                        onSkip: () => resolveHowHeard(null, null),
                      }}
                    />
                  ) : runPassphrase &&
                    !passSaved &&
                    readyEmailEnabled &&
                    (notifyEmail ?? linkedEmail) ? (
                    // SYNC passphrase ceremony (same gate the hand-off runs):
                    // without it a sync user who left mid-run had an emailed
                    // link that opens nothing. Gated on the ready email being
                    // real — with emails off, leaving is never safe, so the
                    // live view keeps its keep-this-tab-open framing and the
                    // passphrase arrives at the Done screen as before.
                    <PassphraseGate
                      key={runPassphrase}
                      passphrase={runPassphrase}
                      restarted={restartedRun}
                      durationPhrase="it usually takes 15–30 minutes"
                      onSaved={() => {
                        setPassSaved(true);
                        // Survive reloads: the ceremony happened once.
                        markActiveRunPassAcked();
                      }}
                    />
                  ) : (
                    <RunState
                      phase={runView.phase}
                      surfaced={runView.surfaced}
                      patternCount={runView.patternCount}
                      grounded={runView.grounded}
                      groundedTotal={runView.groundedTotal}
                      elapsedLabel={runElapsedLabel}
                      // Mirrors RunHandoff's safeToClose: server-side chain +
                      // a real ready email + the passphrase ceremony done (or
                      // no in-memory passphrase — a reloaded tab whose user
                      // already saved it). lib/unload-guard is the complement.
                      // Before convert-running, the same email promise shows
                      // as "pending" — the batch hand-off's staged messaging
                      // ("turns green within a few minutes"), so the reading
                      // phase never reads as an unbounded keep-open.
                      leave={
                        readyEmailEnabled &&
                        (notifyEmail ?? linkedEmail) &&
                        (!runPassphrase || passSaved)
                          ? phase.kind === "convert-running"
                            ? "safe"
                            : "pending"
                          : "hold"
                      }
                      email={notifyEmail ?? linkedEmail}
                      // Sponsored/free runs never paid — the banner must not
                      // reassure about a payment that doesn't exist (review,
                      // 2026-07-10).
                      sponsored={pilotFreeCovered && !alreadyPaid}
                      // Sync is the prod default transport, so this view is
                      // where most real users wait — same question, same
                      // latch as the batched hand-off.
                      howHeard={{
                        state: howHeardState,
                        onAnswer: (s, d) => resolveHowHeard(s, d),
                        onSkip: () => resolveHowHeard(null, null),
                      }}
                    />
                  )}

                  {emailModalOpen && (
                    <ChangeEmailModal
                      current={notifyEmail ?? linkedEmail ?? ""}
                      onClose={() => setEmailModalOpen(false)}
                      onSave={async (newEmail) => {
                        if (phase.kind !== "convert-running") {
                          throw new Error("no active run to update");
                        }
                        const res = await apiSetNotifyEmail({
                          server,
                          runId: phase.runId,
                          runToken: phase.runToken,
                          email: newEmail,
                        });
                        setNotifyEmail(res.email);
                      }}
                    />
                  )}

                  {/* Diagnostics stay behind the dev flag — never product UI. */}
                  {DEV_DIAGNOSTICS_ENABLED && "events" in phase && (
                    <div className="run-id muted" style={{ marginTop: 16 }}>
                      {phase.kind === "preview-running" ||
                      phase.kind === "preview-ready" ||
                      phase.kind === "preview-failed"
                        ? `preview_run_id: ${phase.previewId}`
                        : `run_id: ${phase.runId}`}
                      {phase.kind === "preview-failed" && ` · ${phase.reason}`}
                      {phase.kind === "convert-failed" && ` · ${phase.reason}`}
                      <EventLog events={phase.events} />
                    </div>
                  )}

                  {/* Keep-open notice appears the instant the run step opens
                      (setup + running), not only once the first POST returns —
                      otherwise it flashes in late. No top divider (wiz-cta-run). */}
                  {currentStep === STEP_RUN &&
                    (runView.mode === "running" || runView.mode === "setup" ? (
                      confirmCancel ? (
                        <div className="wiz-cta wiz-cta-run">
                          {/* Same fix as the stopped panel below (cancel-path,
                              2026-07-04): this used to read "already been
                              used, can't restart" for a convert-phase stop —
                              wrong, the code re-arms on any stop regardless
                              of phase. One neutral message, no phase.kind
                              branch. */}
                          <span className="rs-cancelconfirm-msg">
                            Stop the analysis? Your access code stays good —
                            stopping now keeps it available to start again.
                          </span>
                          <div className="actions-right">
                            <button
                              type="button"
                              className="primary"
                              onClick={() => setConfirmCancel(false)}
                            >
                              Keep running
                            </button>
                            <button
                              type="button"
                              className="ghost"
                              onClick={() => {
                                setConfirmCancel(false);
                                void triggerCancel();
                              }}
                            >
                              Stop the analysis
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="wiz-cta wiz-cta-run">
                          {/* "Keep this tab open" now lives as a prominent banner
                              at the top of the running view (RunState) rather than
                              a small line down here. */}
                          <button
                            type="button"
                            className="ghost"
                            onClick={() => setConfirmCancel(true)}
                            disabled={runView.mode === "setup"}
                          >
                            Cancel
                          </button>
                        </div>
                      )
                    ) : holdForPassAck ? null : (
                      // While the passphrase gate holds a completed run, the
                      // ceremony is the ONLY chrome — rendering the footer
                      // here made a greyed "Continue →" MATERIALIZE under the
                      // gate the instant the run finished (Rich, 2026-07-22
                      // staging drill: fast run completed mid-ceremony and a
                      // second, dead CTA appeared on the one-job screen). The
                      // continueDisabled hold stays as defense-in-depth; this
                      // removes the button's presence, not just its click.
                      cta
                    ))}
                </div>
              </section>

              {/* === STEP 4 — Download viewer === */}
              <section className={paneClass(STEP_DONE)} data-pane="5">
                <div className="pane-inner">
                  <h1 className="step-title">Download your analysis</h1>
                  <p className="step-lede">
                    You'll save one file —{" "}
                    <code className="filename">aurora-analysis.zip</code>. Open
                    it and you'll find your analysis plus a short readme that
                    walks you through it. It runs in any web browser, offline —
                    no account, nothing to install, and it's{" "}
                    <span className="nowrap">yours to keep.</span>
                  </p>

                  {phase.kind === "run-complete" ? (
                    <>
                      {/* The file is locked with the submit-time passphrase
                          (shown once at hand-off; Aurora keeps no copy). In
                          the same session we can repeat it as a courtesy;
                          after a refresh the saved copy is the only one. */}
                      <div className="download-passnote">
                        {runPassphrase ? (
                          <>
                            <strong>It opens with your passphrase.</strong> The
                            one you saved when the analysis started —{" "}
                            <code className="filename">{runPassphrase}</code> —
                            unlocks the analysis inside. Aurora doesn't keep a
                            copy.
                          </>
                        ) : (
                          <>
                            <strong>It opens with your passphrase.</strong> Use
                            the passphrase you saved when the analysis started.
                            Aurora doesn't keep a copy, so the analysis can't be
                            opened without it.
                          </>
                        )}
                      </div>
                      {backupAvailable && (
                        <RetentionNote
                          phase="pre"
                          retentionDays={retentionDays}
                          email={backupEmailAddr}
                          className="backup-note"
                        />
                      )}
                      <div className="actions">
                        <button
                          type="button"
                          className="primary"
                          onClick={triggerViewerDownload}
                          disabled={viewerBuilding}
                        >
                          {viewerBuilding
                            ? "Preparing your download…"
                            : "Download aurora-analysis.zip"}
                        </button>
                      </div>
                      {/* After the viewer downloads (phase.viewer set), the
                    whole wizard is replaced by the full-screen DoneScreen
                    bookend — see the early return in render. So the Download
                    step only ever shows the pre-download state here. */}
                      {/* Resume exit: a returning user who already saved the
                          file (this pane is mostly reached by resume) had NO
                          way out short of re-downloading — the only finish
                          action lived on the DoneScreen (dead end, live
                          2026-06-12). Same handleClose: clears the resume
                          pointer + code so the next visit starts clean. */}
                      <p className="download-finish">
                        Already saved your file?{" "}
                        <button
                          type="button"
                          className="landing-link landing-link-btn"
                          onClick={handleClose}
                        >
                          {isStagingHost()
                            ? "You're done — start fresh →"
                            : "You're done — return to plaintosee.cc →"}
                        </button>
                      </p>
                    </>
                  ) : (
                    <div className="phase-banner muted">
                      No analysis ready yet. Return to step 4 to run one.
                    </div>
                  )}
                  {/* Download is the terminal step: the only action is the
                      Download button above (then the DoneScreen). Both Back and
                      Continue are suppressed here — a nav footer with nothing in
                      it ("back to what?" — the analysis is already done) reads as
                      dead chrome. Stepper nav still lets the user go back. */}
                </div>
              </section>

              {/* Status toast: a single wrapper so an error + a reconnect notice
                  stack instead of overlapping. Floats over the bottom of the
                  active pane; `.has-toast` (above) reserves room so it never
                  covers the pane's content or CTA. */}
              {(resuming || errorMsg || payNotice) && (
                <div className="wizard-status">
                  {resuming && (
                    <div className="resuming-banner">
                      Reconnecting to your in-flight analysis…
                    </div>
                  )}
                  {payNotice && (
                    <div className="pay-banner" role="status">
                      {payNotice}
                    </div>
                  )}
                  {errorMsg && (
                    <div className="error-banner">
                      <strong>error</strong> · {errorMsg}
                    </div>
                  )}
                </div>
              )}
            </section>
          </div>
        </section>
      </div>
    </main>
  );
}

// Shown on the cost screen when the record's estimated cost exceeds the
// server's per-run budget (estimate.over_budget). Rather than let the user
// authorize a run the chain would refuse, we dead-end the wizard gracefully:
// a warm "this needs a human" note + a prefilled email to hello@plaintosee.cc,
// and a way back to swap in a smaller file.
export function CostOffRamp({
  estimate,
  onBack,
  accessCode,
  alreadyPaid = false,
}: {
  estimate: PrescanResponse;
  onBack: () => void;
  /** Pilot code, embedded as a visible body line so the note ties to a case. */
  accessCode?: string;
  /** Already-paid × inviable paid tier (review fix, 2026-08-05): same
   *  off-ramp, but the body + email lead acknowledge the payment on file so
   *  the user knows writing us isn't starting over — the payment rides along
   *  into the hand-off. */
  alreadyPaid?: boolean;
}) {
  const actualPages = estimate.actual_pages;
  const messages = estimate.message_count;
  // Lead with the universal unit — every export has messages; only a true
  // paginated PDF (actual_pages set) has pages. A CSV user has messages, not
  // pages, so don't make them squint at a page count to affirm the note.
  const sizePhrase =
    actualPages != null
      ? `about ${messages.toLocaleString()} messages across ` +
        `${actualPages.toLocaleString()} pages`
      : `about ${messages.toLocaleString()} messages`;
  const mailto = buildIntakeMailto({
    subject: "Aurora — my record is larger than the standard limit",
    lead: alreadyPaid
      ? `Hi,\n\nMy record has ${sizePhrase} — larger than the tier I ` +
        "already paid for covers. Could you help me get it analyzed?"
      : `Hi,\n\nMy record has ${sizePhrase} — larger than Aurora ` +
        "analyzes in one pass. Could you help me get it analyzed?",
    code: accessCode,
    // The size is already in the lead; don't ask for it again below.
    includeCount: false,
  });
  return (
    <div className="cost-offramp">
      <h2 className="cost-offramp-head">This one needs a human.</h2>
      <p className="cost-offramp-body">
        Your record is larger than what Aurora can analyze on its own
        right now. That's not a dead end — the bigger records are often the
        ones that matter most, and they're worth doing right. Send us a note
        and we'll personally figure out how to get yours analyzed.
      </p>
      {/* Set apart from the body, not an inline tail (review R3, owner):
          the payment reassurance is the one line an already-paid user is
          scanning for. Its own quiet element — bold lead + top rule via the
          shared .cost-offramp-paid-note — never a second band inside the
          off-ramp band (no boxes-in-boxes). */}
      {alreadyPaid && (
        <p className="cost-offramp-paid-note">
          <strong>You've already paid.</strong> That payment stays on file
          and counts toward getting yours&nbsp;done.
        </p>
      )}
      <div className="cost-offramp-actions">
        <button type="button" className="ghost" onClick={onBack}>
          ← Use a different file
        </button>
        <a className="primary cost-offramp-email" href={mailto}>
          Email us →
        </a>
      </div>
    </div>
  );
}

export function CapBlockedPanel({
  accessCode,
  onNewCode,
}: {
  accessCode?: string;
  /** Clear the stored code and return to code entry. */
  onNewCode: () => void;
}) {
  const mailto = buildIntakeMailto({
    subject: "Aurora — my access code is out of fresh starts",
    lead:
      "Hi,\n\nMy access code has used both of its starts. Could you set " +
      "me up to run again?",
    code: accessCode,
    includeCount: false,
  });
  return (
    <div className="cost-offramp">
      <h2 className="cost-offramp-head">
        This code has no fresh starts left.
      </h2>
      <p className="cost-offramp-body">
        An access code can begin the analysis twice — your first start, plus
        one more after stopping early. Yours has used both. That&rsquo;s a
        guardrail, not a judgment: send us a note and we&rsquo;ll set you up
        to run again.
      </p>
      <div className="cost-offramp-actions">
        <button type="button" className="ghost" onClick={onNewCode}>
          I have a new code
        </button>
        <a className="primary cost-offramp-email" href={mailto}>
          Email us &rarr;
        </a>
      </div>
    </div>
  );
}

export function CheckoutClosedPanel({
  accessCode,
  onBack,
}: {
  accessCode?: string;
  /** Return to the previous step. */
  onBack: () => void;
}) {
  // A priced run whose self-serve checkout is closed (Stripe not wired, or the
  // operator pulled public_access). No dead Authorize — the real exit is to
  // request access by email, phrased as "we'll set you up" rather than a
  // technical "checkout is closed."
  const mailto = buildIntakeMailto({
    subject: "Aurora — I'd like to get my record analyzed",
    lead: "Hi,\n\nI'd like to get my record analyzed. Could you set me up?",
    code: accessCode,
    includeCount: false,
  });
  return (
    <div className="cost-offramp">
      <h2 className="cost-offramp-head">Let&rsquo;s set you up.</h2>
      <p className="cost-offramp-body">
        We&rsquo;re getting new analyses started personally right now rather than
        through automatic checkout. Send us a note with your code and we&rsquo;ll
        get yours going &mdash; we read every one and reply as soon as we can.
      </p>
      <div className="cost-offramp-actions">
        <button type="button" className="ghost" onClick={onBack}>
          &larr; Back
        </button>
        <a className="primary cost-offramp-email" href={mailto}>
          Request access &rarr;
        </a>
      </div>
    </div>
  );
}

export function PwycCostPanel({
  estimate,
  choice,
  onChoice,
  attested,
  onAttested,
  paidMode,
  alreadyPaid,
  livePrices = null,
}: {
  estimate: PrescanResponse;
  /** /config-refreshed tier prices (name → USD), overriding the
   *  prescan-time snapshot so an operator reprice between file-drop and
   *  Continue can't show a stale amount (seam audit 2026-07-22). Null →
   *  prescan prices. */
  livePrices?: Record<string, number> | null;
  /** selected PWYC tier name (paid mode); "Standard" default lives in App. */
  choice: string;
  onChoice: (name: string) => void;
  /** the Access hardship attestation — the ONLY gate on the Access tier. */
  attested: boolean;
  onAttested: (v: boolean) => void;
  /** true when this run needs payment (not sponsored, not already paid) —
   *  renders the chooser; false renders the sponsored band only. */
  paidMode: boolean;
  /** true when this access code already has a completed payment — server-known
   *  (estimate.already_paid, a fresh session on a previously-paid code) OR
   *  paid earlier this same visit (paidThisVisit, the Stripe-redirect flag).
   *  Either way: replace the price cards with the "already paid" band and no
   *  tier is picked (launch-audit fix, 2026-07-04). */
  alreadyPaid: boolean;
}) {
  // PWYC cost panel (pricing decision 2026-06-30): one flat pay-what-you-can
  // ladder, self-selected, no proof ever — size is no longer a menu. Full
  // design + load-bearing copy rules:
  // docs/topics/landing-design-marketing/public-pages-plan-2026-06-30.md §4.
  const serviceRoute = estimate.service_route === true;
  // No fallback: the server's price ladder is the ONLY source of truth for a
  // dollar figure shown in copy. If the Standard tier is missing from the
  // response (an older/misconfigured server), degrade the sentences that
  // mention it gracefully rather than rendering a stale hardcoded number.
  const standardTier = estimate.pwyc_tiers.find((t) => t.name === "Standard");
  const standardPrice = standardTier
    ? livePrices?.[standardTier.name] ?? standardTier.price_usd
    : undefined;
  const sponsored = estimate.pilot_free === true && !serviceRoute;
  // Sponsored takes precedence in the (rare) case both are somehow true — a
  // pilot-free code should never look like it charged anyone. Same visual
  // register as the sponsored band (reuses .pilot-free-* — see CostOffRamp /
  // CheckoutClosedPanel / CapBlockedPanel for the same shared-class pattern
  // across differently-worded off-ramp panels).
  const paidBand = alreadyPaid && !sponsored && !serviceRoute;
  // Door 2 (per-tier cost gate): an inviable card's one-line explanation ends
  // in "email us" — the same intake mailto the off-ramp panels use, so every
  // contact path asks the same questions in the same shape. The record's size
  // rides in the lead (universal unit first — messages; pages only for a true
  // PDF), so the details block drops its redundant size prompt.
  const inviableSizePhrase =
    estimate.actual_pages != null
      ? `about ${estimate.message_count.toLocaleString()} messages across ` +
        `${estimate.actual_pages.toLocaleString()} pages`
      : `about ${estimate.message_count.toLocaleString()} messages`;
  const inviableMailto = buildIntakeMailto({
    subject: "Aurora — help with a larger record",
    lead:
      `Hi,\n\nMy record has ${inviableSizePhrase} — larger than some of ` +
      "Aurora's standard tiers cover. Could you help me figure out the " +
      "best way to get it analyzed?",
    includeCount: false,
  });
  return (
    <>
      {sponsored && (
        <div className="pilot-free-band">
          <div className="pilot-free-eyebrow">Sponsored access</div>
          <h2 className="pilot-free-head">This analysis is free.</h2>
          <p className="pilot-free-body">
            {standardPrice !== undefined && (
              <>
                An analysis is normally <strong>${standardPrice}</strong>.{" "}
              </>
            )}
            That cost is covered by a grant supporting access to the courts —
            {" "}
            <strong>you won't be charged anything&nbsp;today.</strong>
          </p>
        </div>
      )}
      {paidBand && (
        <div className="pilot-free-band">
          <div className="pilot-free-eyebrow">Paid</div>
          <h2 className="pilot-free-head">This analysis is covered.</h2>
          <p className="pilot-free-body">
            You already paid{" "}
            {estimate.paid_amount_usd != null && (
              <>
                <strong>${estimate.paid_amount_usd}</strong>{" "}
              </>
            )}
            for this code — nothing more to pay&nbsp;today.
          </p>
        </div>
      )}
      {!sponsored && !paidBand && !serviceRoute && paidMode && (
        <div className="tier-frame pwyc-panel">
          <div className="pwyc-head">
            <div className="pwyc-eyebrow">Choose what you pay</div>
            <h2 className="pwyc-title">One analysis. Three ways to&nbsp;pay.</h2>
            {/* Density pass 2026-07-04 (Rich: "even the two subtitle texts
                are crazy long") — cut to the one load-bearing sentence; the
                deleted paragraphs below the cards leaned on this same
                no-proof promise, so it stays. */}
            <p className="pwyc-lede">
              We don't check, and we'll never ask you to prove anything.
            </p>
          </div>
          <fieldset className="pwyc-grid">
            <legend className="sr-only">Choose what you pay</legend>
            {estimate.pwyc_tiers.map((tier) => {
              const selected = tier.name === choice;
              // Door 2: an inviable tier renders DISABLED, not hidden — the
              // whole ladder stays visible so the pricing never looks like it
              // shrank, but the card can't be picked and carries one plain
              // line explaining why (never blame — the record is just bigger
              // than the tier covers).
              const viable = tierIsViable(tier);
              return (
                <label
                  key={tier.name}
                  className={`pwyc-card${selected ? " is-selected" : ""}${
                    viable ? "" : " is-unavailable"
                  }`}
                >
                  <input
                    type="radio"
                    name="pwyc-tier"
                    className="sr-only"
                    value={tier.name}
                    checked={selected}
                    disabled={!viable}
                    onChange={() => {
                      if (viable) onChoice(tier.name);
                    }}
                  />
                  <div className="pwyc-card-top">
                    <span className="pwyc-card-name">{tier.name}</span>
                    <span className="pwyc-card-price">
                      ${livePrices?.[tier.name] ?? tier.price_usd}
                    </span>
                  </div>
                  {tier.name === "Standard" && (
                    <span className="pwyc-most">The standard rate</span>
                  )}
                  <p className="pwyc-card-blurb">{tier.blurb}</p>
                  {!viable && (
                    <p className="pwyc-card-unavailable">
                      {tier.name === "Access" ? (
                        <>
                          This record is larger than the Access tier covers
                          — choose Standard, or{" "}
                          <a href={inviableMailto}>email us</a>.
                        </>
                      ) : (
                        <>
                          This record is larger than the {tier.name} tier
                          covers — <a href={inviableMailto}>email us</a>.
                        </>
                      )}
                    </p>
                  )}
                  {tier.name === "Access" && selected && viable && (
                    <label
                      className="pwyc-attest"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={attested}
                        onChange={(e) => onAttested(e.target.checked)}
                      />
                      <span>
                        Yes — the standard price would be a hardship for me
                        right&nbsp;now.
                      </span>
                    </label>
                  )}
                </label>
              );
            })}
          </fieldset>
        </div>
      )}
      <div className="tier-frame pwyc-context-frame">
        <p className="tier-context">
          {typeof estimate.actual_pages === "number" ? (
            // Real page count surfaced from the source PDF (OFW adapter
            // only) — verifiable against the user's own export, no
            // "roughly" hedge. typeof check accepts both null and
            // undefined as "not present" — defensive against future
            // wire-shape drift.
            <>
              Your record is{" "}
              <strong>{estimate.actual_pages.toLocaleString()} pages</strong>{" "}
              across{" "}
              <strong>
                {estimate.message_count.toLocaleString()} messages
              </strong>
              .
            </>
          ) : (
            // Non-PDF source — page count is derived from the token
            // estimate; keep the "roughly" hedge so it reads as an
            // estimate rather than a fact.
            <>
              Your record measures roughly{" "}
              <strong>{estimate.pages_estimate.toLocaleString()} pages</strong>{" "}
              across{" "}
              <strong>
                {estimate.message_count.toLocaleString()} messages
              </strong>
              .
            </>
          )}
          {serviceRoute && (
            <>
              {" "}
              It's larger than our self-serve sizes — write us at{" "}
              <a href="mailto:hello@plaintosee.cc?subject=Larger%20record">
                hello@plaintosee.cc
              </a>{" "}
              and we'll set your case up directly, at no extra&nbsp;charge.
            </>
          )}
        </p>
      </div>
    </>
  );
}

function EventLog({ events }: { events: SseEvent[] }) {
  const logRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [events.length]);
  const lines = useMemo(() => events.map(formatEvent), [events]);
  return (
    <div ref={logRef} className="event-log">
      {lines.length === 0 ? (
        <div className="muted">(waiting for events…)</div>
      ) : (
        lines.map((line, i) => (
          <div key={i} className={`event-line event-${events[i].type}`}>
            {line}
          </div>
        ))
      )}
    </div>
  );
}

function formatEvent(ev: SseEvent): string {
  const d = ev.data as Record<string, unknown> | string | null;
  if (ev.type === "terminal_state" && d && typeof d === "object") {
    return `[${ev.type}] state=${(d as { chain_state?: string }).chain_state} exit_code=${
      (d as { exit_code?: number }).exit_code
    }`;
  }
  if (ev.type === "progress" && d && typeof d === "object") {
    const s = d as { stage?: string; message?: string };
    return `[${ev.type}] ${s.stage ?? "?"}${s.message ? ` — ${s.message}` : ""}`;
  }
  if (ev.type === "cost_estimate" && d && typeof d === "object") {
    return `[${ev.type}] est=${(d as { estimated_total_usd?: number }).estimated_total_usd}`;
  }
  if (ev.type === "error" && d && typeof d === "object") {
    return `[${ev.type}] ${(d as { detail?: string }).detail ?? JSON.stringify(d)}`;
  }
  return `[${ev.type}] ${typeof d === "object" ? JSON.stringify(d) : String(d ?? "")}`;
}

// Run-step progress surface now lives in components/RunState.tsx (RunState +
// ResultsSummary, driven by deriveRunView). The old PhaseProgress/PatternList
// were retired with the run-state redesign — counts only during the run, names
// on the results card, quotes deferred to the encrypted viewer.
